import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    FiActivity,
    FiAlertTriangle,
    FiArchive,
    FiArrowRight,
    FiArrowUp,
    FiBarChart2,
    FiBox,
    FiBriefcase,
    FiCalendar,
    FiCheck,
    FiCheckSquare,
    FiCopy,
    FiChevronDown,
    FiChevronLeft,
    FiChevronUp,
    FiClock,
    FiDollarSign,
    FiDownload,
    FiEdit2,
    FiEdit3,
    FiEye,
    FiEyeOff,
    FiFilter,
    FiLayers,
    FiMapPin,
    FiMessageSquare,
    FiPlus,
    FiRefreshCw,
    FiRotateCcw,
    FiSave,
    FiSearch,
    FiSettings,
    FiTarget,
    FiTrash2,
    FiTrendingUp,
    FiUsers,
    FiX,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import ColumnsPanel from "./panels/ColumnsPanel";
import FiltersPanel from "./panels/FiltersPanel";
import { countByValue, summarizeFilter, withCategoryValues } from "./panels/panelModel";
import { DERIVED_FILTER_IDS, EMPTY_FILTER_LABEL, EMPTY_FILTER_VALUE, apiObra, buildObraSections as buildObraSectionsAt, filterFieldValue, filterValueLabel, isEmptyObraFilter, normalizeSearchValue, obraFilterSpec, obraMatchesFilter, obraSearchText, sectionStatus as sectionStatusAt } from "./obraFilter";
import { computeRowWindow, rowOffset, shouldUpdateViewport, showBackToTop } from "./sheetWindow";
import { MIN_COLUMN_WIDTH, computeColumnWidth, defaultColumnWidth, genericCellText, isTruncated, tableWidth } from "./sheetColumnWidths";
import { DETAIL_TABS, buildDetailFacts, detailPlacementOfField, detailTabFields, detailTabOfField, isZoneSection, sectionLayout } from "./detailLayout";
import { EMPRESA_CATALOG_CODES, canonicalCatalogValue, catalogKey, catalogRenameError, mergeEmpresaCatalogEntries, reviewedCatalogValues, splitSueloValue } from "./catalogosRevisados";
import { draftsToPersist, editKey as getEditKey, nextTempObraId, pruneOrphanDrafts, remapAuditEntries, remapDrafts, remapIdSet, savedDraftKeys, savedIdMap } from "./obrasAltas";
import { filterUbicaciones, geoPayload, regionTypeLabel, renameCityInDrafts, ubicacionError } from "./ubicaciones";
import { isOneOf, usePersistentState } from "./portfolio/persistentState";
import SectionHeader from "../../../components/sectionHeader/sectionHeader";
import { fetchObras, guardarObra, archivarObra, fetchCatalogos, guardarCatalogo, cambiarEstadoCatalogo, eliminarCatalogo, fetchAuditoria, fetchResponsables, fetchGeografia, guardarGeografia, estadoGeografia, eliminarGeografia } from "./data/obrasService";
import PortfolioControlCenter from "./portfolio/PortfolioControlCenter";
import {
    dateFromIso,
    getCommunity,
    getForecastEac,
    getRiskTone,
    isObraAtRisk,
    getCapexDeviation,
    currency,
    daysBetween,
    formatCompactDate,
    monthLabels,
    moneyCompact,
    portfolioToday,
    portfolioViews,
    selectionScope,
    toIsoDate,
} from "./portfolio/portfolioShared";
import { exportElementToPdf } from "./portfolio/exportPdf";
import { CAPEX_TRIANGLE_FIELDS, capexTriangleDraft, recalculateDerivedCosts } from "./costes";
import { ALERT_STATUSES, HITO_STATUS, NEEDS_DATA_WINDOW_DAYS, criticalHito, hitoState, isCriticalHito, listHitoAlerts, obraHitoAlerts, obraHitos } from "./hitosModel";
import HitoConfirmModal from "./HitoConfirmModal";
import { apiErrorMessage } from "./data/apiErrors";
import { OBRAS_ROLES, canEditObraField, isReadOnlyObrasRole } from "../../../utils/obrasAccess";
import { buildBulkFieldGroups, bulkChanges, bulkCityChoice } from "./bulkEditModel";
import { responsableCod, responsableOptions, userNames, userValueText } from "./responsablesSelect";
import { DB_CATALOG_TYPES, apiRowChanged, catalogAllowsClear, catalogAllowsInlineCreate, catalogStateFromRows } from "./catalogSync";
import ObraPlanView from "./ObraPlanView";
import { sheetBlockClass } from "./sheetBlocks";
import { OPTIONAL_REASON, effectiveReason, isReasonRequired, issuesForSave, newObraCloseAction } from "./obrasValidation";

import "./obras.scss";

// El Excel de demostración solo se empaqueta en desarrollo.
const obras2026Source = process.env.NODE_ENV === "development" ? require("./data/obras_2026.json") : { rows: [] };

// Opciones de los catálogos. Los que salen del Excel (tipos, campañas, imagen, iluminación,
// suelos y proveedores) vienen revisados de data/catalogos_obras.json, la misma lista que
// carga el script 22 en BD. País/ubicación, estado, PM y liquidación son propios de la app.
const catalogOptions = {
    pais: ["ALEMANIA", "ESPAÑA", "FRANCIA", "ITALIA", "PORTUGAL", "REINO UNIDO"],
    ciudad: [
        { value: "BOCHUM", parent: "ALEMANIA" },
        { value: "FRANKFURT", parent: "ALEMANIA" },
        { value: "HAMBURGO", parent: "ALEMANIA" },
        { value: "MALLORCA", parent: "ESPAÑA" },
        { value: "A CORUÑA", parent: "ESPAÑA" },
        { value: "MADRID", parent: "ESPAÑA" },
        { value: "BORDEAUX", parent: "FRANCIA" },
        { value: "MILAN", parent: "ITALIA" },
        { value: "LISBOA", parent: "PORTUGAL" },
        { value: "LONDRES", parent: "REINO UNIDO" },
    ],
    tipo: reviewedCatalogValues("tipo"),
    campana: reviewedCatalogValues("campana"),
    estado: ["Planificada", "En curso", "En riesgo", "Completada", "Bloqueada", "Reserva"],
    // Proveedores (dbo.Empresas): además se añaden los nombres que ya usan las obras.
    consulting: reviewedCatalogValues("consulting"),
    constructora: reviewedCatalogValues("constructora"),
    studio: reviewedCatalogValues("studio"),
    pm: ["Ana R.", "Javier M.", "Laura C.", "Mario P.", "Sin asignar"],
    pm2: ["Ana R.", "Javier M.", "Laura C.", "Mario P.", "Sin asignar"],
    pm3: ["Ana R.", "Javier M.", "Laura C.", "Mario P.", "Sin asignar"],
    pmi: ["GUI", "ROB", "GE", "PA", "Sin asignar"],
    imagen: reviewedCatalogValues("imagen"),
    imgOriginal: reviewedCatalogValues("imgOriginal"),
    mobiliario: reviewedCatalogValues("mobiliario"),
    sonido: reviewedCatalogValues("sonido"),
    // Tipo de iluminación de la zona: catálogo único compartido por gral/caja/prob/vigas.
    ilumGral: reviewedCatalogValues("ilumGral"),
    // Proveedor de iluminación: catálogo único compartido por Prov. Ilum. 1 y 2.
    provIlum: reviewedCatalogValues("provIlum"),
    // Proveedor y tipo de suelo: cada uno es un catálogo único compartido por gral/alf/prob.
    provSuelo: reviewedCatalogValues("provSuelo"),
    tipoSuelo: reviewedCatalogValues("tipoSuelo"),
    liquidacion: ["Pendiente", "En curso", "Cerrada", "No aplica"],
};

function createInitialCatalogs() {
    return { subdivision: [], ...Object.fromEntries(
        Object.entries(catalogOptions).map(([catalog, entries]) => [
            catalog,
            entries.map((entry, index) => ({
                id: `${catalog}-${index}`,
                value: typeof entry === "string" ? entry : entry.value,
                parent: typeof entry === "string" ? null : entry.parent || null,
                active: true,
            })),
        ])
    ) };
}

const initialAuditEntries = [
    {
        id: 1,
        codObra: 1,
        field: "legal",
        label: "LEGAL",
        previousValue: "2026-06-24",
        newValue: "2026-06-28",
        reason: "Ajuste confirmado con el equipo legal local.",
        user: "Ana R.",
        date: "Hoy, 09:42",
        source: "ENTER",
    },
    {
        id: 2,
        codObra: 1,
        field: "provIlum1",
        label: "PROV ILUM 1",
        previousValue: "Por asignar",
        newValue: "LightCo",
        reason: "Proveedor adjudicado tras validacion de oferta.",
        user: "Ana R.",
        date: "Ayer, 17:18",
        source: "ICONO",
    },
];

const numericFields = new Set([
    "dias", "supCons", "supVta", "cM2", "capex", "vnc", "vta3Year", "ventaM2",
    "presupuesto", "adicional", "costeObra", "fachada", "almacExt", "totalInversion",
    "totalOc", "euroM2Oc", "costeMobiliario", "audiovisual", "variosBsk", "subtotalBsk",
    "coefTrans", "transporteBsk", "markedUpItx", "totalBsk", "euroM2Bsk", "aranceles",
    "variosPaisCorp", "ssGg", "techTienda", "totalOtros", "euroM2Inv", "mlConfeccion",
    "mlPaqueteria", "cestas", "licencias",
    "bufMobiliario", "bufIluminacion", "bufSuelo",
    "fabMobiliario", "fabIluminacion", "fabSuelo",
]);

const calculatedCostFields = new Set([
    "costeObra",
    "totalOc",
    "euroM2Oc",
    "subtotalBsk",
    "coefTrans",
    "transporteBsk",
    "markedUpItx",
    "totalBsk",
    "euroM2Bsk",
    "totalOtros",
    "totalInversion",
    "euroM2Inv",
    "observacionesCalculo",
]);

const fieldUnits = {
    dias: "dias",
    supCons: "m2",
    supVta: "m2",
    cM2: "EUR/m2",
    capex: "EUR",
    vnc: "EUR",
    vta3Year: "EUR",
    ventaM2: "EUR/m2",
    licencias: "EUR",
    presupuesto: "EUR",
    adicional: "EUR",
    costeObra: "EUR",
    fachada: "EUR",
    almacExt: "EUR",
    totalOc: "EUR",
    euroM2Oc: "EUR/m2",
    costeMobiliario: "EUR",
    audiovisual: "EUR",
    variosBsk: "EUR",
    subtotalBsk: "EUR",
    coefTrans: "x",
    transporteBsk: "EUR",
    markedUpItx: "x",
    totalBsk: "EUR",
    euroM2Bsk: "EUR/m2",
    aranceles: "EUR",
    variosPaisCorp: "EUR",
    ssGg: "EUR",
    techTienda: "EUR",
    totalOtros: "EUR",
    totalInversion: "EUR",
    euroM2Inv: "EUR/m2",
    capacidadAlmacen: "m2",
    mlConfeccion: "m.l.",
    mlPaqueteria: "m.l.",
    cestas: "uds",
    bufMobiliario: "días",
    bufIluminacion: "días",
    bufSuelo: "días",
};

// Símbolo mostrado en el badge de cada campo (las unidades son clave).
const UNIT_SYMBOLS = {
    "EUR": "€",
    "EUR/m2": "€/m²",
    "m2": "m²",
    "m.l.": "m.l.",
    "uds": "uds",
};
const unitSymbol = (unit) => (unit ? (UNIT_SYMBOLS[unit] || unit) : "");

// Mini-descripciones que aparecen al pasar el ratón por el nombre del campo.
const FIELD_HINTS = {
    // Obra civil / OC
    licencias: "Coste de licencias y permisos de obra.",
    presupuesto: "Presupuesto base de obra civil (OC).",
    adicional: "Partidas adicionales de obra sobre el presupuesto base.",
    costeObra: "Total de obra civil. Se calcula sumando presupuesto y adicionales.",
    fachada: "Coste de la fachada.",
    almacExt: "Coste de almacén externo.",
    totalOc: "Total de obra civil (OC). Calculado: licencias + obra + fachada + almacén externo.",
    euroM2Oc: "Coste de obra civil por m² de superficie construida. Total OC ÷ Sup Cons.",
    // Equipamiento BSK
    costeMobiliario: "Coste del mobiliario de tienda.",
    audiovisual: "Coste del equipamiento audiovisual.",
    variosBsk: "Otros costes de equipamiento BSK.",
    subtotalBsk: "Subtotal de equipamiento BSK antes de transporte y marcado. Calculado.",
    coefTrans: "Coeficiente de transporte que se aplica al subtotal BSK.",
    transporteBsk: "Coste de transporte del equipamiento BSK. Calculado con el coeficiente.",
    markedUpItx: "Coeficiente de marked up ITX aplicado al equipamiento.",
    totalBsk: "Total de equipamiento BSK. Calculado (subtotal + transporte + marked up).",
    euroM2Bsk: "Equipamiento BSK por m² de superficie construida. Total BSK ÷ Sup Cons.",
    // Otros país / corporativo
    aranceles: "Aranceles e impuestos de importación.",
    variosPaisCorp: "Costes varios de país / corporativos.",
    ssGg: "Servicios generales.",
    techTienda: "Tecnología de tienda.",
    totalOtros: "Total de otros costes país / corporativos. Calculado: aranceles + varios + SS.GG. + tecnología de tienda.",
    // Inversión / control
    totalInversion: "Inversión total de la obra: suma de todos los gastos. Calculado (OC + BSK + otros).",
    euroM2Inv: "Inversión total por m² de superficie construida. Inversión ÷ Sup Cons.",
    observacionesCalculo: "Observación automática: 'ok' si la inversión total no supera el CAPEX, 'no ok' si lo supera.",
    observaciones: "Notas de la obra; se conservan independientemente del control automático de CAPEX.",
    liquidacionNotas: "Notas sobre la liquidación de la obra.",
    liquidacion: "Estado de liquidación de la obra (Pendiente, En curso, Cerrada, No aplica).",

    // General
    pais: "País donde se ubica la tienda.",
    ciudad: "Ciudad de la tienda.",
    tipo: "Tipo de intervención: apertura, reforma, reubicación, ampliación…",
    campana: "Campaña comercial de apertura (p. ej. INV 26, VER 26).",
    inicioObra: "Fecha de inicio de la obra en tienda.",
    apertura: "Fecha prevista de apertura de la tienda.",
    dias: "Duración de obra en días (de inicio a apertura).",
    estado: "Estado de la obra: Planificada, En curso, En riesgo, Completada, Bloqueada o Reserva.",
    supCons: "Superficie construida en m². Con el coste m² ref. da el CAPEX; con el CAPEX, el coste m².",
    supVta: "Superficie de sala de venta en m².",
    cM2: "Coste por m² de referencia (lo fija BSK). Con la superficie construida da el CAPEX.",
    capex: "CAPEX aprobado: coste m² ref. × superficie construida. Con dos de los tres datos se calcula el tercero.",
    vnc: "Valor neto contable.",
    vta3Year: "Venta estimada acumulada a 3 años.",
    ventaM2: "Venta estimada por m² de sala de venta.",

    // Equipo
    consulting: "Consultora asignada a la obra.",
    constructora: "Constructora responsable de la ejecución.",
    studio: "Estudio de diseño / interiorismo.",
    pm: "Project manager principal de la obra.",
    pm2: "Segundo project manager de apoyo.",
    pm3: "Tercer project manager de apoyo.",
    p3Constructora: "Plano P3 de la constructora. Hito crítico: hay que confirmarlo.",
    p3Mobiliario: "Plano P3 de mobiliario. Hito crítico: hay que confirmarlo.",
    inicioMontajeMob: "Día en que empieza el montaje del mobiliario en tienda.",
    finMontajeMob: "Día en que termina el montaje del mobiliario en tienda.",
    deadlineMob: "Deadline de mobiliario: fecha en que el mobiliario va a obra. Hito crítico: hay que confirmarlo.",
    pmi: "Project manager de imagen.",

    // Hitos y fechas
    imagen: "Concepto de imagen aplicado a la tienda.",
    imgOriginal: "Imagen original de referencia.",
    solZonif: "Solicitud de zonificación (sí / no).",
    zonif: "Fecha de zonificación.",
    legal: "Fecha de obtención de permisos legales.",
    planoP3: "Fecha del plano P3 (definitivo).",
    mobiliario: "Proveedor de mobiliario.",
    sonido: "Proveedor audiovisuales.",
    ilumGral: "Iluminación general.",
    ilumCaja: "Iluminación de la zona de caja.",
    ilumProb: "Iluminación de probadores.",
    ilumVigas: "Iluminación de vigas.",
    provIlum1: "Proveedor de iluminación principal.",
    provIlum2: "Proveedor de iluminación secundario.",
    sueloGral: "Proveedor de suelo general.",
    sueloAlf: "Proveedor de suelo de alfombra.",
    sueloProb: "Proveedor de suelo de probadores.",
    tipoSueloGral: "Tipo de suelo de la zona general.",
    tipoSueloAlf: "Tipo de suelo de la zona de alfombras.",
    tipoSueloProb: "Tipo de suelo de los probadores.",
    bufMobiliario: "Días de gestión y transporte del mobiliario que se suman a la fecha de entrega para calcular la fecha lista para montaje. Alimenta el cronograma.",
    bufIluminacion: "Días de gestión y transporte de la iluminación que se suman a la fecha de entrega para calcular la fecha lista para montaje. Alimenta el cronograma.",
    bufSuelo: "Días de gestión y transporte del suelo que se suman a la fecha de entrega para calcular la fecha lista para montaje. Alimenta el cronograma.",

    // Operativa
    capacidadAlmacen: "Capacidad de almacén de la tienda en m².",
    mlConfeccion: "Metros lineales de exposición de confección.",
    mlPaqueteria: "Metros lineales de exposición de paquetería.",
    cestas: "Número de cestas previstas.",
};

// Tipos de guardado que son edición de datos (sin tipo = un solo campo): en ellos la
// justificación solo es obligatoria si cambia el inicio de obra o la apertura.
const FIELD_EDIT_CHANGE_KINDS = new Set([undefined, "batch", "alta", "geography"]);

const changeReasonPresets = [
    "Correccion de dato",
    "Cambio de alcance",
    "Actualizacion de proveedor",
    "Ajuste de planificacion",
];

const catalogReasonPresets = [
    "Valor obsoleto",
    "Correccion de catalogo",
    "Vuelve a estar disponible",
    "Cambio operativo",
];

const archiveObraReasonPresets = [
    "Obra cancelada",
    "Registro duplicado",
    "Creada por error",
    "Proyecto descartado",
];

const reactivateObraReasonPresets = [
    "Proyecto retomado",
    "Archivo accidental",
    "Cambio de decision",
    "Reapertura operativa",
];


function rawText(value, fallback = "") {
    const text = String(value ?? "").trim();
    return text || fallback;
}

function rawNumber(value, fallback = 0) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value ?? "").replace(/\./g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : fallback;
}

function rawDate(value) {
    const text = rawText(value);
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function rawMilestoneState(value) {
    const state = rawText(value).toUpperCase();
    if (state === "N/A") return "NA";
    if (["OK", "NA", "RIESGO", "PENDIENTE"].includes(state)) return state;
    return rawDate(value) ? "PENDIENTE" : null;
}

// Estados de obra válidos (el resto de valores del campo ESTADO del Excel son
// códigos de zona como "A-Canarias" o "NEG", que no son estados y se ignoran).
// Exportado para comprobar que cada estado tiene su color (statusClass). Un estado nuevo
// se da de alta en la BD (catálogo 'estado') y aquí: sin esto, la hoja lo deja en blanco.
export const VALID_OBRA_STATUSES = new Set(["Planificada", "En curso", "En riesgo", "Completada", "Bloqueada", "Reserva"]);

function inferPortfolioStatus(row) {
    const explicit = rawText(row.ESTADO);
    if (explicit && VALID_OBRA_STATUSES.has(explicit)) return explicit;
    const opening = dateFromIso(rawDate(row.APERTURA));
    const start = dateFromIso(rawDate(row["INICIO O,"]));
    if (opening && opening < portfolioToday) return "Completada";
    if (start && start <= portfolioToday) return "En curso";
    return "Planificada";
}

// Estado de una sección por su fecha y secciones de la ficha: regla en obraFilter.js
// (la búsqueda de la hoja las indexa y el calendario suscrito filtra igual en el API).
const sectionStatus = (deadline, openingDate) => sectionStatusAt(deadline, openingDate, portfolioToday);
const buildObraSections = (obra) => buildObraSectionsAt(obra, portfolioToday);

function buildExcelObra(row, index) {
    const opening = rawDate(row.APERTURA);
    // Mismos valores limpios que deja el script 22 en BD (catálogos revisados).
    const clean = (code, value) => canonicalCatalogValue(code, value);
    const [sueloGral, sueloAlf, sueloProb] = ["SUELO gral", "SUELO alf", "SUELO prob"].map((column) => splitSueloValue(row[column]));
    const inicioObra = rawDate(row["INICIO O,"]);
    const capex = rawNumber(row.CAPEX);
    const totalOc = rawNumber(row["TOTAL OC"]);
    const totalBsk = rawNumber(row["TOTAL BSK"]);
    const totalOtros = rawNumber(row["TOTAL OTROS"]);
    const totalInversion = rawNumber(row["TOTAL INVERSION"], totalOc + totalBsk + totalOtros);
    // El coef. de transporte y el marked up ITX varían por obra y no vienen como
    // columna en el Excel: se derivan de los valores crudos para reproducir los
    // totales del Excel y recalcular correctamente al editar los componentes.
    const rawSubtotalBsk = rawNumber(row["SubTotal BSK"]);
    const rawTransporteBsk = rawNumber(row["Transporte BSK"]);
    const coefTrans = rawSubtotalBsk > 0 ? rawTransporteBsk / rawSubtotalBsk : 0;
    const markedUpItx = (rawSubtotalBsk + rawTransporteBsk) > 0 ? totalBsk / (rawSubtotalBsk + rawTransporteBsk) : 1;
    const obra = recalculateDerivedCosts({
        // codObra es el identificador interno (IDENTITY en BD). El COD del Excel es el código
        // de tienda y viene a 0 o repetido, así que en los datos de ejemplo se usa la fila:
        // con codObra repetido ninguna ficha se podía abrir en /obras-mock.
        codObra: index + 1, codigoTienda: rawText(row.COD, String(index + 1)), tienda: rawText(row.TIENDA, "Sin nombre"), pais: rawText(row.PAIS, "Sin pais"), ciudad: rawText(row.CIUDAD, "Sin ciudad"), tipo: clean("tipo", row.TIPO), inicioObra, apertura: opening, dias: rawNumber(row.DIAS), estado: inferPortfolioStatus(row),
        supCons: rawNumber(row["SUP CONS"]), supVta: rawNumber(row["SUP VTA"]), cM2: row["C/M2"] == null ? null : rawNumber(row["C/M2"]), pm: rawText(row.PM, "Sin PM"), pm2: rawText(row.PM2), pmi: rawText(row.PMI), consulting: clean("consulting", row.CONSULTING), constructora: clean("constructora", row.CONSTRUCTORA), studio: clean("studio", row.STUDIO), capex, totalInversion, totalOc, totalBsk, totalOtros,
        costes: [{ concepto: "Licencias", importe: rawNumber(row.LICENCIAS) }, { concepto: "Mobiliario", importe: rawNumber(row.Mobiliario) }, { concepto: "Almacen ext", importe: rawNumber(row["Almac ext"]) }],
        activa: true, fechaBaja: null, usuarioBaja: null, motivoBaja: null, imagen: clean("imagen", row.IMAGEN), imgOriginal: clean("imgOriginal", row["IMG. ORIGINAL"]), solZonif: rawText(row["Sol Zonif"]), zonif: rawDate(row.ZONIF), legal: rawDate(row.LEGAL), planoP3: rawDate(row["PLANO P3"]), deadlineMob: rawDate(row["Deadline MOB"]), mobiliario: clean("mobiliario", row.MOBILIARIO), sonido: clean("sonido", row.SONIDO), ilumGral: clean("ilumGral", row["ILUM GRAL"]), ilumCaja: clean("ilumGral", row["ILUM CAJA"]), ilumProb: clean("ilumGral", row["ILUM PROB"]), ilumVigas: clean("ilumGral", row["ILUM VIGAS"]), provIlum1: clean("provIlum", row["PROV ILUM 1"]), provIlum2: clean("provIlum", row["PROV ILUM 2"]), sueloGral: sueloGral.proveedor, sueloAlf: sueloAlf.proveedor, sueloProb: sueloProb.proveedor, tipoSueloGral: sueloGral.tipo, tipoSueloAlf: sueloAlf.tipo, tipoSueloProb: sueloProb.tipo, licencias: rawNumber(row.LICENCIAS), presupuesto: rawNumber(row.PRESUPUESTO), adicional: rawNumber(row.Adicional), costeObra: rawNumber(row.Obra), fachada: rawNumber(row.Fachada), almacExt: rawNumber(row["Almac ext"]), costeMobiliario: rawNumber(row.Mobiliario), audiovisual: rawNumber(row.AudioVisual), variosBsk: rawNumber(row.Varios), subtotalBsk: rawNumber(row["SubTotal BSK"]), transporteBsk: rawNumber(row["Transporte BSK"]), coefTrans, markedUpItx, aranceles: rawNumber(row.Aranceles), variosPaisCorp: rawNumber(row["Varios Pais//Corport"]), ssGg: rawNumber(row["SS.GG"]), techTienda: rawNumber(row["Tech Tda."]), observaciones: rawText(row.Observaciones), liquidacion: rawText(row["LIQUIDACIÓN "]), bufMobiliario: 7, bufIluminacion: 7, bufSuelo: 7, _sourceIndex: index,
        estadoOrigen: rawText(row.ESTADO),
        zonifEstado: rawMilestoneState(row.ZONIF), legalEstado: rawMilestoneState(row.LEGAL),
        planoP3Estado: rawMilestoneState(row["PLANO P3"]), deadlineMobEstado: rawMilestoneState(row["Deadline MOB"]),
    });
    return { ...obra, secciones: buildObraSections(obra) };
}

// Conserva toda la ficha del API: importes base, catálogos, operativa, notas,
// estados de hitos y metadatos. Los totales de lectura son los calculados por SQL.
// Solo se adaptan las fechas DATE y se construyen las agrupaciones de la interfaz.
// Fechas DATE, duración y secciones: apiObra (obraFilter.js), la misma obra que filtra el API.
function buildApiObra(row, index) {
    const obra = apiObra(row, portfolioToday);
    return {
        ...obra,
        costes: [
            { concepto: "Licencias", importe: obra.licencias },
            { concepto: "Mobiliario", importe: obra.costeMobiliario },
            { concepto: "Almacen ext", importe: obra.almacExt },
        ],
        _sourceIndex: index,
    };
}

const baseObras = [{ codObra: 0, codigoTienda: "NUEVA", tienda: "Nueva obra", pais: "ESPANA", ciudad: "MADRID", pm: "Sin PM", estado: "Planificada", costes: [], secciones: [{ seccion: "Legal", proveedor: "", deadline: "", estado: "Pendiente" }], totalOc: 0, totalBsk: 0, totalOtros: 0, totalInversion: 0, supCons: 0, supVta: 0 }];


const buildExcelRecord = (obra) => {
    const getCost = (concepto) =>
        obra.costes.find((coste) => coste.concepto === concepto)?.importe || 0;
    const section = (name) => obra.secciones.find((item) => item.seccion === name);
    const totalInvestment = Number(obra.totalInversion || 0);
    const targetOc = Number(obra.totalOc || 0);
    const targetBsk = Number(obra.totalBsk || 0);
    const targetOtros = Number(obra.totalOtros || 0);
    const sellingArea = Number(obra.supVta || 0);
    const constructionArea = Number(obra.supCons || 0);
    const licencias = getCost("Licencias") || Math.round(targetOc * 0.06);
    const almacExt = getCost("Almacen ext");
    const costeObra = Math.max(0, targetOc - licencias - almacExt);
    const presupuesto = Math.round(costeObra * 0.92);
    const adicional = Math.max(0, costeObra - presupuesto);
    const coefTrans = 0.07;
    const markedUpItx = 1.05;
    const desiredBskSubtotal = targetBsk
        ? Math.round(targetBsk / ((1 + coefTrans) * markedUpItx))
        : 0;
    const costeMobiliario = getCost("Mobiliario") || Math.round(desiredBskSubtotal * 0.72);
    const audiovisual = getCost("AudioVisual") || Math.round(desiredBskSubtotal * 0.14);
    const variosBsk = Math.max(0, desiredBskSubtotal - costeMobiliario - audiovisual);
    const techTienda = getCost("Tech tienda");
    const ssGg = getCost("SS.GG") || Math.round(targetOtros * 0.34);
    const variosPaisCorp = Math.max(0, targetOtros - ssGg - techTienda);

    return recalculateDerivedCosts({
        ...obra,
        activa: obra.activa !== false,
        fechaBaja: obra.fechaBaja || null,
        usuarioBaja: obra.usuarioBaja || null,
        motivoBaja: obra.motivoBaja || null,
        pm2: obra.pm,
        pmi: "GUI",
        cM2: obra.euroM2Inv,
        capex: Math.round(totalInvestment * 1.06),
        vnc: Math.round(totalInvestment * 0.16),
        vta3Year: Math.round(totalInvestment * 2.85),
        ventaM2: sellingArea ? Math.round((totalInvestment * 2.85) / sellingArea) : 0,
        imagen: "Splau",
        imgOriginal: "Stage",
        solZonif: "Si",
        zonif: section("Imagen")?.deadline || "",
        legal: section("Legal")?.deadline || "",
        planoP3: section("Legal")?.deadline || "",
        deadlineMob: section("Mobiliario")?.deadline || "",
        mobiliario: section("Mobiliario")?.proveedor || "",
        sonido: "Light&Sound",
        ilumGral: "VDG",
        ilumCaja: "Duospot",
        ilumProb: "Ibiza",
        ilumVigas: "Monospot",
        provIlum1: section("Iluminacion")?.proveedor || "",
        provIlum2: "",
        sueloGral: section("Suelo")?.proveedor || "",
        sueloAlf: "",
        sueloProb: "",
        bufMobiliario: obra.bufMobiliario ?? 7,
        bufIluminacion: obra.bufIluminacion ?? 7,
        bufSuelo: obra.bufSuelo ?? 7,
        licencias,
        presupuesto,
        adicional,
        costeObra,
        fachada: getCost("Fachada"),
        almacExt,
        costeMobiliario,
        audiovisual,
        variosBsk,
        coefTrans,
        markedUpItx,
        aranceles: getCost("Aranceles"),
        variosPaisCorp,
        ssGg,
        techTienda,
        capacidadAlmacen: Math.round(Math.max(0, constructionArea - sellingArea)),
        mlConfeccion: Math.round(sellingArea * 0.56),
        mlPaqueteria: Math.round(sellingArea * 0.18),
        cestas: Math.round(sellingArea / 18),
        liquidacion: obra.estado === "Completada" ? "Pendiente" : "",
    });
};

const importedObras = Array.isArray(obras2026Source?.rows)
    ? obras2026Source.rows.map(buildExcelObra)
    : [];
const initialObras = importedObras.length ? importedObras : baseObras.map(buildExcelRecord);

// /obras-mock usa los datos locales (JSON). Cualquier otra ruta (/admin/obras)
// pide las obras al backend. Se decide por la URL para no acoplar el componente
// a react-router ni tener dos componentes casi idénticos.
const isMockObrasRoute = () =>
    typeof window !== "undefined" && window.location.pathname.includes("/obras-mock");

// Catálogos gestionados en dbo.Obras_Catalogos: DB_CATALOG_TYPES (catalogSync.js).
// Studio en la ruta real: empresas de tipo 4 del API, sin mezclar con la lista revisada.
const STUDIO_FROM_API = new Set(["studio"]);

// Catálogos de proveedores: sus valores son filas de dbo.Empresas (tabla compartida con
// otros módulos). Desde Obras se pueden crear y renombrar (el API escribe en Empresas),
// pero no deshabilitar ni borrar: eso afectaría a los demás módulos.
const EMPRESA_CATALOG_TYPES = new Set(EMPRESA_CATALOG_CODES);

// Tipos de ubicación que ofrece la ficha (dbo.Obras_Ubicaciones.Tipo es texto libre).
const UBICACION_TIPOS = ["Ciudad", "Centro comercial", "Retail park", "Aeropuerto", "Otro"];

// Catálogos que se pueden renombrar desde la gestión de catálogo (el API registra el
// cambio). No se renombran: el estado de obra (la lógica depende de sus valores exactos),
// la zona comercial (no se edita en la ficha) ni PM/PM2/PMI (usuarios que gestiona el
// equipo en BD). Los proveedores sí: el API los renombra en dbo.Empresas.
const RENAMEABLE_CATALOG_TYPES = new Set([
    ...[...DB_CATALOG_TYPES].filter((tipo) => !["estado", "zona_comercial"].includes(tipo)),
    ...EMPRESA_CATALOG_TYPES,
]);

// Fecha de auditoría del backend (UTC sin zona) -> texto local legible.
function formatAuditDate(value) {
    if (!value) return "";
    const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("es-ES", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
}

// Fila de auditoría del API -> entrada del timeline (misma forma que las locales).
function mapAuditRow(row) {
    return {
        id: row.id,
        codObra: row.codObra,
        field: row.field,
        label: row.label,
        previousValue: row.previousValue,
        newValue: row.newValue,
        reason: row.reason,
        user: row.user || "Sistema",
        date: formatAuditDate(row.date),
        source: row.source || "",
    };
}

const excelFieldGroups = [
    {
        id: "general",
        label: "General",
        fields: [
            { field: "codigoTienda", label: "Código tienda", maxLength: 50 },
            { field: "tienda", label: "Nombre tienda", maxLength: 150 },
            { field: "pais", label: "País", catalog: "pais" },
            { field: "ciudad", label: "Ubicación", catalog: "ciudad" },
            { field: "tipo", label: "Tipo", catalog: "tipo" },
            { field: "campana", label: "Campaña", catalog: "campana" },
            { field: "inicioObra", label: "Inicio Obra", type: "date" },
            { field: "apertura", label: "Apertura", type: "date" },
            { field: "dias", label: "Días", type: "number", calculated: true },
            {
                field: "estado",
                label: "Estado",
                catalog: "estado",
            },
            { field: "supCons", label: "Sup. Cons.", type: "number" },
            { field: "supVta", label: "Sup. Vta.", type: "number" },
            { field: "cM2", label: "Coste m² ref.", type: "number" },
            { field: "capex", label: "CAPEX", type: "number" },
            { field: "vnc", label: "VNC", type: "number" },
            { field: "vta3Year", label: "Vta. 3Y", type: "number" },
            { field: "ventaM2", label: "Vta./m²", type: "number" },
        ],
    },
    {
        id: "equipo",
        label: "Equipo",
        fields: [
            { field: "consulting", label: "Consulting", catalog: "consulting" },
            { field: "constructora", label: "Constructora", catalog: "constructora" },
            { field: "studio", label: "Studio", catalog: "studio" },
            { field: "pm", label: "PM", usuario: "codPm", rol: "pm" },
            { field: "pm2", label: "PM2", usuario: "codPm2", rol: "pm" },
            { field: "pm3", label: "PM3", usuario: "codPm3", rol: "pm" },
            { field: "pmi", label: "PMI", usuario: "codPmi", rol: "pmi" },
        ],
    },
    {
        id: "hitos",
        label: "Hitos y fechas",
        fields: [
            { field: "imagen", label: "Imagen", catalog: "imagen" },
            { field: "imgOriginal", label: "Imagen Origen", catalog: "imgOriginal" },
            // Sí/No fijo: en BD es un bit (SolZonif), así que no es un catálogo que se pueda ampliar.
            { field: "solZonif", label: "Sol. Zonif.", options: ["Si", "No"] },
            { field: "zonif", label: "Zonif.", type: "date" },
            { field: "legal", label: "Legal", type: "date" },
            { field: "planoP3", label: "Plano P3", type: "date" },
            { field: "deadlineMob", label: "Deadline MOB", type: "date" },
            { field: "p3Constructora", label: "P3 constructora", type: "date" },
            { field: "p3Mobiliario", label: "P3 mobiliario", type: "date" },
            { field: "inicioMontajeMob", label: "Inicio montaje mob.", type: "date" },
            { field: "finMontajeMob", label: "Fin montaje mob.", type: "date" },
            { field: "mobiliario", label: "Prov. Mobiliario", catalog: "mobiliario" },
            { field: "sonido", label: "Prov. Audiovisuales", catalog: "sonido" },
            { field: "ilumGral", label: "Ilum. Gral.", catalog: "ilumGral" },
            { field: "ilumCaja", label: "Ilum. Caja", catalog: "ilumGral" },
            { field: "ilumProb", label: "Ilum. Prob.", catalog: "ilumGral" },
            { field: "ilumVigas", label: "Ilum. Vigas", catalog: "ilumGral" },
            { field: "provIlum1", label: "Prov. Ilum. 1", catalog: "provIlum" },
            { field: "provIlum2", label: "Prov. Ilum. 2", catalog: "provIlum" },
            // Suelo por zona: proveedor ('provSuelo') y tipo ('tipoSuelo'), cada uno un
            // catálogo único compartido por las tres zonas (como iluminación).
            { field: "sueloGral", label: "Prov. Suelo Gral.", catalog: "provSuelo" },
            { field: "tipoSueloGral", label: "Tipo Suelo Gral.", catalog: "tipoSuelo" },
            { field: "sueloAlf", label: "Prov. Suelo Alf.", catalog: "provSuelo" },
            { field: "tipoSueloAlf", label: "Tipo Suelo Alf.", catalog: "tipoSuelo" },
            { field: "sueloProb", label: "Prov. Suelo Prob.", catalog: "provSuelo" },
            { field: "tipoSueloProb", label: "Tipo Suelo Prob.", catalog: "tipoSuelo" },
        ],
    },
    {
        id: "costes",
        label: "Costes / CAPEX",
        fields: [
            { field: "licencias", label: "Licencias", type: "number" },
            { field: "presupuesto", label: "Presup. OC", type: "number" },
            { field: "adicional", label: "Adicionales OC", type: "number" },
            { field: "costeObra", label: "Obra", type: "number", calculated: true },
            { field: "fachada", label: "Fachada", type: "number" },
            { field: "almacExt", label: "Alm. Ext.", type: "number" },
            { field: "totalOc", label: "Total OC", type: "number", calculated: true },
            { field: "euroM2Oc", label: "€/m² OC", type: "number", calculated: true },
            { field: "costeMobiliario", label: "Mobiliario", type: "number" },
            { field: "audiovisual", label: "Audiovisual", type: "number" },
            { field: "variosBsk", label: "Varios", type: "number" },
            { field: "subtotalBsk", label: "Subt. BSK", type: "number", calculated: true },
            { field: "coefTrans", label: "Coef. Trans.", type: "number", step: "0.01", calculated: true },
            { field: "transporteBsk", label: "Transporte BSK", type: "number", calculated: true },
            { field: "markedUpItx", label: "Marked up ITX", type: "number", step: "0.01", calculated: true },
            { field: "totalBsk", label: "Total BSK", type: "number", calculated: true },
            { field: "euroM2Bsk", label: "€/m² BSK", type: "number", calculated: true },
            { field: "aranceles", label: "Aranceles", type: "number" },
            { field: "variosPaisCorp", label: "Varios País / Corp", type: "number" },
            { field: "ssGg", label: "SS.GG.", type: "number" },
            { field: "techTienda", label: "Tech. Tienda", type: "number" },
            { field: "totalOtros", label: "Total Otros", type: "number", calculated: true },
            { field: "totalInversion", label: "Total Inversión", type: "number", calculated: true },
            { field: "euroM2Inv", label: "€/m² Inv.", type: "number", calculated: true },
            { field: "observacionesCalculo", label: "Observaciones control CAPEX", multiline: true, calculated: true },
            { field: "liquidacion", label: "Liquidación", catalog: "liquidacion" },
            { field: "liquidacionNotas", label: "Notas de liquidación", multiline: true, maxLength: 500 },
            { field: "observaciones", label: "Notas de la obra", multiline: true, maxLength: 1000 },
        ],
    },
    {
        id: "operativa",
        label: "Operativa",
        fields: [
            { field: "capacidadAlmacen", label: "Capacidad Almacén", maxLength: 60 },
            { field: "mlConfeccion", label: "ML Confección", type: "number" },
            { field: "mlPaqueteria", label: "ML Paquetería", type: "number" },
            { field: "cestas", label: "Nº Cestas", type: "number" },
        ],
    },
];


// Campos que guardan valores de cada catálogo (un catálogo puede servir a varios campos).
const CATALOG_FIELDS = excelFieldGroups.reduce((acc, group) => {
    group.fields.forEach((config) => {
        if (!config.catalog) return;
        (acc[config.catalog] = acc[config.catalog] || []).push(config.field);
    });
    return acc;
}, {});

// Pestañas y tarjetas de la ficha: ver detailLayout.js (su test comprueba que todos
// los campos editables de excelFieldGroups tienen tarjeta).

const fieldSearchIndex = excelFieldGroups.flatMap((group) =>
    group.fields.map((fieldConfig) => ({
        ...fieldConfig,
        groupId: group.id,
        groupLabel: group.label,
        searchText: normalizeSearchValue([
            fieldConfig.label,
            fieldConfig.field,
            group.label,
        ].join(" ")),
    }))
);

const fieldConfigByName = {
    ...Object.fromEntries(
        excelFieldGroups.flatMap((group) =>
            group.fields.map((fieldConfig) => [fieldConfig.field, fieldConfig])
        )
    ),
    // Fecha "va a obra", gestión+transporte y fabricación por entregable (dentro de su card).
    // Deadline MOB es la fecha va a obra del mobiliario, pero se pinta en la tarjeta de
    // Imagen, zonificación y legal con su nombre del Excel (petición de BSK, 09/2026).
    fechaIluminacion: { field: "fechaIluminacion", label: "Fecha va a obra", type: "date" },
    fechaSuelo: { field: "fechaSuelo", label: "Fecha va a obra", type: "date" },
    bufMobiliario: { field: "bufMobiliario", label: "Gestión + transporte (días)", type: "number" },
    bufIluminacion: { field: "bufIluminacion", label: "Gestión + transporte (días)", type: "number" },
    bufSuelo: { field: "bufSuelo", label: "Gestión + transporte (días)", type: "number" },
    fabMobiliario: { field: "fabMobiliario", label: "Fabricación (días)", type: "number" },
    fabIluminacion: { field: "fabIluminacion", label: "Fabricación (días)", type: "number" },
    fabSuelo: { field: "fabSuelo", label: "Fabricación (días)", type: "number" },
};

// Índice del buscador de la ficha: etiqueta del campo + pestaña y tarjeta donde está.
const DETAIL_SEARCH_INDEX = DETAIL_TABS.flatMap((tab) => tab.sections.flatMap((section) => section.fields.map((field) => {
    const config = fieldConfigByName[field];
    const placement = detailPlacementOfField(field);
    const label = config?.label || field;
    return {
        field,
        label,
        groupId: tab.id,
        groupLabel: `${placement.tabLabel} · ${placement.sectionLabel}`,
        calculated: Boolean(config?.calculated),
        searchText: normalizeSearchValue([label, field, placement.tabLabel, placement.sectionLabel].join(" ")),
    };
})));


// Entregables físicos: además de su fecha de entrega llevan un buffer de días de
// transporte (por defecto +7) hasta que están listos para montaje. Los tres
// comparten la fecha "Deadline MOB"; cada uno puede ajustar su propio transporte.
const DEFAULT_TRANSPORT_BUFFER = 7;
const PHYSICAL_DELIVERABLES = [
    { seccion: "Mobiliario", label: "Mobiliario", bufferField: "bufMobiliario", proveedorField: "mobiliario" },
    { seccion: "Iluminacion", label: "Iluminación", bufferField: "bufIluminacion", proveedorField: "provIlum1" },
    { seccion: "Suelo", label: "Suelo", bufferField: "bufSuelo", proveedorField: "sueloGral" },
];
const PHYSICAL_BUFFER_FIELDS = PHYSICAL_DELIVERABLES.map((item) => item.bufferField);
const getBufferDays = (obra, field) => {
    const raw = obra?.[field];
    if (raw === "" || raw === null || raw === undefined) return DEFAULT_TRANSPORT_BUFFER;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : DEFAULT_TRANSPORT_BUFFER;
};
const addDaysToIso = (iso, days) => {
    if (!iso) return "";
    const base = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(base.getTime())) return "";
    base.setDate(base.getDate() + Number(days || 0));
    return base.toISOString().slice(0, 10);
};

// ── Informes descargables (.xlsx) de las obras seleccionadas ──────────────────
// Helpers de celda: números como number (Excel los suma), fechas dd/mm/aaaa, texto plano.
const cellNum = (value) => (value === "" || value === null || value === undefined ? "" : Number(value));
const cellDate = (iso) => (iso ? formatCompactDate(iso) : "");
const cellText = (value) => (value === null || value === undefined ? "" : String(value));
const cellEstado = (obra) => (VALID_OBRA_STATUSES.has(obra.estado) ? obra.estado : "");
const cellMontaje = (obra, bufferField) =>
    (obra.deadlineMob ? cellDate(addDaysToIso(obra.deadlineMob, getBufferDays(obra, bufferField))) : "");

// Exportación "Excel completo": inverso de buildExcelObra. Cada columna del fichero
// base se obtiene del estado real de la obra (no del JSON mock, que solo existe en
// desarrollo), de modo que el export funciona también en producción y refleja los
// valores cargados/editados en lugar de salir en blanco.
const EXCEL_COMPLETE_COLUMNS = [
    { label: "COD", get: (o) => cellText(o.codigoTienda) },
    { label: "PAIS", get: (o) => cellText(o.pais) },
    { label: "CIUDAD", get: (o) => cellText(o.ciudad) },
    { label: "TIENDA", get: (o) => cellText(o.tienda) },
    { label: "TIPO", get: (o) => cellText(o.tipo) },
    { label: "INICIO O,", get: (o) => cellDate(o.inicioObra) },
    { label: "APERTURA", get: (o) => cellDate(o.apertura) },
    { label: "DIAS", get: (o) => cellNum(o.dias) },
    { label: "CONSULTING", get: (o) => cellText(o.consulting) },
    { label: "CONSTRUCTORA", get: (o) => cellText(o.constructora) },
    { label: "STUDIO", get: (o) => cellText(o.studio) },
    { label: "PM", get: (o) => cellText(o.pm) },
    { label: "PM2", get: (o) => cellText(o.pm2) },
    { label: "PM3", get: (o) => cellText(o.pm3) },
    { label: "PMI", get: (o) => cellText(o.pmi) },
    { label: "ESTADO", get: (o) => cellEstado(o) },
    { label: "SUP CONS", get: (o) => cellNum(o.supCons) },
    { label: "SUP VTA", get: (o) => cellNum(o.supVta) },
    { label: "C/M2", get: (o) => cellNum(o.cM2) },
    { label: "CAPEX", get: (o) => cellNum(o.capex) },
    { label: "TOTAL INVERSION", get: (o) => cellNum(o.totalInversion) },
    { label: "IMAGEN", get: (o) => cellText(o.imagen) },
    { label: "IMG. ORIGINAL", get: (o) => cellText(o.imgOriginal) },
    { label: "Sol Zonif", get: (o) => cellText(o.solZonif) },
    { label: "ZONIF", get: (o) => cellDate(o.zonif) },
    { label: "LEGAL", get: (o) => cellDate(o.legal) },
    { label: "PLANO P3", get: (o) => cellDate(o.planoP3) },
    { label: "Deadline MOB", get: (o) => cellDate(o.deadlineMob) },
    { label: "P3 CONSTRUCTORA", get: (o) => cellDate(o.p3Constructora) },
    { label: "P3 MOBILIARIO", get: (o) => cellDate(o.p3Mobiliario) },
    { label: "INICIO MONTAJE MOB", get: (o) => cellDate(o.inicioMontajeMob) },
    { label: "FIN MONTAJE MOB", get: (o) => cellDate(o.finMontajeMob) },
    { label: "MOBILIARIO", get: (o) => cellText(o.mobiliario) },
    { label: "SONIDO", get: (o) => cellText(o.sonido) },
    { label: "ILUM GRAL", get: (o) => cellText(o.ilumGral) },
    { label: "ILUM CAJA", get: (o) => cellText(o.ilumCaja) },
    { label: "ILUM PROB", get: (o) => cellText(o.ilumProb) },
    { label: "ILUM VIGAS", get: (o) => cellText(o.ilumVigas) },
    { label: "PROV ILUM 1", get: (o) => cellText(o.provIlum1) },
    { label: "PROV ILUM 2", get: (o) => cellText(o.provIlum2) },
    { label: "SUELO gral", get: (o) => cellText(o.sueloGral) },
    { label: "SUELO alf", get: (o) => cellText(o.sueloAlf) },
    { label: "SUELO prob", get: (o) => cellText(o.sueloProb) },
    { label: "TIPO SUELO gral", get: (o) => cellText(o.tipoSueloGral) },
    { label: "TIPO SUELO alf", get: (o) => cellText(o.tipoSueloAlf) },
    { label: "TIPO SUELO prob", get: (o) => cellText(o.tipoSueloProb) },
    { label: "LICENCIAS", get: (o) => cellNum(o.licencias) },
    { label: "PRESUPUESTO", get: (o) => cellNum(o.presupuesto) },
    { label: "Adicional", get: (o) => cellNum(o.adicional) },
    { label: "Obra", get: (o) => cellNum(o.costeObra) },
    { label: "Fachada", get: (o) => cellNum(o.fachada) },
    { label: "Almac ext", get: (o) => cellNum(o.almacExt) },
    { label: "TOTAL OC", get: (o) => cellNum(o.totalOc) },
    { label: "Mobiliario", get: (o) => cellNum(o.costeMobiliario) },
    { label: "AudioVisual", get: (o) => cellNum(o.audiovisual) },
    { label: "Varios", get: (o) => cellNum(o.variosBsk) },
    { label: "SubTotal BSK", get: (o) => cellNum(o.subtotalBsk) },
    { label: "Transporte BSK", get: (o) => cellNum(o.transporteBsk) },
    { label: "TOTAL BSK", get: (o) => cellNum(o.totalBsk) },
    { label: "Aranceles", get: (o) => cellNum(o.aranceles) },
    { label: "Varios Pais//Corport", get: (o) => cellNum(o.variosPaisCorp) },
    { label: "SS.GG", get: (o) => cellNum(o.ssGg) },
    { label: "Tech Tda.", get: (o) => cellNum(o.techTienda) },
    { label: "TOTAL OTROS", get: (o) => cellNum(o.totalOtros) },
    { label: "Observaciones", get: (o) => cellText(o.observaciones) },
    { label: "LIQUIDACIÓN ", get: (o) => cellText(o.liquidacion) },
    { label: "TIPO_NORMALIZADO", get: (o) => cellText(o.tipoNormalizado ?? o.tipo) },
];

const REPORT_COMMON_COLUMNS = [
    { label: "Código", get: (o) => cellText(o.codigoTienda) },
    { label: "Obra", get: (o) => cellText(o.tienda) },
    { label: "País", get: (o) => cellText(o.pais) },
    { label: "Ciudad", get: (o) => cellText(o.ciudad) },
    { label: "Estado", get: (o) => cellEstado(o) },
];

const REPORT_DEFINITIONS = {
    economico: {
        name: "Económico",
        sheet: "Económico",
        fileBase: "Informe_economico",
        columns: [
            ...REPORT_COMMON_COLUMNS,
            { label: "Licencias", get: (o) => cellNum(o.licencias) },
            { label: "Presupuesto", get: (o) => cellNum(o.presupuesto) },
            { label: "Adicional", get: (o) => cellNum(o.adicional) },
            { label: "Total OC", get: (o) => cellNum(o.totalOc) },
            { label: "€/m² OC", get: (o) => cellNum(o.euroM2Oc) },
            { label: "Mobiliario", get: (o) => cellNum(o.costeMobiliario) },
            { label: "Audiovisual", get: (o) => cellNum(o.audiovisual) },
            { label: "Varios BSK", get: (o) => cellNum(o.variosBsk) },
            { label: "Subtotal BSK", get: (o) => cellNum(o.subtotalBsk) },
            { label: "Transporte BSK", get: (o) => cellNum(o.transporteBsk) },
            { label: "Total BSK", get: (o) => cellNum(o.totalBsk) },
            { label: "€/m² BSK", get: (o) => cellNum(o.euroM2Bsk) },
            { label: "Aranceles", get: (o) => cellNum(o.aranceles) },
            { label: "Varios país / corp", get: (o) => cellNum(o.variosPaisCorp) },
            { label: "SS.GG", get: (o) => cellNum(o.ssGg) },
            { label: "Tech tienda", get: (o) => cellNum(o.techTienda) },
            { label: "Total otros", get: (o) => cellNum(o.totalOtros) },
            { label: "Total inversión", get: (o) => cellNum(o.totalInversion) },
            { label: "€/m² inversión", get: (o) => cellNum(o.euroM2Inv) },
            { label: "CAPEX", get: (o) => cellNum(o.capex) },
            { label: "VNC", get: (o) => cellNum(o.vnc) },
            { label: "Venta 3er año", get: (o) => cellNum(o.vta3Year) },
            { label: "Venta / m²", get: (o) => cellNum(o.ventaM2) },
        ],
    },
    temporal: {
        name: "Temporal",
        sheet: "Temporal",
        fileBase: "Informe_temporal",
        columns: [
            ...REPORT_COMMON_COLUMNS,
            { label: "Inicio obra", get: (o) => cellDate(o.inicioObra) },
            { label: "Apertura", get: (o) => cellDate(o.apertura) },
            { label: "Días", get: (o) => cellNum(o.dias) },
            { label: "Zonificación", get: (o) => cellDate(o.zonif) },
            { label: "Legal", get: (o) => cellDate(o.legal) },
            { label: "Plano P3", get: (o) => cellDate(o.planoP3) },
            { label: "Deadline mobiliario", get: (o) => cellDate(o.deadlineMob) },
            { label: "Transporte mobiliario (días)", get: (o) => getBufferDays(o, "bufMobiliario") },
            { label: "Mobiliario listo", get: (o) => cellMontaje(o, "bufMobiliario") },
            { label: "Transporte iluminación (días)", get: (o) => getBufferDays(o, "bufIluminacion") },
            { label: "Iluminación lista", get: (o) => cellMontaje(o, "bufIluminacion") },
            { label: "Transporte suelo (días)", get: (o) => getBufferDays(o, "bufSuelo") },
            { label: "Suelo listo", get: (o) => cellMontaje(o, "bufSuelo") },
        ],
    },
    proveedores: {
        name: "Proveedores",
        sheet: "Proveedores",
        fileBase: "Informe_proveedores",
        columns: [
            ...REPORT_COMMON_COLUMNS,
            { label: "PM", get: (o) => cellText(o.pm) },
            { label: "PM2", get: (o) => cellText(o.pm2) },
            { label: "PM3", get: (o) => cellText(o.pm3) },
            { label: "PMI", get: (o) => cellText(o.pmi) },
            { label: "Consulting", get: (o) => cellText(o.consulting) },
            { label: "Constructora", get: (o) => cellText(o.constructora) },
            { label: "Studio", get: (o) => cellText(o.studio) },
            { label: "Mobiliario", get: (o) => cellText(o.mobiliario) },
            { label: "Iluminación 1", get: (o) => cellText(o.provIlum1) },
            { label: "Iluminación 2", get: (o) => cellText(o.provIlum2) },
            { label: "Suelo general", get: (o) => cellText(o.sueloGral) },
            { label: "Suelo alfombra", get: (o) => cellText(o.sueloAlf) },
            { label: "Suelo probadores", get: (o) => cellText(o.sueloProb) },
            { label: "Imagen", get: (o) => cellText(o.imagen) },
        ],
    },
};

// ── Informe imprimible: KPIs + gráficos SVG + tabla, para "Guardar como PDF" ───
const REPORT_META = {
    economico: { title: "Informe económico", subtitle: "Costes, CAPEX e inversión de las obras seleccionadas", column: "economico" },
    temporal: { title: "Informe temporal", subtitle: "Fechas, hitos y cronograma de las obras seleccionadas", column: "temporal" },
    proveedores: { title: "Informe de proveedores", subtitle: "Asignación y carga de proveedores", column: "proveedores" },
};
const REPORT_PALETTE = ["#2d7a6d", "#4b9c8c", "#7bbcae", "#e0a24a", "#c9743d", "#8a6fb0", "#5b8bd0", "#b0475f"];
const reportEur = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const reportNum = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 });
const reportEsc = (value) => String(value ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const reportSum = (arr, get) => arr.reduce((acc, item) => acc + (Number(get(item)) || 0), 0);

function reportBars(items, { color = REPORT_PALETTE[0], rowH = 24, gap = 8, width = 680, labelW = 190 } = {}) {
    if (!items.length) return '<p class="rep-empty">Sin datos.</p>';
    const max = Math.max(1, ...items.map((i) => i.value));
    const barMax = width - labelW - 96;
    const h = items.length * (rowH + gap);
    const rows = items.map((item, idx) => {
        const y = idx * (rowH + gap);
        const w = Math.max(2, (item.value / max) * barMax);
        return `<g transform="translate(0 ${y})">`
            + `<text x="0" y="${rowH / 2}" dominant-baseline="middle" class="rep-bl">${reportEsc(item.label)}</text>`
            + `<rect x="${labelW}" y="3" width="${w}" height="${rowH - 6}" rx="3" fill="${color}"></rect>`
            + `<text x="${labelW + w + 8}" y="${rowH / 2}" dominant-baseline="middle" class="rep-bv">${reportEsc(item.display)}</text>`
            + `</g>`;
    }).join("");
    return `<svg viewBox="0 0 ${width} ${h}" class="rep-svg" preserveAspectRatio="xMinYMin meet">${rows}</svg>`;
}

function reportDonut(segments, { size = 190 } = {}) {
    const total = segments.reduce((acc, s) => acc + (Number(s.value) || 0), 0);
    if (!total) return '<p class="rep-empty">Sin datos.</p>';
    const r = size / 2;
    const inner = r * 0.58;
    let angle = -Math.PI / 2;
    const arcs = segments.map((seg, idx) => {
        const frac = (Number(seg.value) || 0) / total;
        if (frac <= 0) return "";
        const a2 = angle + frac * 2 * Math.PI;
        const large = frac > 0.5 ? 1 : 0;
        const x1 = r + r * Math.cos(angle);
        const y1 = r + r * Math.sin(angle);
        const x2 = r + r * Math.cos(a2);
        const y2 = r + r * Math.sin(a2);
        const xi2 = r + inner * Math.cos(a2);
        const yi2 = r + inner * Math.sin(a2);
        const xi1 = r + inner * Math.cos(angle);
        const yi1 = r + inner * Math.sin(angle);
        angle = a2;
        const color = REPORT_PALETTE[idx % REPORT_PALETTE.length];
        return `<path d="M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} L ${xi2.toFixed(2)} ${yi2.toFixed(2)} A ${inner} ${inner} 0 ${large} 0 ${xi1.toFixed(2)} ${yi1.toFixed(2)} Z" fill="${color}"></path>`;
    }).join("");
    return `<svg viewBox="0 0 ${size} ${size}" class="rep-donut" preserveAspectRatio="xMidYMid meet">${arcs}</svg>`;
}

function reportLegend(segments) {
    return `<ul class="rep-legend">${segments.map((s, i) => `<li><span style="background:${REPORT_PALETTE[i % REPORT_PALETTE.length]}"></span><em>${reportEsc(s.label)}</em><b>${reportEsc(s.display ?? s.value)}</b></li>`).join("")}</ul>`;
}

function reportKpiRow(kpis) {
    return `<div class="rep-kpis">${kpis.map((k) => `<div class="rep-kpi"><span class="rep-kpi-lbl">${reportEsc(k.label)}</span><span class="rep-kpi-val">${reportEsc(k.value)}</span>${k.note ? `<span class="rep-kpi-note">${reportEsc(k.note)}</span>` : ""}</div>`).join("")}</div>`;
}

function reportTable(kind, obras) {
    const spec = REPORT_DEFINITIONS[kind];
    if (!spec) return "";
    const head = spec.columns.map((c) => `<th>${reportEsc(c.label)}</th>`).join("");
    const rows = obras.map((obra) => {
        const cells = spec.columns.map((col) => {
            const value = col.get(obra);
            const display = typeof value === "number" ? reportNum.format(value) : reportEsc(value);
            return `<td>${display}</td>`;
        }).join("");
        return `<tr>${cells}</tr>`;
    }).join("");
    return `<table class="rep-table"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function reportBlocks(kind, obras) {
    const topBars = (items) => items.slice().sort((a, b) => b.value - a.value).slice(0, 12);
    if (kind === "economico") {
        const totalInv = reportSum(obras, (o) => o.totalInversion);
        const totalCapex = reportSum(obras, (o) => o.capex);
        const totalOc = reportSum(obras, (o) => o.totalOc);
        const totalBsk = reportSum(obras, (o) => o.totalBsk);
        const totalOtros = reportSum(obras, (o) => o.totalOtros);
        const totalSup = reportSum(obras, (o) => o.supVta);
        const kpis = [
            { label: "Inversión total", value: reportEur.format(totalInv) },
            { label: "CAPEX total", value: reportEur.format(totalCapex) },
            { label: "€/m² medio", value: totalSup ? reportEur.format(Math.round(totalInv / totalSup)) : "—", note: "sobre sala de venta" },
            { label: "Obras", value: reportNum.format(obras.length) },
        ];
        const desglose = [
            { label: "Obra civil (OC)", value: totalOc, display: reportEur.format(totalOc) },
            { label: "Equipamiento BSK", value: totalBsk, display: reportEur.format(totalBsk) },
            { label: "Otros / corporativo", value: totalOtros, display: reportEur.format(totalOtros) },
        ];
        const porObra = topBars(obras.map((o) => ({ label: `${o.codigoTienda} · ${o.tienda}`, value: Number(o.totalInversion) || 0, display: reportEur.format(Number(o.totalInversion) || 0) })));
        return `${reportKpiRow(kpis)}
            <div class="rep-grid">
                <section class="rep-card"><h2>Desglose del coste</h2><div class="rep-donut-wrap">${reportDonut(desglose)}${reportLegend(desglose)}</div></section>
                <section class="rep-card rep-card--wide"><h2>Inversión por obra</h2>${reportBars(porObra)}</section>
            </div>
            <section class="rep-card"><h2>Detalle por obra</h2>${reportTable(kind, obras)}</section>`;
    }
    if (kind === "temporal") {
        const withOpen = obras.filter((o) => o.apertura);
        const future = withOpen.filter((o) => o.apertura >= mockTodayIso).sort((a, b) => a.apertura.localeCompare(b.apertura));
        const in90 = withOpen.filter((o) => { const d = dateFromIso(o.apertura); return d && (d - portfolioToday) / 86400000 <= 90 && d >= portfolioToday; });
        const kpis = [
            { label: "Obras", value: reportNum.format(obras.length) },
            { label: "Próxima apertura", value: future.length ? formatCompactDate(future[0].apertura) : "—", note: future.length ? `${future[0].codigoTienda} · ${future[0].tienda}` : "" },
            { label: "Aperturas ≤ 90 días", value: reportNum.format(in90.length) },
            { label: "Días medios de obra", value: obras.length ? reportNum.format(Math.round(reportSum(obras, (o) => o.dias) / obras.length)) : "—" },
        ];
        const estadoCounts = {};
        obras.forEach((o) => { const e = VALID_OBRA_STATUSES.has(o.estado) ? o.estado : "Sin estado"; estadoCounts[e] = (estadoCounts[e] || 0) + 1; });
        const estados = Object.entries(estadoCounts).map(([label, value]) => ({ label, value, display: reportNum.format(value) }));
        const monthCounts = {};
        withOpen.forEach((o) => { const m = o.apertura.slice(0, 7); monthCounts[m] = (monthCounts[m] || 0) + 1; });
        const meses = Object.keys(monthCounts).sort().map((m) => ({ label: m, value: monthCounts[m], display: reportNum.format(monthCounts[m]) }));
        return `${reportKpiRow(kpis)}
            <div class="rep-grid">
                <section class="rep-card"><h2>Distribución por estado</h2><div class="rep-donut-wrap">${reportDonut(estados)}${reportLegend(estados)}</div></section>
                <section class="rep-card rep-card--wide"><h2>Aperturas por mes</h2>${reportBars(meses, { color: REPORT_PALETTE[3] })}</section>
            </div>
            <section class="rep-card"><h2>Fechas por obra</h2>${reportTable(kind, obras)}</section>`;
    }
    // proveedores
    const providerCounts = {};
    const constructoraCounts = {};
    let sinProveedor = 0;
    obras.forEach((o) => {
        const provs = [o.mobiliario, o.provIlum1, o.sueloGral].map((p) => (p || "").trim()).filter(Boolean);
        if (!provs.length) sinProveedor += 1;
        provs.forEach((p) => { providerCounts[p] = (providerCounts[p] || 0) + 1; });
        const c = (o.constructora || "").trim();
        if (c) constructoraCounts[c] = (constructoraCounts[c] || 0) + 1;
    });
    const providerEntries = Object.entries(providerCounts).sort((a, b) => b[1] - a[1]);
    const kpis = [
        { label: "Proveedores distintos", value: reportNum.format(providerEntries.length) },
        { label: "Proyectos sin proveedor", value: reportNum.format(sinProveedor) },
        { label: "Proveedor más frecuente", value: providerEntries.length ? providerEntries[0][0] : "—", note: providerEntries.length ? `${providerEntries[0][1]} obras` : "" },
        { label: "Constructoras", value: reportNum.format(Object.keys(constructoraCounts).length) },
    ];
    const provBars = providerEntries.slice(0, 12).map(([label, value]) => ({ label, value, display: reportNum.format(value) }));
    const constrSegments = Object.entries(constructoraCounts).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value, display: reportNum.format(value) }));
    return `${reportKpiRow(kpis)}
        <div class="rep-grid">
            <section class="rep-card rep-card--wide"><h2>Obras por proveedor</h2>${reportBars(provBars, { color: REPORT_PALETTE[0] })}</section>
            <section class="rep-card"><h2>Reparto por constructora</h2><div class="rep-donut-wrap">${reportDonut(constrSegments)}${reportLegend(constrSegments)}</div></section>
        </div>
        <section class="rep-card"><h2>Proveedores por obra</h2>${reportTable(kind, obras)}</section>`;
}

function buildObraReport(kind, obras) {
    const meta = REPORT_META[kind];
    if (!meta || !obras.length) return "";
    const stamp = new Date();
    const dateLabel = stamp.toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
    const body = reportBlocks(kind, obras);
    return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${reportEsc(meta.title)}</title>
<style>
    @page { size: A4 landscape; margin: 10mm 11mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: "Mulish", "Segoe UI", Arial, sans-serif; color: #26364c; font-size: 12px; }
    .rep-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; padding-bottom: 12px; border-bottom: 3px solid #2d7a6d; }
    .rep-head h1 { margin: 0; font-size: 22px; color: #2d7a6d; }
    .rep-head p { margin: 4px 0 0; color: #6b7785; font-size: 12px; }
    .rep-head .rep-meta { text-align: right; color: #6b7785; font-size: 11px; line-height: 1.5; }
    .rep-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }
    .rep-kpi { border: 1px solid #e2e9e6; border-radius: 8px; padding: 10px 12px; background: #f6faf8; }
    .rep-kpi-lbl { display: block; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #6b7785; }
    .rep-kpi-val { display: block; margin-top: 4px; font-size: 20px; font-weight: 900; color: #1f2a24; }
    .rep-kpi-note { display: block; margin-top: 2px; font-size: 10px; color: #8a94a0; }
    .rep-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 12px; margin-bottom: 12px; }
    .rep-card { border: 1px solid #e2e9e6; border-radius: 8px; padding: 12px 14px; page-break-inside: avoid; }
    .rep-card--wide { grid-column: auto; }
    .rep-card h2 { margin: 0 0 10px; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #2d7a6d; }
    .rep-svg { width: 100%; height: auto; }
    .rep-bl { font-size: 11px; fill: #26364c; font-weight: 700; }
    .rep-bv { font-size: 11px; fill: #4b5563; font-weight: 800; }
    .rep-donut-wrap { display: flex; align-items: center; gap: 14px; }
    .rep-donut { width: 150px; height: 150px; flex: 0 0 auto; }
    .rep-legend { list-style: none; margin: 0; padding: 0; display: grid; gap: 5px; font-size: 11px; }
    .rep-legend li { display: flex; align-items: center; gap: 6px; }
    .rep-legend span { width: 11px; height: 11px; border-radius: 3px; flex: 0 0 auto; }
    .rep-legend em { font-style: normal; color: #4b5563; }
    .rep-legend b { margin-left: auto; font-variant-numeric: tabular-nums; }
    .rep-table { width: 100%; border-collapse: collapse; font-size: 9.5px; }
    .rep-table th { text-align: left; padding: 5px 6px; background: #eef5f2; color: #2d7a6d; font-weight: 800; border-bottom: 2px solid #d7e5e0; word-break: break-word; }
    .rep-table td { padding: 4px 6px; border-bottom: 1px solid #eef1f0; white-space: normal; word-break: break-word; }
    .rep-table td:first-child, .rep-table th:first-child { white-space: nowrap; }
    .rep-table tbody tr:nth-child(even) { background: #fafcfb; }
    .rep-empty { color: #8a94a0; font-style: italic; }
    .rep-foot { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e2e9e6; color: #8a94a0; font-size: 10px; text-align: center; }
</style></head><body>
    <header class="rep-head">
        <div><h1>${reportEsc(meta.title)}</h1><p>${reportEsc(meta.subtitle)}</p></div>
        <div class="rep-meta">Obras 2026<br>${reportEsc(dateLabel)}<br><b>${obras.length} ${obras.length === 1 ? "obra" : "obras"}</b></div>
    </header>
    ${body}
    <footer class="rep-foot">Generado desde el cuadro de mandos Obras 2026 · ${reportEsc(dateLabel)}</footer>
</body></html>`;
}

const mockTodayIso = portfolioToday instanceof Date ? portfolioToday.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
const mockToday = portfolioToday;

const numberFormatter = new Intl.NumberFormat("es-ES", {
    useGrouping: "always",
    maximumFractionDigits: 2,
});

const numberEditorFormatter = new Intl.NumberFormat("es-ES", {
    useGrouping: false,
    maximumFractionDigits: 2,
});

function parseLocaleNumber(value) {
    if (typeof value === "number") return value;

    const normalized = String(value ?? "")
        .trim()
        .replace(/\s/g, "");

    if (!normalized) return "";

    const decimalNormalized = normalized.includes(",")
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.split(".").length > 2 || /^\D*-?\d{1,3}(\.\d{3})+$/.test(normalized)
            ? normalized.replace(/\./g, "")
            : normalized;
    const parsed = Number(decimalNormalized);

    return Number.isFinite(parsed) ? parsed : "";
}

function formatNumber(value) {
    const parsed = parseLocaleNumber(value);
    return parsed === "" ? "" : numberFormatter.format(parsed);
}

function formatEditableNumber(value) {
    if (typeof value === "string") return value;
    return Number.isFinite(value) ? numberEditorFormatter.format(value) : "";
}

function roundToTwoDecimals(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

export const statusClass = {
    "Completada": "success",
    "Completado": "success",
    "En curso": "info",
    "Planificada": "neutral",
    "Pendiente": "warning",
    "Riesgo": "danger",
    "En riesgo": "danger",
    "Bloqueada": "danger",
    "Reserva": "neutral",
};

function getCatalogValues(field, obra, currentValue, catalogs) {
    const entries = catalogs[field] || [];
    const availableEntries = entries.filter(entry => field !== "ciudad" || (obra.pais && entry.parent && normalizeSearchValue(entry.parent) === normalizeSearchValue(obra.pais)));

    if (currentValue !== "" && currentValue !== null && currentValue !== undefined) {
        const normalizedValue = String(currentValue);
        if (!availableEntries.some((entry) => String(entry.value) === normalizedValue)) {
            return [
                {
                    id: `${field}-current-${normalizedValue}`,
                    value: currentValue,
                    parent: field === "ciudad" ? obra.pais : null,
                    active: true,
                    synthetic: true,
                },
                ...availableEntries,
            ];
        }
    }

    return availableEntries;
}

// `names`: CodUsuario -> nombre (PM, PM2, PM3, PMI se guardan por código); `text`: el
// nombre que ya trae el historial del API.
function formatChangeValue(value, field, names, text) {
    if (value === "" || value === null || value === undefined) return "Sin dato";
    const usuario = userValueText(field, value, names, text);
    if (usuario) return usuario;
    if (numericFields.has(field)) return formatNumber(value);
    return String(value);
}

function LocalizedNumberInput({ value, onChange, onKeyDown, ...props }) {
    const [focused, setFocused] = useState(false);

    return (
        <input
            {...props}
            type="text"
            inputMode="decimal"
            value={focused ? formatEditableNumber(value) : formatNumber(value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={onChange}
            onKeyDown={onKeyDown}
        />
    );
}

function StatusBadge({ value }) {
    return (
        <span className={`obras-badge obras-badge--${statusClass[value] || "neutral"}`}>
            {value}
        </span>
    );
}

// Opción para dejar un campo sin valor, la misma en todos los desplegables de edición.
const EMPTY_OPTION_LABEL = EMPTY_FILTER_LABEL;

function CatalogSelect({
    ariaLabel,
    className = "",
    onChange,
    onCreate,
    onKeyDown,
    onOpenManager,
    onRequestStatusChange,
    options,
    value,
    disabled = false,
    allowCreate = true,
    allowClear = true,
    onDisabledAttempt,
}) {
    const [adding, setAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [newValue, setNewValue] = useState("");
    const [error, setError] = useState("");
    const [duplicateInactive, setDuplicateInactive] = useState(null);
    const [searchText, setSearchText] = useState(value || "");
    useEffect(() => { setSearchText(value || ""); }, [value]);

    // El dropdown se posiciona con position:fixed calculado desde el campo, para
    // escapar del overflow de las tarjetas/paneles de la ficha (si no, se recorta).
    const fieldRef = useRef(null);
    const containerRef = useRef(null);
    const [ddPos, setDdPos] = useState(null);
    // Cerrar al clicar fuera (mousedown en captura: el stopPropagation del contenedor
    // no lo bloquea, así abrir otro desplegable cierra este).
    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocMouseDown, true);
        return () => document.removeEventListener("mousedown", onDocMouseDown, true);
    }, [open]);
    useEffect(() => {
        if (!open) { setDdPos(null); return; }
        const place = () => {
            const el = fieldRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            const width = Math.max(r.width, 200);
            const spaceBelow = window.innerHeight - r.bottom;
            const openUp = spaceBelow < 240 && r.top > spaceBelow; // abre hacia arriba si no cabe
            setDdPos({
                left: Math.min(r.left, window.innerWidth - width - 8),
                top: openUp ? undefined : r.bottom + 2,
                bottom: openUp ? (window.innerHeight - r.top + 2) : undefined,
                width,
            });
        };
        place();
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [open]);

    const activeOptions = options.filter((entry) => entry.active);
    const inactiveOptions = options.filter((entry) => !entry.active);
    const selectedInactive = inactiveOptions.find(
        (entry) => String(entry.value) === String(value)
    );
    const selectOptions = selectedInactive
        ? [selectedInactive, ...activeOptions]
        : activeOptions;
    const filteredOptions = selectOptions.filter((entry) => normalizeSearchValue(entry.value).includes(normalizeSearchValue(search)));

    const closeAdd = () => {
        setAdding(false);
        setNewValue("");
        setError("");
        setDuplicateInactive(null);
    };

    const createOption = async () => {
        const normalizedValue = newValue.trim();
        if (!normalizedValue) {
            setError("Introduce un nombre.");
            return;
        }

        let result;
        try { result = await onCreate(normalizedValue); }
        catch (err) { setError(err?.response?.data?.message || err.message || "No se pudo crear el valor."); return; }
        if (result?.status === "error") { setError(result.message); return; }
        if (result?.status === "inactive") {
            setDuplicateInactive(result.entry);
            setError("");
            return;
        }

        closeAdd();
    };

    return (
        <div
            ref={containerRef}
            className={`obras-catalog-select${adding ? " is-adding" : ""}${selectedInactive ? " has-inactive-value" : ""}`}
            onClick={(event) => event.stopPropagation()}
        >
            <div className={`obras-catalog-select__field${onOpenManager ? "" : " is-plain"}`} ref={fieldRef}>
                <button
                    type="button"
                    className={`${className}${disabled ? " is-disabled" : ""}`}
                    aria-disabled={disabled}
                    title={disabled ? "Selecciona un país antes de editar la ubicación" : undefined}
                    onClick={() => { if (disabled) onDisabledAttempt?.(); else setOpen(v => !v); }}
                >{value || "Sin seleccionar"}</button>
                <FiChevronDown className="obras-catalog-select__chevron" aria-hidden="true" />
                {onOpenManager ? (
                    <button
                        type="button"
                        className="obras-catalog-select__manage"
                        aria-label={`Gestionar catalogo ${ariaLabel}`}
                        title={`Gestionar catalogo${inactiveOptions.length ? ` (${inactiveOptions.length} deshabilitados)` : ""}`}
                        aria-disabled={disabled}
                        disabled={disabled}
                        onClick={onOpenManager}
                    >
                        <FiSettings />
                        {inactiveOptions.length ? <span>{inactiveOptions.length}</span> : null}
                    </button>
                ) : null}
            </div>

            {open && ddPos ? (
                <div
                    className="obras-catalog-dropdown"
                    style={{ position: "fixed", left: ddPos.left, top: ddPos.top, bottom: ddPos.bottom, width: ddPos.width }}
                >
                    <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar…" />
                    {/* Dejar el campo vacío: sin esto un proveedor o catálogo elegido por error solo
                        se podía cambiar por otro. Se ofrece siempre, también con el campo ya
                        vacío, para que todos los desplegables tengan la misma primera opción. */}
                    {allowClear && !search ? (
                        <button type="button" className="obras-catalog-dropdown__clear" onClick={() => { if (value) onChange("", null); setOpen(false); }}>{EMPTY_OPTION_LABEL}</button>
                    ) : null}
                    {filteredOptions.map(option => <button type="button" key={option.id} onClick={() => { onChange(option.value, option); setOpen(false); setSearch(""); }}>{option.value}</button>)}
                    {allowCreate ? <button type="button" className="obras-catalog-dropdown__add" onClick={() => { setOpen(false); setAdding(true); }}>+ Añadir nuevo…</button> : null}
                </div>
            ) : null}

            {adding ? (
                <div className="obras-catalog-add">
                    {duplicateInactive ? (
                        <div className="obras-catalog-duplicate">
                            <FiRefreshCw />
                            <span>
                                <strong>{duplicateInactive.value}</strong>
                                Ya existe, pero esta deshabilitado.
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    onRequestStatusChange(duplicateInactive, true, true);
                                    closeAdd();
                                }}
                            >
                                Reactivar
                            </button>
                            <button
                                type="button"
                                className="icon-only"
                                aria-label="Cancelar reactivacion"
                                title="Cancelar"
                                onClick={closeAdd}
                            >
                                <FiX />
                            </button>
                        </div>
                    ) : (
                        <>
                            <input
                                autoFocus
                                aria-label={`Nuevo valor para ${ariaLabel}`}
                                value={newValue}
                                onChange={(event) => {
                                    setNewValue(event.target.value);
                                    setError("");
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        createOption();
                                    }
                                    if (event.key === "Escape") {
                                        event.preventDefault();
                                        closeAdd();
                                    }
                                }}
                                placeholder="Nuevo valor"
                            />
                            <button type="button" aria-label="Confirmar nuevo valor" title="Confirmar" onClick={createOption}>
                                <FiCheck />
                            </button>
                            <button
                                type="button"
                                aria-label="Cancelar nuevo valor"
                                title="Cancelar"
                                onClick={closeAdd}
                            >
                                <FiX />
                            </button>
                        </>
                    )}
                    {error ? <small>{error}</small> : null}
                </div>
            ) : null}

        </div>
    );
}

// Gestor de ubicaciones (catálogo geográfico) de un país: buscar, filtrar por región,
// añadir, editar (nombre, región y tipo), activar/desactivar, eliminar y asignar a la obra.
// "Región" es el primer nivel administrativo del país (ADM1): se llama por su nombre real
// (comunidad autónoma, provincia, estado...) según el catálogo. Robusto al 409: si el alta
// choca con una ubicación existente, se recarga y se asigna igualmente.
function GeographyManagerModal({ catalogs, initialCountry = "", assignedValue = "", usageCount, onClose, onAssign, onRenamed, onReload }) {
    const [subdivision, setSubdivision] = useState("");
    // El alta tiene su propia región: compartirla con el filtro dejaba el botón "Añadir"
    // deshabilitado al filtrar por "Todas" y reducía la lista a una región al añadir.
    const [newSubdivision, setNewSubdivision] = useState("");
    const [location, setLocation] = useState("");
    const [kind, setKind] = useState("Ciudad");
    const [search, setSearch] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [editing, setEditing] = useState(null);

    const countryName = catalogs.pais.find((x) => String(x.id) === String(initialCountry))?.value || "País de la obra";
    const allSubs = catalogs.subdivision.filter((x) => String(x.codPais) === String(initialCountry));
    const subs = allSubs.filter((x) => x.active);
    const regionLabel = regionTypeLabel(subs);
    // Región del alta: la del filtro si hay una elegida, hasta que se elija otra.
    const addSubdivision = newSubdivision || subdivision;
    const countryLocations = (catalogs.ciudad || []).filter((u) => String(u.codPais) === String(initialCountry));
    const activeCount = countryLocations.filter((u) => u.active).length;
    const filters = { codPais: initialCountry, codSubdivision: subdivision, showInactive, search, matches: (value, text) => normalizeSearchValue(value).includes(normalizeSearchValue(text)) };
    const locations = filterUbicaciones(countryLocations, filters);

    const reload = async () => { if (onReload) await onReload(); };
    const assignedKey = normalizeSearchValue(assignedValue);

    const add = async (event) => {
        event.preventDefault();
        setError(""); setInfo("");
        const name = location.trim();
        const problem = ubicacionError(countryLocations, { nombre: name, codSubdivision: addSubdivision }, normalizeSearchValue);
        if (problem) { setError(problem.replace("la región", `la ${regionLabel.toLowerCase()}`)); return; }
        setBusy(true);
        try {
            await guardarGeografia("ubicacion", { valor: name, codSubdivision: Number(addSubdivision), tipo: kind, motivo: "Alta desde ficha de obra" });
            // Guardada: se asigna ya. Si luego falla el refresco de la lista, no es un
            // error de guardado (antes se anunciaba como «No se pudo guardar»).
            onAssign?.(name);
            setLocation("");
            const refreshed = await reload().then(() => true, () => false);
            setInfo(refreshed
                ? `"${name}" añadida y seleccionada en la obra.`
                : `"${name}" añadida y seleccionada en la obra. No se pudo refrescar la lista: cierra y vuelve a abrir este panel.`);
        } catch (err) {
            // 409: puede ser un duplicado real (misma región) o un choque de constraint.
            // Solo damos por buena la selección si tras recargar la ubicación existe.
            if (err?.response?.status === 409) {
                let exists = false;
                try {
                    const fresh = await fetchGeografia();
                    exists = (fresh.ciudad || []).some((u) =>
                        normalizeSearchValue(u.value) === normalizeSearchValue(name)
                        && String(u.codSubdivision) === String(addSubdivision));
                } catch { /* si falla la comprobación, tratamos como error real */ }
                await reload();
                if (exists) {
                    onAssign?.(name);
                    setLocation("");
                    setInfo(`"${name}" ya existía en esa ${regionLabel.toLowerCase()}; la he seleccionado.`);
                } else {
                    setError("No se pudo crear la ubicación (conflicto en la base de datos). Avisa a soporte.");
                }
            } else {
                setError(apiErrorMessage(err, "No se pudo guardar la ubicación."));
            }
        } finally { setBusy(false); }
    };

    const startEdit = (u) => {
        setError(""); setInfo("");
        setEditing({ id: u.id, nombre: u.value, codSubdivision: String(u.codSubdivision || ""), tipo: u.tipo || "Ciudad", error: "" });
    };

    // Opciones del editor. Una ubicación puede estar en una región deshabilitada o tener un
    // tipo antiguo (dbo.Obras_Ubicaciones.Tipo es texto libre): si su valor no está en la
    // lista, el desplegable enseña otro y al guardar se cambiaría sin querer.
    const editSubs = editing ? allSubs.filter((x) => x.active || String(x.id) === String(editing.codSubdivision)) : subs;
    const editSubMissing = Boolean(editing) && !editSubs.some((x) => String(x.id) === String(editing.codSubdivision));
    const editTipos = editing?.tipo && !UBICACION_TIPOS.includes(editing.tipo)
        ? [editing.tipo, ...UBICACION_TIPOS]
        : UBICACION_TIPOS;

    // Renombrar, deshabilitar/reactivar y eliminar cambian el catálogo de todas las obras:
    // como en el resto de catálogos, piden una justificación que queda en el historial.
    // `confirming`: { title, detail, run(motivo) }.
    const [confirming, setConfirming] = useState(null);
    const [reason, setReason] = useState("");
    const askReason = (action) => { setError(""); setInfo(""); setReason(""); setConfirming(action); };
    const confirmAction = async (event) => {
        event.preventDefault();
        const motivo = reason.trim();
        if (!motivo || !confirming) return;
        const action = confirming;
        setConfirming(null);
        await action.run(motivo);
    };

    // Editar renombra la ubicación y puede moverla de región (útil para las que están
    // "Sin clasificar"). Las obras que la tienen asignada pasan a mostrar el nombre nuevo.
    const saveEdit = (u) => {
        const nombre = editing.nombre.trim();
        const problem = ubicacionError(countryLocations, { id: u.id, nombre, codSubdivision: editing.codSubdivision }, normalizeSearchValue);
        if (problem) { setEditing({ ...editing, error: problem.replace("la región", `la ${regionLabel.toLowerCase()}`) }); return; }
        const draft = { ...editing, nombre };
        askReason({
            title: nombre === u.value ? `Guardar cambios de «${u.value}»` : `Renombrar «${u.value}» a «${nombre}»`,
            detail: usageCount && usageCount(u) ? `${usageCount(u)} ${usageCount(u) === 1 ? "obra la usa" : "obras la usan"} y ${usageCount(u) === 1 ? "verá" : "verán"} el cambio.` : "Sin obras asignadas.",
            run: (motivo) => commitEdit(u, draft, motivo),
        });
    };
    const commitEdit = async (u, draft, motivo) => {
        const { nombre } = draft;
        setBusy(true); setError(""); setInfo("");
        try {
            await guardarGeografia("ubicacion", { valor: nombre, codSubdivision: Number(draft.codSubdivision), tipo: draft.tipo, motivo }, u.id);
        } catch (err) {
            setEditing({ ...draft, error: apiErrorMessage(err, "No se pudo guardar la ubicación.") });
            setBusy(false);
            return;
        }
        // Guardada. Las obras de ESTE país (el mismo nombre existe en varios) muestran ya
        // el nombre nuevo, aunque luego falle el refresco de la lista: antes un fallo al
        // refrescar se anunciaba como «No se pudo guardar» y la obra seguía con el viejo.
        onRenamed?.(u.value, nombre, countryName);
        setEditing(null);
        const refreshed = await reload().then(() => true, () => false);
        setInfo(refreshed
            ? `Ubicación guardada como "${nombre}".`
            : `Ubicación guardada como "${nombre}". No se pudo refrescar la lista: cierra y vuelve a abrir este panel.`);
        setBusy(false);
    };

    const toggle = (u) => askReason({
        title: `${u.active ? "Deshabilitar" : "Reactivar"} «${u.value}»`,
        detail: u.active ? "Deja de ofrecerse en las fichas; las obras que ya la tienen la conservan." : "Vuelve a ofrecerse en las fichas.",
        run: async (motivo) => {
            setBusy(true);
            try { await estadoGeografia("ubicacion", u.id, !u.active, motivo); await reload(); }
            catch (err) { setError(apiErrorMessage(err, "No se pudo cambiar el estado.")); }
            finally { setBusy(false); }
        },
    });

    const remove = (u) => askReason({
        title: `Eliminar «${u.value}»`,
        detail: "Se borra del catálogo definitivamente.",
        run: async (motivo) => {
            setBusy(true);
            try { await eliminarGeografia("ubicacion", u.id, motivo); await reload(); }
            catch (err) { setError(apiErrorMessage(err, "No se pudo eliminar (¿tiene obras asignadas?).")); }
            finally { setBusy(false); }
        },
    });

    const assign = (u) => { onAssign?.(u.value); onClose(); };

    return (
        <div className="obras-catalog-modal-layer" onMouseDown={onClose}>
            <section className="obras-catalog-manager obras-geo-manager" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Ubicaciones de ${countryName}`}>
                <header>
                    <div className="obras-catalog-manager__heading">
                        <i><FiMapPin /></i>
                        <span><small>Catálogo geográfico</small><strong>Ubicaciones de {countryName}</strong></span>
                    </div>
                    <button type="button" aria-label="Cerrar" onClick={onClose}><FiX /></button>
                </header>

                <div className="obras-catalog-manager__toolbar obras-geo-manager__toolbar">
                    <label>
                        <FiSearch />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Buscar ubicación o ${regionLabel.toLowerCase()}`} aria-label="Buscar ubicación" />
                    </label>
                    <select value={subdivision} onChange={(e) => setSubdivision(e.target.value)} aria-label={regionLabel}>
                        <option value="">Todas · {regionLabel.toLowerCase()}</option>
                        {subs.map((x) => <option key={x.id} value={x.id}>{x.value}</option>)}
                    </select>
                    <div className="obras-catalog-manager__tabs">
                        <button type="button" className={showInactive ? "" : "active"} onClick={() => setShowInactive(false)}>Activas <span>{activeCount}</span></button>
                        <button type="button" className={showInactive ? "active" : ""} onClick={() => setShowInactive(true)}>Deshabilitadas <span>{countryLocations.length - activeCount}</span></button>
                    </div>
                </div>

                <div className="obras-catalog-manager__list">
                    {locations.map((u) => {
                        const assigned = usageCount ? usageCount(u) : 0;
                        if (editing?.id === u.id) {
                            return (
                                <article key={u.id} className="is-editing">
                                    <div className="obras-geo-manager__edit">
                                        <input value={editing.nombre} maxLength={120} aria-label={`Nombre de ${u.value}`} aria-invalid={Boolean(editing.error)}
                                            onChange={(e) => setEditing({ ...editing, nombre: e.target.value, error: "" })}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(u); } if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setEditing(null); } }} />
                                        <select value={editing.codSubdivision} aria-label={regionLabel} onChange={(e) => setEditing({ ...editing, codSubdivision: e.target.value, error: "" })}>
                                            {editSubMissing ? <option value={editing.codSubdivision}>Sin asignar</option> : null}
                                            {editSubs.map((x) => <option key={x.id} value={x.id}>{x.value}{x.active ? "" : " (deshabilitada)"}</option>)}
                                        </select>
                                        <select value={editing.tipo} aria-label="Tipo de ubicación" onChange={(e) => setEditing({ ...editing, tipo: e.target.value })}>
                                            {editTipos.map((tipo) => <option key={tipo}>{tipo}</option>)}
                                        </select>
                                        <small className={editing.error ? "is-error" : ""}>
                                            {editing.error || (assigned
                                                ? `${assigned === 1 ? "1 obra usa esta ubicación y verá" : `${assigned} obras usan esta ubicación y verán`} el nombre nuevo.`
                                                : "Sin obras asignadas.")}
                                        </small>
                                    </div>
                                    <div className="obras-catalog-manager__actions">
                                        <button type="button" className="reactivate" disabled={busy} title="Guardar" aria-label={`Guardar cambios de ${u.value}`} onClick={() => saveEdit(u)}><FiCheck /></button>
                                        <button type="button" title="Cancelar" aria-label="Cancelar edición" onClick={() => setEditing(null)}><FiX /></button>
                                    </div>
                                </article>
                            );
                        }
                        return (
                            <article key={u.id} className={u.active ? "" : "is-inactive"}>
                                <div>
                                    <strong>{u.value}{assignedKey && normalizeSearchValue(u.value) === assignedKey ? <em className="obras-geo-manager__badge">En esta obra</em> : null}</strong>
                                    <small>{u.subdivision || "—"} · {u.tipo || "Ciudad"}{assigned ? ` · ${assigned} ${assigned === 1 ? "obra" : "obras"}` : ""}</small>
                                </div>
                                <div className="obras-catalog-manager__actions">
                                    {u.active ? (
                                        <button type="button" className="reactivate" title="Seleccionar para la obra" aria-label={`Seleccionar ${u.value}`} onClick={() => assign(u)}><FiCheck /></button>
                                    ) : null}
                                    <button type="button" title="Editar nombre y región" aria-label={`Editar ${u.value}`} onClick={() => startEdit(u)}><FiEdit2 /></button>
                                    <button type="button" className={u.active ? "deactivate" : "reactivate"} title={u.active ? "Deshabilitar" : "Reactivar"} aria-label={`${u.active ? "Deshabilitar" : "Reactivar"} ${u.value}`} onClick={() => toggle(u)}>{u.active ? <FiEyeOff /> : <FiRefreshCw />}</button>
                                    {!assigned ? (
                                        <button type="button" className="delete" title="Eliminar definitivamente" aria-label={`Eliminar ${u.value}`} onClick={() => remove(u)}><FiTrash2 /></button>
                                    ) : null}
                                </div>
                            </article>
                        );
                    })}
                    {!locations.length ? (
                        <p>{search || subdivision
                            ? "Ninguna ubicación coincide con la búsqueda."
                            : showInactive ? "No hay ubicaciones deshabilitadas." : "Este país aún no tiene ubicaciones. Añade la primera abajo."}</p>
                    ) : null}
                </div>

                {confirming ? (
                    <form className="obras-geo-manager__confirm" onSubmit={confirmAction} aria-label={confirming.title}>
                        <strong>{confirming.title}</strong>
                        {confirming.detail ? <small>{confirming.detail}</small> : null}
                        <label>
                            <span>Justificación obligatoria</span>
                            <textarea
                                autoFocus
                                rows={2}
                                maxLength={500}
                                value={reason}
                                placeholder="Por qué se cambia el catálogo (queda en el historial)"
                                onChange={(e) => setReason(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setConfirming(null); } }}
                            />
                        </label>
                        <div>
                            <button type="button" onClick={() => setConfirming(null)}>Cancelar</button>
                            <button type="submit" className="is-primary" disabled={busy || !reason.trim()}><FiCheck /> Confirmar</button>
                        </div>
                    </form>
                ) : (
                <form className="obras-geo-manager__add" onSubmit={add}>
                    <label>
                        <span>{regionLabel}</span>
                        <select value={addSubdivision} onChange={(e) => setNewSubdivision(e.target.value)} aria-label={`${regionLabel} de la nueva ubicación`}>
                            <option value="">Elegir…</option>
                            {subs.map((x) => <option key={x.id} value={x.id}>{x.value}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Nombre</span>
                        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ciudad, centro comercial…" aria-label="Nombre de la nueva ubicación" maxLength={120} />
                    </label>
                    <label>
                        <span>Tipo</span>
                        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Tipo de la nueva ubicación">
                            {UBICACION_TIPOS.map((tipo) => <option key={tipo}>{tipo}</option>)}
                        </select>
                    </label>
                    <button type="submit" disabled={busy || !location.trim() || !addSubdivision}><FiPlus /> Añadir y asignar</button>
                    {error ? <p className="obras-inline-error">{error}</p> : null}
                    {info ? <p className="obras-geo-manager__info">{info}</p> : null}
                </form>
                )}

                <footer>
                    <span><FiAlertTriangle /> Al añadir se selecciona en la obra. Solo se eliminan ubicaciones sin obras asignadas.</span>
                    <button type="button" onClick={onClose}>Cerrar</button>
                </footer>
            </section>
        </div>
    );
}


function CatalogManagerModal({
    ariaLabel,
    canRename,
    entries,
    note,
    onClose,
    onDelete,
    onRename,
    onStatusChange,
    usageCount,
}) {
    const [showInactive, setShowInactive] = useState(false);
    const [search, setSearch] = useState("");
    // Renombrado en línea: una fila a la vez; Enter confirma y Escape cancela.
    const [editing, setEditing] = useState(null);
    const startRename = (entry) => setEditing({ id: entry.id, value: entry.value, error: "" });
    const confirmRename = (entry) => {
        const error = catalogRenameError(entries, entry, editing?.value);
        if (error) { setEditing((current) => ({ ...current, error })); return; }
        onRename(entry, editing.value.replace(/\s+/g, " ").trim());
        setEditing(null);
    };
    const activeEntries = entries.filter((entry) => entry.active && !entry.synthetic);
    const inactiveEntries = entries.filter((entry) => !entry.active && !entry.synthetic);
    const visibleEntries = (showInactive ? inactiveEntries : activeEntries).filter((entry) =>
        normalizeSearchValue(entry.value).includes(normalizeSearchValue(search))
    );

    return (
        <div
            className="obras-catalog-modal-layer"
            role="presentation"
            onMouseDown={onClose}
        >
            <section
                className="obras-catalog-manager"
                role="dialog"
                aria-modal="true"
                aria-labelledby="obras-catalog-manager-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header>
                    <div className="obras-catalog-manager__heading">
                        <i><FiSettings /></i>
                        <span>
                            <small>Gestion de catalogo</small>
                            <strong id="obras-catalog-manager-title">{ariaLabel}</strong>
                        </span>
                    </div>
                    <button
                        type="button"
                        aria-label="Cerrar gestion de catalogo"
                        title="Cerrar"
                        onClick={onClose}
                    >
                        <FiX />
                    </button>
                </header>

                <div className="obras-catalog-manager__toolbar">
                    <label>
                        <FiSearch />
                        <input
                            aria-label={`Buscar en catalogo ${ariaLabel}`}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Buscar valor"
                        />
                    </label>
                    <div className="obras-catalog-manager__tabs">
                        <button
                            type="button"
                            className={showInactive ? "" : "active"}
                            onClick={() => setShowInactive(false)}
                        >
                            Activos <span>{activeEntries.length}</span>
                        </button>
                        <button
                            type="button"
                            className={showInactive ? "active" : ""}
                            onClick={() => setShowInactive(true)}
                        >
                            Deshabilitados <span>{inactiveEntries.length}</span>
                        </button>
                    </div>
                </div>

                <div className="obras-catalog-manager__list">
                    {visibleEntries.map((entry) => {
                        const assigned = usageCount(entry);
                        if (editing?.id === entry.id) {
                            return (
                                <article key={entry.id} className="is-editing">
                                    <div className="obras-catalog-manager__rename">
                                        <input
                                            autoFocus
                                            aria-label={`Nuevo nombre para ${entry.value}`}
                                            value={editing.value}
                                            maxLength={120}
                                            aria-invalid={Boolean(editing.error)}
                                            onChange={(event) => setEditing({ ...editing, value: event.target.value, error: "" })}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") { event.preventDefault(); confirmRename(entry); }
                                                if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setEditing(null); }
                                            }}
                                        />
                                        <small className={editing.error ? "is-error" : ""}>
                                            {editing.error || (assigned
                                                ? `Cambia el nombre en ${assigned} ${assigned === 1 ? "obra" : "obras"}. Se pedirá justificación.`
                                                : "Se pedirá justificación.")}
                                        </small>
                                    </div>
                                    <div className="obras-catalog-manager__actions">
                                        <button type="button" className="reactivate" aria-label={`Confirmar nuevo nombre de ${entry.value}`} title="Renombrar" onClick={() => confirmRename(entry)}>
                                            <FiCheck />
                                        </button>
                                        <button type="button" aria-label="Cancelar renombrado" title="Cancelar" onClick={() => setEditing(null)}>
                                            <FiX />
                                        </button>
                                    </div>
                                </article>
                            );
                        }
                        return (
                            <article key={entry.id} className={entry.active ? "" : "is-inactive"}>
                                <div>
                                    <strong>{entry.value}</strong>
                                    <small>
                                        {assigned
                                            ? `${assigned} ${assigned === 1 ? "obra asignada" : "obras asignadas"}`
                                            : onDelete ? "Disponible para eliminar" : "Sin obras asignadas"}
                                    </small>
                                </div>
                                <div className="obras-catalog-manager__actions">
                                    {onRename && (!canRename || canRename(entry)) ? (
                                        <button
                                            type="button"
                                            aria-label={`Renombrar ${entry.value}`}
                                            title="Renombrar"
                                            onClick={() => startRename(entry)}
                                        >
                                            <FiEdit2 />
                                        </button>
                                    ) : null}
                                    {onStatusChange ? (
                                        <button
                                            type="button"
                                            className={entry.active ? "deactivate" : "reactivate"}
                                            aria-label={`${entry.active ? "Deshabilitar" : "Reactivar"} ${entry.value}`}
                                            title={entry.active ? "Deshabilitar" : "Reactivar"}
                                            onClick={() => onStatusChange(entry, !entry.active)}
                                        >
                                            {entry.active ? <FiEyeOff /> : <FiRefreshCw />}
                                        </button>
                                    ) : null}
                                    {onDelete && !assigned ? (
                                        <button
                                            type="button"
                                            className="delete"
                                            aria-label={`Eliminar ${entry.value}`}
                                            title="Eliminar definitivamente"
                                            onClick={() => onDelete(entry)}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    ) : null}
                                </div>
                            </article>
                        );
                    })}
                    {!visibleEntries.length ? (
                        <p>
                            {search
                                ? "No hay valores que coincidan con la busqueda."
                                : showInactive
                                    ? "No hay valores deshabilitados."
                                    : "No hay valores activos."}
                        </p>
                    ) : null}
                </div>

                <footer>
                    <span>
                        <FiAlertTriangle />
                        {note || (onRename
                            ? "Renombrar cambia el nombre en todas las obras que lo usan. Solo se pueden eliminar valores sin obras asignadas."
                            : "Este catálogo no se renombra desde aquí. Solo se pueden eliminar valores sin obras asignadas.")}
                    </span>
                    <button type="button" onClick={onClose}>Cerrar</button>
                </footer>
            </section>
        </div>
    );
}

function InlineCell({
    ariaLabel,
    catalog,
    label,
    field,
    obra,
    onCatalogStatusChange,
    onChange,
    onCreateOption,
    onOpenCatalogManager,
    onRequestSave,
    options,
    draft,
    type = "text",
}) {
    const value = draft ? draft.newValue : obra[field] ?? "";
    const dirty = Boolean(draft);
    const handleKeyDown = (event) => {
        if (dirty && ["Enter", "Tab"].includes(event.key)) {
            event.preventDefault();
            onRequestSave(obra.codObra, field, event.key.toUpperCase());
        }
    };

    return (
        <div className={`obras-edit-control${dirty ? " is-dirty" : ""}`}>
            {options ? (
                <CatalogSelect
                    ariaLabel={ariaLabel}
                    catalog={catalog}
                    disabled={catalog === "ciudad" && !obra.pais}
                    allowCreate={catalogAllowsInlineCreate(catalog)}
                    allowClear={catalogAllowsClear(catalog)}
                    className="obras-cell-input obras-cell-input--select"
                    options={options}
                    value={value}
                    onChange={(nextValue) => onChange(obra.codObra, field, nextValue, label)}
                    onCreate={(nextValue) => onCreateOption(obra, field, catalog, nextValue, label)}
                    onRequestStatusChange={(entry, nextActive, selectAfter) =>
                        onCatalogStatusChange({
                            obra,
                            field,
                            catalog,
                            entry,
                            nextActive,
                            selectAfter,
                            label,
                        })
                    }
                    onOpenManager={() => onOpenCatalogManager({
                        obra,
                        field,
                        catalog,
                        label,
                        ariaLabel,
                    })}
                    onKeyDown={handleKeyDown}
                />
            ) : (
                type === "number" ? (
                    <LocalizedNumberInput
                        aria-label={ariaLabel}
                        className="obras-cell-input"
                        value={value}
                        onChange={(event) => onChange(obra.codObra, field, event.target.value, label)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={handleKeyDown}
                    />
                ) : (
                    <input
                        aria-label={ariaLabel}
                        className="obras-cell-input"
                        type={type}
                        value={value}
                        onChange={(event) => onChange(obra.codObra, field, event.target.value, label)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={handleKeyDown}
                    />
                )
            )}
            {dirty ? (
                <button
                    type="button"
                    className="obras-save-change"
                    aria-label={`Guardar cambio en ${label}`}
                    title="Guardar cambio"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                        event.stopPropagation();
                        onRequestSave(obra.codObra, field, "ICONO");
                    }}
                >
                    <FiSave />
                </button>
            ) : null}
        </div>
    );
}

function DetailField({
    config,
    highlighted,
    obra,
    onCatalogStatusChange,
    onChange,
    onCreateOption,
    onOpenCatalogManager,
    onRequestSave,
    onDiscardField,
    onBlockedCatalogSelect,
    draft,
    options,
    responsables,
    hito,
    onReviewHito,
    readOnly = false,
}) {
    const value = draft ? draft.newValue : obra[config.usuario || config.field] ?? "";
    const dirty = Boolean(draft);
    const unit = fieldUnits[config.field];
    const hint = config.hint || FIELD_HINTS[config.field];
    const calculated = config.calculated || calculatedCostFields.has(config.field);
    const commonProps = {
        "aria-label": config.label,
        value,
        readOnly: calculated,
        onChange: (event) => {
            if (calculated) return;
            onChange(obra.codObra, config.field, event.target.value, config.label);
        },
        onKeyDown: (event) => {
            if (calculated) return;
            const enterCommits = event.key === "Enter" && (!config.multiline || event.ctrlKey);
            if (dirty && (event.key === "Tab" || enterCommits)) {
                event.preventDefault();
                onRequestSave(obra.codObra, config.field, event.key.toUpperCase());
            }
        },
    };

    return (
        <label
            id={`obra-field-${config.field}`}
            className={`obras-detail-field${config.multiline ? " obras-detail-field--wide" : ""}${dirty ? " is-dirty" : ""}${calculated ? " is-calculated" : ""}${highlighted ? " is-highlighted" : ""}`}
        >
            <span className="obras-detail-field__label">
                {hint
                    ? <span className="obras-detail-field__name has-hint" data-hint={hint} tabIndex={0} aria-label={`${config.label}. ${hint}`}>{config.label}</span>
                    : <span className="obras-detail-field__name">{config.label}</span>}
                <span className="obras-detail-field__aside">
                    {calculated ? <small>Calculado</small> : config.catalog ? <small>Catalogo</small> : config.usuario ? <small>Usuario</small> : null}
                </span>
            </span>
            <div className={`obras-detail-field__control${unit ? " has-unit" : ""}`}>
                {readOnly ? (
                    // Perfil de consulta (Imagen, Estudio): el valor, sin controles de edición.
                    <div className={`obras-detail-field__readonly${config.multiline ? " is-multiline" : ""}`} aria-label={config.label}>
                        {readOnlyText(config, obra, value)}
                    </div>
                ) : config.usuario ? (
                    // Mismo desplegable que los catálogos (buscar, quitar), sin alta ni gestión:
                    // las opciones son los usuarios con ese rol.
                    (() => {
                        const { options: usuarios, label } = responsableOptions(responsables, value, obra[config.field]);
                        return (
                            <CatalogSelect
                                ariaLabel={config.label}
                                allowCreate={false}
                                options={usuarios}
                                value={label}
                                onChange={(_nombre, option) => onChange(obra.codObra, config.usuario, responsableCod(option), config.label)}
                                onKeyDown={commonProps.onKeyDown}
                            />
                        );
                    })()
                ) : config.options ? (
                    <select
                        className="obras-detail-select"
                        aria-label={config.label}
                        value={value ?? ""}
                        onChange={(event) => onChange(obra.codObra, config.field, event.target.value, config.label)}
                        onKeyDown={commonProps.onKeyDown}
                    >
                        <option value="">{EMPTY_OPTION_LABEL}</option>
                        {config.options.map((option) => <option key={option} value={option}>{option}</option>)}
                        {value && !config.options.includes(value) ? <option value={value}>{value}</option> : null}
                    </select>
                ) : options ? (
                    <CatalogSelect
                        ariaLabel={config.label}
                        catalog={config.catalog}
                        disabled={config.catalog === "ciudad" && !obra.pais}
                        allowCreate={catalogAllowsInlineCreate(config.catalog)}
                        allowClear={catalogAllowsClear(config.catalog)}
                        onDisabledAttempt={() => onBlockedCatalogSelect?.(config.catalog)}
                        options={options}
                        value={value}
                        onChange={(nextValue) => onChange(obra.codObra, config.field, nextValue, config.label)}
                        onCreate={(nextValue) => onCreateOption(
                            obra,
                            config.field,
                            config.catalog,
                            nextValue,
                            config.label
                        )}
                        onRequestStatusChange={(entry, nextActive, selectAfter) =>
                            onCatalogStatusChange({
                                obra,
                                field: config.field,
                                catalog: config.catalog,
                                entry,
                                nextActive,
                                selectAfter,
                                label: config.label,
                            })
                        }
                        onOpenManager={() => onOpenCatalogManager({
                            obra,
                            field: config.field,
                            catalog: config.catalog,
                            label: config.label,
                            ariaLabel: config.label,
                        })}
                        onKeyDown={commonProps.onKeyDown}
                    />
                ) : config.multiline ? (
                    // maxLength = tamaño de la columna en BD: sin él, lo que sobra se
                    // perdía al guardar sin avisar.
                    <textarea {...commonProps} rows={4} maxLength={config.maxLength} />
                ) : config.type === "number" ? (
                    <LocalizedNumberInput
                        aria-label={config.label}
                        value={value}
                        onChange={commonProps.onChange}
                        onKeyDown={commonProps.onKeyDown}
                    />
                ) : (
                    <input type={config.type || "text"} {...commonProps} maxLength={config.maxLength} />
                )}
                {unit ? <span className="obras-field-unit" aria-hidden="true">{unitSymbol(unit)}</span> : null}
            </div>
            {!readOnly && config.maxLength && String(value ?? "").length >= config.maxLength * 0.8 ? (
                <small className={`obras-detail-field__count${String(value ?? "").length >= config.maxLength ? " is-full" : ""}`} aria-live="polite">
                    {String(value ?? "").length} / {config.maxLength}
                </small>
            ) : null}
            {hito && readOnly ? (
                <span className={`obras-hito-status is-static tone-${hito.tone}${hito.alert ? " is-alert" : ""}`} title={[hito.status === "confirmado" && hito.realDate ? `Confirmado el ${formatCompactDate(hito.realDate)}` : hito.message, hito.notes].filter(Boolean).join(" · ") || hito.statusLabel}>
                    <span>
                        {hito.statusLabel}
                        {hito.status === "confirmado" && hito.realDate ? ` ${hito.realDate.slice(8, 10)}/${hito.realDate.slice(5, 7)}` : ""}
                    </span>
                </span>
            ) : hito ? (
                // Hito crítico: su situación (confirmado, no completado, necesita datos...)
                // es a la vez el botón para confirmarlo o revisarlo (un solo elemento: en
                // una tarjeta estrecha no caben la situación y un botón aparte).
                <button
                    type="button"
                    className={`obras-hito-status tone-${hito.tone}${hito.alert ? " is-alert" : ""}`}
                    title={`${[hito.status === "confirmado" && hito.realDate ? `Confirmado el ${formatCompactDate(hito.realDate)}` : hito.message, hito.notes].filter(Boolean).join(" · ") || hito.statusLabel}. ${hito.estado === "PENDIENTE" ? "Clic para confirmar el hito." : "Clic para revisar la confirmación."}`}
                    aria-label={`${config.label}: ${hito.statusLabel}. ${hito.estado === "PENDIENTE" ? "Confirmar hito" : "Revisar hito"}`}
                    onClick={(event) => { event.preventDefault(); onReviewHito?.(config.field); }}
                >
                    <span>
                        {hito.statusLabel}
                        {hito.status === "confirmado" && hito.realDate ? ` ${hito.realDate.slice(8, 10)}/${hito.realDate.slice(5, 7)}` : ""}
                    </span>
                    {hito.estado === "PENDIENTE" ? <FiCheck aria-hidden="true" /> : <FiEdit2 aria-hidden="true" />}
                </button>
            ) : null}
        </label>
    );
}

// Valor de un campo tal como se ve en modo consulta ("—" si no hay dato).
function readOnlyText(config, obra, value) {
    if (config.usuario) return obra[config.field] || "—";
    if (value === "" || value === null || value === undefined) return "—";
    if (config.type === "number") return formatNumber(value) || "—";
    if (config.type === "date") return formatCompactDate(value) || "—";
    return String(value);
}

// Historial de la obra. Cada valor (el de antes y el de después) es un botón cuando se
// puede volver a poner en la ficha: un clic lo deja como cambio pendiente, con su
// justificación, igual que si se hubiera escrito a mano. No deshace nada.
function AuditTimeline({ entries, canRestore, onRestore, userNames: names }) {
    if (!entries.length) {
        return (
            <div className="obras-history-empty">
                <FiClock />
                <strong>Sin cambios registrados</strong>
                <span>Las ediciones justificadas apareceran aqui.</span>
            </div>
        );
    }

    return (
        <div className="obras-history-list">
            {entries.map((entry) => (
                <article className="obras-history-entry" key={entry.id}>
                    <div className="obras-history-entry__icon"><FiEdit3 /></div>
                    <div>
                        <header>
                            <strong>{entry.label}</strong>
                            <span>{entry.date}</span>
                        </header>
                        <div className="obras-history-values">
                            <HistoryValue entry={entry} value={entry.previousValue} text={entry.previousText} names={names} tag="del" canRestore={canRestore} onRestore={onRestore} />
                            <FiArrowRight />
                            <HistoryValue entry={entry} value={entry.newValue} text={entry.newText} names={names} tag="ins" canRestore={canRestore} onRestore={onRestore} />
                        </div>
                        <p>{entry.reason}</p>
                        <footer>{entry.user} · {entry.source}</footer>
                    </div>
                </article>
            ))}
        </div>
    );
}

// Un valor del historial: texto a secas o botón para volver a ponerlo en la ficha.
function HistoryValue({ entry, value, text, names, tag: Tag, canRestore, onRestore }) {
    const texto = formatChangeValue(value, entry.field, names, text);
    const restorable = canRestore?.(entry, value);
    if (!restorable) return <Tag>{texto}</Tag>;
    return (
        <button
            type="button"
            className="obras-history-restore"
            title={`Volver a poner ${texto} en ${entry.label}`}
            aria-label={`Volver a poner ${texto} en ${entry.label}`}
            onClick={() => onRestore(entry, value)}
        >
            <Tag>{texto}</Tag>
            <FiRotateCcw />
        </button>
    );
}

const providerGroups = [
    { type: "Constructora", field: "constructora", deadlineField: "apertura", icon: FiBriefcase },
    { type: "Mobiliario", field: "mobiliario", deadlineField: "deadlineMob", icon: FiBox },
    { type: "Iluminacion", field: "provIlum1", deadlineField: "deadlineMob", icon: FiActivity },
    { type: "Suelos", field: "sueloGral", deadlineField: "deadlineMob", icon: FiLayers },
    { type: "Studio", field: "studio", deadlineField: "planoP3", icon: FiTarget },
    { type: "Consulting", field: "consulting", deadlineField: "legal", icon: FiUsers },
];



function buildPortfolioModel(obras) {
    // Año de referencia de las series mensuales: el actual (antes fijo en 2026).
    const modelYear = portfolioToday.getFullYear();
    const active = obras.filter((obra) => obra.activa !== false);
    const totalCapex = active.reduce((acc, obra) => acc + Number(obra.capex || 0), 0);
    const committed = active.reduce((acc, obra) => acc + Number(obra.totalInversion || 0), 0);
    // Gastado: todas las partidas informadas (obra civil + equipamiento BSK + otros).
    const spentOf = (obra) => Number(obra.totalOc || 0) + Number(obra.totalBsk || 0) + Number(obra.totalOtros || 0);
    const real = active.reduce((acc, obra) => acc + spentOf(obra), 0);
    const forecast = active.reduce((acc, obra) => acc + getForecastEac(obra), 0);
    const blockedCapex = active
        .filter((obra) => isObraAtRisk(obra) || blockingHitoAlerts(obra).length > 0)
        .reduce((acc, obra) => acc + Number(obra.capex || 0), 0);
    const area = active.reduce((acc, obra) => acc + Number(obra.supVta || obra.supCons || 0), 0);
    const euroM2 = area ? committed / area : 0;

    const monthly = monthLabels.map((label, month) => {
        const monthEnd = new Date(modelYear, month + 1, 0);
        const items = active.filter((obra) => {
            const opening = dateFromIso(obra.apertura);
            return opening && opening.getFullYear() === modelYear && opening <= monthEnd;
        });
        const openings = active.filter((obra) => {
            const opening = dateFromIso(obra.apertura);
            return opening && opening.getFullYear() === modelYear && opening.getMonth() === month;
        });
        const starts = active.filter((obra) => {
            const start = dateFromIso(obra.inicioObra);
            return start && start.getFullYear() === modelYear && start.getMonth() === month;
        });

        return {
            month: label,
            aprobado: items.reduce((acc, obra) => acc + Number(obra.capex || 0), 0) / 1_000_000,
            comprometido: items.reduce((acc, obra) => acc + Number(obra.totalInversion || 0), 0) / 1_000_000,
            real: items.reduce((acc, obra) => acc + spentOf(obra), 0) / 1_000_000,
            forecast: items.reduce((acc, obra) => acc + getForecastEac(obra), 0) / 1_000_000,
            aperturas: items.length,
            aperturasMes: openings.length,
            iniciosMes: starts.length,
        };
    });

    const countryMap = new Map();
    const cityMap = new Map();
    const communityMap = new Map();
    active.forEach((obra) => {
        const country = obra.pais || "Sin pais";
        const city = obra.ciudad || "Sin ciudad";
        const community = getCommunity(obra);
        const addTerritory = (map, key) => {
            if (!map.has(key)) {
                map.set(key, { name: key, projects: 0, capex: 0, risk: 0, topProject: null, tone: "success" });
            }
            const item = map.get(key);
            item.projects += 1;
            item.capex += Number(obra.capex || 0);
            // Criterio unico de riesgo (isObraAtRisk), el mismo que Resumen y Mapa.
            if (isObraAtRisk(obra)) item.risk += 1;
            if (!item.topProject || Number(obra.capex || 0) > Number(item.topProject.capex || 0)) item.topProject = obra;
            item.tone = item.risk > Math.max(1, item.projects * 0.3) ? "danger" : item.risk ? "warning" : "success";
        };

        addTerritory(countryMap, country);
        addTerritory(cityMap, `${country} / ${city}`);
        addTerritory(communityMap, community);
    });

    const countryRows = Array.from(countryMap.values()).sort((a, b) => b.capex - a.capex);
    const cityRows = Array.from(cityMap.values()).sort((a, b) => b.capex - a.capex);
    const communityRows = Array.from(communityMap.values()).sort((a, b) => b.capex - a.capex);

    const topCapexProjects = [...active]
        .sort((a, b) => Number(b.capex || 0) - Number(a.capex || 0))
        .slice(0, 12);

    const providerMap = new Map();
    providerGroups.forEach((group) => {
        active.forEach((obra) => {
            const provider = obra[group.field] || "Por asignar";
            const key = `${group.type}:${provider}`;
            if (!providerMap.has(key)) {
                providerMap.set(key, {
                    type: group.type,
                    provider,
                    icon: group.icon,
                    projects: 0,
                    risk: 0,
                    capex: 0,
                    nextDelivery: null,
                    managers: new Set(),
                    projectsList: [],
                    assignments: [],
                    history: Array.from({ length: 6 }, () => 0),
                    example: obra,
                });
            }

            const item = providerMap.get(key);
            const deadline = obra[group.deadlineField] || obra.apertura;
            const deadlineDate = dateFromIso(deadline);
            item.projects += 1;
            item.capex += Number(obra.capex || 0);
            item.managers.add(obra.pm || "Sin PM");
            item.projectsList.push(obra);
            item.assignments.push({ obra, deadline });
            if (getRiskTone(obra) === "danger") item.risk += 1;
            if (deadlineDate && deadlineDate >= portfolioToday && (!item.nextDelivery || deadlineDate < dateFromIso(item.nextDelivery))) {
                item.nextDelivery = deadline;
            }
            const opening = dateFromIso(obra.apertura);
            if (opening && opening.getFullYear() === modelYear) {
                const bucket = Math.min(5, Math.floor(opening.getMonth() / 2));
                item.history[bucket] += 1;
            }
        });
    });

    const providerRows = Array.from(providerMap.values())
        .map((item) => ({
            ...item,
            managersCount: item.managers.size,
            managers: Array.from(item.managers),
            tone: item.risk >= 3 ? "danger" : item.risk ? "warning" : "success",
        }))
        .sort((a, b) => b.projects - a.projects);
    const topProvider = providerRows[0] || null;
    const providerConcentration = topProvider ? (topProvider.projects / Math.max(1, active.length)) * 100 : 0;

    const capexDrivers = [
        { name: "Obra", value: active.reduce((acc, obra) => acc + Math.max(0, Number(obra.totalOc || 0) - Number(obra.licencias || 0)), 0) },
        { name: "Mobiliario", value: active.reduce((acc, obra) => acc + Number(obra.totalBsk || 0), 0) },
        { name: "Licencias", value: active.reduce((acc, obra) => acc + Number(obra.licencias || 0), 0) },
        { name: "Otros", value: active.reduce((acc, obra) => acc + Number(obra.totalOtros || 0), 0) },
    ].map((item) => ({ ...item, valueMEur: item.value / 1_000_000 }));

    const ganttRows = [...active]
        .filter((obra) => obra.apertura || obra.inicioObra)
        .sort((a, b) => {
            const aDate = dateFromIso(a.apertura || a.inicioObra)?.getTime() || 0;
            const bDate = dateFromIso(b.apertura || b.inicioObra)?.getTime() || 0;
            return aDate - bDate;
        });

    return {
        obras: active,
        totals: {
            totalCapex,
            committed,
            real,
            forecast,
            pending: Math.max(0, forecast - real),
            projects: active.length,
            blockedCapex,
            euroM2,
            providerConcentration,
            topProvider,
        },
        monthly,
        countryRows,
        cityRows,
        communityRows,
        topCapexProjects,
        capexDrivers,
        providerRows,
        ganttRows,
    };
}

function PortfolioLauncher({ activeView, model, onOpenView }) {
    const launcherData = {
        gantt: { metric: `${model.ganttRows.length} obras`, detail: "Planificacion por trimestre" },
        agenda: { metric: `${model.monthly.reduce((total, item) => total + item.aperturasMes, 0)} aperturas`, detail: "Calendario y agenda" },
        capex: { metric: moneyCompact(model.totals.totalCapex), detail: "Total, zona y obra" },
        mapa: { metric: `${model.countryRows.length} paises`, detail: "Pais y subdivision" },
        proveedores: { metric: `${model.providerRows.length} proveedores`, detail: "Tipo, riesgo y asignacion" },
    };

    return (
        <section className="obras-dashboard-launcher" aria-label="Análisis detallado de obras 2026">
            <header className="obras-dashboard-launcher__head">
                <div className="obras-eyebrow"><FiBarChart2 /><span>Análisis detallado</span></div>
                <small>{model.totals.projects} obras · {moneyCompact(model.totals.totalCapex)} CAPEX</small>
            </header>
            <div className="obras-dashboard-launcher__actions">
                {portfolioViews.map((view) => {
                    const Icon = view.icon;
                    const data = launcherData[view.id];
                    return (
                        <button
                            key={view.id}
                            type="button"
                            className={activeView === view.id ? "active" : ""}
                            onClick={() => onOpenView(view.id)}
                        >
                            <Icon />
                            <span>{view.label}</span>
                            <strong>{data.metric}</strong>
                            <small>{data.detail}</small>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}

// ── Edición Múltiple (edición masiva sobre la selección de la tabla) ───────────
// Campos reales del modelo que además se ven en la tabla; cada uno se aplica solo
// si el usuario lo marca, dejando el resto intacto ("Sin cambios").
const ALL_FIELD_LABELS = Object.fromEntries(
    // PM/PM2/PM3/PMI se guardan por su código de usuario (codPm...): misma etiqueta.
    excelFieldGroups.flatMap((group) => group.fields.map((field) => [field.usuario || field.field, field.label]))
);

function BulkEditModal({ obras, groups, cityCatalog, onApply, onClose }) {
    const [values, setValues] = useState({});
    const [touched, setTouched] = useState({});
    const allFields = groups.flatMap((group) => group.fields);

    // Valor común: si toda la selección comparte el mismo valor, se muestra como partida.
    const commonValue = (fieldId) => {
        if (!obras.length) return null;
        const first = obras[0][fieldId] ?? "";
        return obras.every((obra) => String(obra[fieldId] ?? "") === String(first)) ? first : null;
    };
    // Ubicaciones posibles: las del país marcado aquí o, si no se cambia, las del país
    // común de la selección.
    const cityChoice = bulkCityChoice(cityCatalog, obras, touched.pais ? (values.pais ?? "") : undefined);
    const optionsOf = (field) => (field.catalog === "ciudad" ? cityChoice.options : field.options || []);

    const setField = (fieldId, value) => {
        setValues((prev) => ({
            ...prev,
            [fieldId]: value,
            // Otro país invalida la ubicación elegida.
            ...(fieldId === "pais" && prev.pais !== value ? { ciudad: "" } : {}),
        }));
        setTouched((prev) => ({ ...prev, [fieldId]: true }));
    };
    // Al marcar un campo se parte del valor común (si lo hay): marcarlo sin tocar nada
    // ya no vacía el campo en todas las obras.
    const toggleField = (fieldId) => {
        if (!touched[fieldId] && values[fieldId] === undefined) {
            const common = commonValue(fieldId);
            if (common != null) setValues((prev) => ({ ...prev, [fieldId]: String(common) }));
        }
        setTouched((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }));
    };

    const activeCount = allFields.filter((field) => touched[field.id]).length;
    const cityBlocked = Boolean(touched.ciudad && cityChoice.reason);

    const handleApply = () => {
        if (cityBlocked) return;
        onApply(bulkChanges(allFields, values, touched));
    };

    const renderEditor = (field, current, isTouched) => {
        const onValue = (value) => setField(field.id, value);
        if (field.editor === "select") {
            const options = optionsOf(field);
            const blocked = field.catalog === "ciudad" && Boolean(cityChoice.reason);
            return (
                <>
                    <select value={current} aria-label={field.label} disabled={!isTouched || blocked} onChange={(event) => onValue(event.target.value)}>
                        <option value="">{isTouched ? EMPTY_OPTION_LABEL : "— Sin cambios —"}</option>
                        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    {isTouched && blocked ? <small className="obras-bulk-field__note">{cityChoice.reason}</small> : null}
                </>
            );
        }
        if (field.editor === "number") {
            return (
                <LocalizedNumberInput
                    aria-label={field.label}
                    value={current}
                    disabled={!isTouched}
                    placeholder={isTouched ? "" : "Sin cambios"}
                    onChange={(event) => onValue(event.target.value)}
                />
            );
        }
        if (field.editor === "textarea") {
            return (
                <textarea
                    aria-label={field.label}
                    rows={3}
                    value={current}
                    maxLength={field.maxLength || undefined}
                    disabled={!isTouched}
                    placeholder={isTouched ? "" : "Sin cambios"}
                    onChange={(event) => onValue(event.target.value)}
                />
            );
        }
        return (
            <input
                type={field.editor === "date" ? "date" : "text"}
                aria-label={field.label}
                value={current}
                maxLength={field.maxLength || undefined}
                disabled={!isTouched}
                placeholder={isTouched ? "" : "Sin cambios"}
                onChange={(event) => onValue(event.target.value)}
            />
        );
    };

    const renderField = (field) => {
        const common = commonValue(field.id);
        const isTouched = !!touched[field.id];
        const raw = isTouched ? values[field.id] : common;
        const current = raw == null ? "" : String(raw);
        return (
            <div key={field.id} className={`obras-bulk-field${isTouched ? " is-active" : ""}${field.editor === "textarea" ? " obras-bulk-field--wide" : ""}`}>
                <div className="obras-bulk-field__head">
                    <label>
                        <input type="checkbox" checked={isTouched} onChange={() => toggleField(field.id)} />
                        <span>{field.label}</span>
                    </label>
                    {common == null
                        ? <em className="obras-bulk-badge obras-bulk-badge--mixed">Mixto</em>
                        : common === ""
                            ? <em className="obras-bulk-badge obras-bulk-badge--mixed">Sin dato</em>
                            : <em className="obras-bulk-badge">Común</em>}
                </div>
                {renderEditor(field, current, isTouched)}
            </div>
        );
    };

    return (
        <div className="obras-dashboard-modal obras-bulk-modal-layer" role="dialog" aria-modal="true" aria-label="Edición múltiple">
            <div className="obras-bulk-modal obras-bulk-modal--full">
                <header className="obras-bulk-modal__head">
                    <div className="obras-bulk-modal__icon"><FiEdit3 /></div>
                    <div className="obras-bulk-modal__titles">
                        <strong>Edición Múltiple</strong>
                        <span>Actualizando <b>{obras.length}</b> {obras.length === 1 ? "obra seleccionada" : "obras seleccionadas"} · marca los campos a cambiar</span>
                    </div>
                    <button type="button" className="obras-bulk-modal__close" aria-label="Cerrar" onClick={onClose}><FiX /></button>
                </header>
                <div className="obras-bulk-modal__scroll">
                    {groups.map((group) => (
                        <section key={group.id} className="obras-bulk-group">
                            <h4>{React.createElement(group.icon)} {group.label}</h4>
                            <div className="obras-bulk-group__grid">
                                {group.fields.map(renderField)}
                            </div>
                        </section>
                    ))}
                </div>
                <footer className="obras-bulk-modal__foot">
                    <button type="button" className="obras-bulk-modal__cancel" onClick={onClose}>Cancelar</button>
                    <div className="obras-bulk-modal__impact">
                        <span>Impacto estimado</span>
                        <strong>{obras.length} {obras.length === 1 ? "ficha" : "fichas"} · {activeCount} {activeCount === 1 ? "campo" : "campos"}</strong>
                    </div>
                    <button type="button" className="obras-bulk-modal__apply" disabled={!activeCount || cityBlocked} onClick={handleApply}>
                        <FiCheck /> Aplicar a la selección
                    </button>
                </footer>
            </div>
        </div>
    );
}

// ── Custom table configuration (columns + multi-filters) ───────────────────────
// Fixed columns always render; optional ones are toggled from the gear menu.
// Alertas de hitos críticos de una obra (regla única de hitosModel.js) para la hoja:
// recuento, situación más grave y texto con el detalle de cada una.
const hitoAlertsOf = (obra) => obraHitoAlerts(obra, portfolioToday);
const hitoAlertCount = (obra) => filterFieldValue(obra, "bloq", portfolioToday);
const hitoSituacion = (obra) => filterFieldValue(obra, "hitoSituacion", portfolioToday);
// Hitos que bloquean (rojo): en riesgo o con la fecha pasada sin confirmar.
const blockingHitoAlerts = (obra) => hitoAlertsOf(obra).filter((hito) => hito.tone === "danger");
const hitoAlertsText = (obra) => hitoAlertsOf(obra).map((hito) => `${hito.label}: ${hito.statusLabel} (${hito.message})`).join("\n");

const TABLE_COLUMNS = [
    { id: "num", label: "#", fixed: true },
    // Fijas y congeladas al desplazarse en horizontal, en este orden (petición de BSK):
    // Código, País, Ubicación y Tienda. El resto se muestran, ocultan y mueven libremente.
    { id: "codigo", label: "Código", fixed: true },
    { id: "pais", label: "País", fixed: true },
    { id: "ciudad", label: "Ubicación", fixed: true },
    { id: "tienda", label: "Tienda", fixed: true },
    { id: "estado", label: "Estado" },
    { id: "apertura", label: "Apertura" },
    { id: "margen", label: "Desviación CAPEX" },
    { id: "eurom2", label: "EUR/m2" },
    { id: "bloq", label: "Alertas" },
    { id: "pm", label: "PM" },
    { id: "actions", label: "", fixed: true },
];
const OPTIONAL_COLUMNS = TABLE_COLUMNS.filter((column) => !column.fixed);
// Cabecera + celda de cada columna "especial" (no generica) de la tabla principal.
// Vive a nivel de modulo porque solo depende de la propia obra, no de estado del
// componente; permite recorrer columnas especiales y genericas con un unico bucle
// ordenable/arrastrable en vez de un bloque JSX fijo por columna.
// Renderizado por ventana de la hoja (ver sheetWindow.js): margen pintado por encima y
// por debajo de lo visible, tramo de scroll que obliga a recalcular la ventana y alto
// estimado de fila por densidad hasta que se miden las reales.
const SHEET_OVERSCAN_PX = 900;
const SHEET_SCROLL_STEP_PX = 225;
const SHEET_ROW_ESTIMATE_PX = { compacto: 34, normal: 48, grande: 64 };

const plainNumberFormatter = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 });
// Elementos de la hoja cuyo texto puede recortarse con puntos suspensivos; al pasar el
// ratón por uno recortado se muestra el texto completo.
const SHEET_TRUNCATABLE_SELECTOR = [
    ".obras-table tbody .obras-table-generic",
    ".obras-table tbody .obras-identity-link__text",
    ".obras-table tbody .obras-table-signal",
    ".obras-table tbody .obras-badge",
    ".obras-table thead .obras-table-sort > span",
    ".obras-table thead tr.obras-table-totals > td",
].join(", ");
// Celda de una columna genérica (campo de la ficha) en la hoja.
const CELL_TEXT_FORMATTERS = {
    formatDate: (value) => formatCompactDate(value),
    formatMoney: (value) => currency.format(value),
    formatNumber: (value) => plainNumberFormatter.format(value),
};
const genericColumnText = (obra, column) => genericCellText(obra[column.id], { type: column.type, unit: fieldUnits[column.id] }, CELL_TEXT_FORMATTERS);
function renderGenericCell(obra, column) {
    const value = obra[column.id];
    const text = genericColumnText(obra, column);
    if (value == null || value === "") return <span className="obras-table-generic obras-table-generic--empty">{text}</span>;
    const numeric = column.type === "number";
    return <span className={`obras-table-generic${numeric ? " obras-table-generic--num" : ""}`}>{text}</span>;
}

// Clases de una columna de la hoja en su cabecera, su total y sus celdas: alineación y
// tono de gris de su categoría de la ficha (sheetBlocks.js). Sin tono solo quedan las que
// no tienen un dato de la ficha detrás (casilla y número de fila).
const sheetColumnClass = (column) => [column.align === "right" ? "align-right" : "", sheetBlockClass(column.id)].filter(Boolean).join(" ") || undefined;

// Fila de la hoja. Memoizada: abrir un panel, marcar otra fila o cambiar cualquier
// estado ajeno no vuelve a construir las ~140 filas; solo se repintan las filas
// cuya obra, posición, columnas o estado (ficha abierta / marcada) cambian.
// onToggleChecked y onOpenDetail deben ser estables (useCallback).
const SheetRow = React.memo(function SheetRow({ obra, index, columns, isOpen, isChecked, onToggleChecked, onOpenDetail }) {
    const className = [isOpen ? "selected" : "", isChecked ? "is-checked" : "", obra.activa === false ? "is-archived" : ""].filter(Boolean).join(" ");
    return (
        <tr className={className} data-row-index={index} aria-rowindex={index + 2}>
            <td className="obras-col-check">
                <input type="checkbox" aria-label={`Seleccionar ${obra.tienda}`} checked={isChecked} onChange={() => onToggleChecked(obra.codObra)} />
            </td>
            <td className="obras-col-num">
                <span className="obras-row-number">{index + 1}</span>
            </td>
            <td className={sheetBlockClass("codigo") || undefined}>
                <button type="button" className="obras-identity-link obras-identity-link--code" onClick={() => onOpenDetail(obra.codObra)}>
                    <span className="obras-identity-link__text">{obra.codigoTienda}</span>
                </button>
            </td>
            <td className={sheetBlockClass("pais") || undefined}>{OPTIONAL_COLUMN_RENDERERS.pais.cell(obra)}</td>
            <td className={sheetBlockClass("ciudad") || undefined}>{OPTIONAL_COLUMN_RENDERERS.ciudad.cell(obra)}</td>
            <td className={sheetBlockClass("tienda") || undefined}>
                <button type="button" className="obras-identity-link" onClick={() => onOpenDetail(obra.codObra)}>
                    <span className="obras-identity-link__text">{obra.tienda}</span>
                </button>
            </td>
            {columns.map((column) => (
                <td key={column.id} className={sheetColumnClass(column)}>{column.cell(obra)}</td>
            ))}
        </tr>
    );
});

const OPTIONAL_COLUMN_RENDERERS = {
    ciudad: {
        label: "Ubicación",
        text: (obra) => obra.ciudad || "Sin ciudad",
        cell: (obra) => <span className="obras-table-generic">{obra.ciudad || "Sin ciudad"}</span>,
    },
    pais: {
        label: "País",
        text: (obra) => obra.pais || "Sin pais",
        cell: (obra) => <span className="obras-table-generic">{obra.pais || "Sin pais"}</span>,
    },
    estado: {
        label: "Estado",
        text: (obra) => obra.estado || "",
        cell: (obra) => <StatusBadge value={obra.estado} />,
    },
    apertura: {
        label: "Apertura",
        text: (obra) => formatCompactDate(obra.apertura),
        cell: (obra) => <span className="obras-table-generic">{formatCompactDate(obra.apertura)}</span>,
    },
    margen: {
        label: "Desviación CAPEX",
        align: "right",
        // Positivo = inversión por encima del CAPEX aprobado (▲, desfavorable).
        text: (obra) => { const value = getCapexDeviation(obra); return `${value > 0 ? "▲+" : "▼"}${currency.format(value)}`; },
        cell: (obra) => {
            const value = getCapexDeviation(obra);
            const over = value > 0;
            return (
                <span className={`obras-table-signal ${over ? "danger" : "success"}`} title={over ? "Por encima del CAPEX aprobado" : "Dentro del CAPEX aprobado"}>
                    <span className="obras-table-signal__glyph" aria-hidden="true">{over ? "▲" : "▼"}</span>
                    {over ? "+" : ""}{currency.format(value)}
                </span>
            );
        },
    },
    eurom2: {
        label: "EUR/m2",
        align: "right",
        text: (obra) => currency.format(obra.euroM2Inv || 0),
        cell: (obra) => <span className="obras-table-signal">{currency.format(obra.euroM2Inv || 0)}</span>,
    },
    // Alertas de hitos críticos (ZONIF, LEGAL, PLANO P3, Deadline MOB y los P3). El id
    // sigue siendo "bloq" para no perder las columnas que cada equipo tiene guardadas.
    bloq: {
        label: "Alertas",
        text: (obra) => String(hitoAlertCount(obra)),
        cell: (obra) => {
            const alerts = hitoAlertsOf(obra);
            const tone = alerts.length ? HITO_STATUS[alerts[0].status].tone : "";
            return (
                <span className={`obras-blocker-count ${tone ? `active tone-${tone}` : ""}`} title={alerts.length ? hitoAlertsText(obra) : "Sin alertas de hitos"}>
                    {alerts.length}
                </span>
            );
        },
    },
    pm: {
        label: "PM",
        text: (obra) => obra.pm || "Sin PM",
        cell: (obra) => <span className="obras-table-generic">{obra.pm || "Sin PM"}</span>,
    },
};
// Columnas fijas que dan acceso al detalle: siempre visibles, no se pueden ocultar.
const LOCKED_TABLE_COLUMNS = TABLE_COLUMNS.filter((column) => column.fixed && column.id !== "num" && column.id !== "actions");
// PM3 se ve por defecto (petición de BSK, 09/2026); PM2 y PMI siguen en el panel de columnas.
const DEFAULT_VISIBLE_COLUMNS = [...TABLE_COLUMNS.map((column) => column.id), "pm3"];
// Columnas nuevas que se enseñan UNA vez a quien ya tenía su selección guardada. Si
// después la oculta, se respeta: la marca de cada una evita volver a añadirla.
const COLUMNS_ADDED_ONCE = [{ id: "pm3", flag: "obras.table.columns.pm3.v1" }];
const TABLE_COLUMN_PRESETS = [
    {
        id: "base",
        label: "Base",
        icon: FiLayers,
        columns: DEFAULT_VISIBLE_COLUMNS,
    },
    {
        id: "capex",
        label: "CAPEX",
        icon: FiDollarSign,
        columns: [
            "num", "codigo", "tienda", "pais", "estado", "apertura",
            "margen", "eurom2", "capex", "totalOc", "totalBsk", "totalInversion",
            "actions",
        ],
    },
    {
        id: "proveedores",
        label: "Prov.",
        icon: FiBriefcase,
        columns: [
            "num", "codigo", "tienda", "pais", "ciudad", "estado",
            "pm", "pm2", "pm3", "consulting", "constructora", "studio", "mobiliario", "provIlum1",
            "actions",
        ],
    },
    {
        id: "deadlines",
        label: "Plazos",
        icon: FiCalendar,
        columns: [
            "num", "codigo", "tienda", "pais", "estado", "inicioObra",
            "apertura", "zonif", "legal", "planoP3", "deadlineMob", "p3Constructora", "p3Mobiliario",
            "actions",
        ],
    },
];

// Icono por grupo de columnas (cabeceras de la maqueta de columnas).
const COLUMN_GROUP_ICONS = { general: FiBox, equipo: FiUsers, hitos: FiCalendar, fechas: FiCalendar, costes: FiDollarSign, capex: FiDollarSign, operativa: FiActivity };
const columnGroupIcon = (label = "") => {
    const key = Object.keys(COLUMN_GROUP_ICONS).find((word) => label.toLowerCase().includes(word));
    return key ? COLUMN_GROUP_ICONS[key] : FiLayers;
};

// Every field from the detail card is also available as a (read-only) column, so the
// table can show ANY field. The five editable fields already have dedicated columns.
// Campos con columna propia en la tabla: se excluyen de las columnas genéricas
// para no duplicarlas (siguen siendo editables en la ficha).
const SPECIAL_FIELD_IDS = new Set(["codigoTienda", "tienda", "pais", "ciudad", "estado", "apertura", "pm"]);
const GENERIC_COLUMN_GROUPS = excelFieldGroups.map((group) => ({
    label: group.label,
    columns: group.fields
        .filter((fieldConfig) => !SPECIAL_FIELD_IDS.has(fieldConfig.field))
        .map((fieldConfig) => ({
            id: fieldConfig.field,
            label: fieldConfig.label,
            type: fieldConfig.type || (numericFields.has(fieldConfig.field) ? "number" : "text"),
        })),
})).filter((group) => group.columns.length);
const GENERIC_COLUMNS = GENERIC_COLUMN_GROUPS.flatMap((group) => group.columns);
// Grupos del panel de columnas: tabla principal (fijas + especiales) y los de la ficha.
const COLUMN_PANEL_GROUPS = [
    {
        id: "principal",
        label: "Tabla principal",
        icon: FiLayers,
        columns: [
            ...LOCKED_TABLE_COLUMNS.map((column) => ({ id: column.id, label: column.label, locked: true })),
            ...OPTIONAL_COLUMNS.map((column) => ({ id: column.id, label: OPTIONAL_COLUMN_RENDERERS[column.id]?.label || column.label })),
        ],
    },
    ...GENERIC_COLUMN_GROUPS.map((group) => ({ id: group.label, label: group.label, icon: columnGroupIcon(group.label), columns: group.columns })),
];
// Exportado para comprobar que los bloques de la hoja (sheetBlocks.js) son columnas reales.
export const ALL_COLUMN_IDS = [...TABLE_COLUMNS.map((column) => column.id), ...GENERIC_COLUMNS.map((column) => column.id)];
const FIXED_DATA_COLUMN_IDS = TABLE_COLUMNS.filter((column) => column.fixed && column.id !== "actions").map((column) => column.id);
// Celdas congeladas al desplazarse en horizontal: la casilla de selección + las fijas
// (# , Código, País, Ubicación y Tienda). obras.scss tiene una regla por cada una.
const FROZEN_CELL_COUNT = 1 + FIXED_DATA_COLUMN_IDS.length;
const OPTIONAL_DATA_COLUMN_IDS = ALL_COLUMN_IDS.filter((id) => id !== "actions" && !FIXED_DATA_COLUMN_IDS.includes(id));

// Categorical filters: each field lets the user pick several values (OR within the
// field); different fields combine with AND. Numeric/date filters use a min/max range.
const BASE_CATEGORY_FILTER_FIELDS = [
    { id: "pais", label: "País", getValue: (obra) => obra.pais },
    { id: "pm", label: "PM", getValue: (obra) => obra.pm },
    { id: "estado", label: "Estado", getValue: (obra) => obra.estado },
    { id: "ciudad", label: "Ubicación", getValue: (obra) => obra.ciudad },
    { id: "hitoSituacion", label: "Hitos", getValue: hitoSituacion },
];
const BASE_NUMBER_FILTER_FIELDS = [
    { id: "margen", label: "Desviación CAPEX", getValue: (obra) => filterFieldValue(obra, "margen", portfolioToday) },
    { id: "eurom2", label: "EUR/m2", getValue: (obra) => filterFieldValue(obra, "eurom2", portfolioToday) },
    { id: "bloq", label: "Alertas de hitos", getValue: hitoAlertCount },
];
const BASE_FILTER_IDS = new Set([
    ...BASE_CATEGORY_FILTER_FIELDS.map((field) => field.id),
    ...BASE_NUMBER_FILTER_FIELDS.map((field) => field.id),
]);
const DETAIL_FILTER_FIELDS = fieldSearchIndex
    .filter((fieldConfig) => !BASE_FILTER_IDS.has(fieldConfig.field))
    .map((fieldConfig) => ({
        id: fieldConfig.field,
        label: fieldConfig.label,
        groupLabel: fieldConfig.groupLabel,
        type: fieldConfig.type === "date"
            ? "date"
            : fieldConfig.type === "number" || numericFields.has(fieldConfig.field)
                ? "number"
                : "category",
        getValue: (obra) => obra[fieldConfig.field],
    }));
const CATEGORY_FILTER_FIELDS = [
    ...BASE_CATEGORY_FILTER_FIELDS,
    ...DETAIL_FILTER_FIELDS.filter((field) => field.type === "category"),
];
const NUMBER_FILTER_FIELDS = [
    ...BASE_NUMBER_FILTER_FIELDS,
    ...DETAIL_FILTER_FIELDS.filter((field) => field.type === "number"),
];
const DATE_FILTER_FIELDS = DETAIL_FILTER_FIELDS.filter((field) => field.type === "date");

// Filter fields unified (categorical + numeric) for the row-based filter builder.
const ALL_FILTER_FIELDS = [
    ...BASE_CATEGORY_FILTER_FIELDS.map((field) => ({ ...field, type: "category", groupLabel: "Básicos" })),
    ...BASE_NUMBER_FILTER_FIELDS.map((field) => ({ ...field, type: "number", groupLabel: "Indicadores" })),
    ...DETAIL_FILTER_FIELDS,
];
const getFilterField = (id) => ALL_FILTER_FIELDS.find((field) => field.id === id) || null;
// Orden de los grupos de campos en el panel de filtros.
const FILTER_GROUP_ORDER = ["Básicos", "Indicadores", ...excelFieldGroups.map((group) => group.label)];

// Campos de cada tipo de filtro personalizado: el filtro como dato (obraFilter.js) solo
// aplica a cada campo el tipo de filtro que tiene en la hoja.
const FILTER_FIELD_IDS = {
    category: new Set(CATEGORY_FILTER_FIELDS.map((field) => field.id)),
    number: new Set(NUMBER_FILTER_FIELDS.map((field) => field.id)),
    date: new Set(DATE_FILTER_FIELDS.map((field) => field.id)),
};

const COLUMNS_STORAGE_KEY = "obras.table.columns.v1";
const COLUMN_ORDER_STORAGE_KEY = "obras.table.columnOrder.v1";
const COLUMN_WIDTHS_STORAGE_KEY = "obras.table.columnWidths.v1";
const TABLE_SORT_STORAGE_KEY = "obras.table.sort.v2";
const ACTIVE_TAB_STORAGE_KEY = "obras.page.activeTab.v1";
// Formulas visibles de los KPI del Resumen. Deben coincidir con el calculo
// (portfolioHealth, buildPortfolioModel, attentionObras). Criterio unico de riesgo:
// isObraAtRisk (estado "En riesgo" o "Bloqueada").
const RESUMEN_FORMULAS = {
    riesgo: "En riesgo = obras con estado «En riesgo» o «Bloqueada»",
    capexExpuesto: "Σ CAPEX aprobado de obras en riesgo o con algún hito crítico en riesgo o no completado",
    ritmo: "Mes del año con más aperturas + inicios de obra",
    senales: "Nº de comprobaciones que fallan (de 5)",
    senalesDetalle: "Cuenta 1 por cada una: aperturas del mes pico > 30% de las obras · |CAPEX aprobado − inversión| > 5% del aprobado · concentración > 40% · obras en riesgo > 15% · algún hito crítico en riesgo o no completado",
    concentracion: "Obras del proveedor con más asignaciones ÷ obras filtradas",
    atencion: "Orden: +100 si la obra está en riesgo, +24 por hito crítico en riesgo o no completado, +8 por hito que necesita datos, +18 si la inversión supera el CAPEX; se muestran las 4 primeras",
};
const ROW_DENSITY_STORAGE_KEY = "obras.table.density.v1";
const IGNORE_SHEET_FILTERS_STORAGE_KEY = "obras.analysis.ignoreSheetFilters.v1";
// Densidad de fila de la hoja: alto de fila y tamaño de texto.
const ROW_DENSITY_OPTIONS = [
    { id: "compacto", label: "Compacto" },
    { id: "normal", label: "Normal" },
    { id: "grande", label: "Grande" },
];
// Pestañas de la pagina. "obras" es la hoja; "resumen" es la vista analitica de KPIs
// + alertas (antes en linea encima de la hoja); el resto son las vistas del cuadro
// de mandos, que hasta ahora vivian en un modal/panel inline lanzado desde cards.
const OBRAS_TABS = [
    { id: "obras", label: "Obras" },
    { id: "resumen", label: "Resumen" },
    { id: "gantt", label: "Cronograma" },
    { id: "agenda", label: "Agenda" },
    { id: "capex", label: "CAPEX" },
    { id: "mapa", label: "Mapa" },
    { id: "proveedores", label: "Proveedores" },
];
const OBRAS_ANALYSIS_TAB_IDS = new Set(["gantt", "agenda", "capex", "mapa", "proveedores"]);
const OBRAS_TAB_IDS = new Set(OBRAS_TABS.map((tab) => tab.id));
const FILTERS_STORAGE_KEY = "obras.table.filters.v1";
const COLUMN_QUICK_CONFIGS_STORAGE_KEY = "obras.table.columnQuickConfigs.v1";
const FILTER_QUICK_CONFIGS_STORAGE_KEY = "obras.table.filterQuickConfigs.v1";
const MAX_QUICK_CONFIGS = 5;
const MAX_QUICK_CONFIG_NAME_LENGTH = 24;
const EMPTY_CUSTOM_FILTERS = { category: {}, number: {}, date: {} };

// Columnas "congeladas" (checkbox, #, codigo, tienda) son siempre las 4 primeras y
// nunca se reordenan: su posicion esta hardcodeada en el CSS sticky (nth-child).
// El resto -las especiales de OPTIONAL_COLUMNS y todas las genericas- se pueden
// arrastrar libremente entre si; su orden persiste por usuario.
// Orden guardado + columnas nuevas desde entonces. Una columna nueva (p. ej. PM3) no va
// al final: se coloca detrás de la que la precede en el orden por defecto (PM2).
function normalizeColumnOrder(order) {
    const known = Array.isArray(order) ? order.filter((id) => OPTIONAL_DATA_COLUMN_IDS.includes(id)) : [];
    const result = [...known];
    OPTIONAL_DATA_COLUMN_IDS.forEach((id, index) => {
        if (result.includes(id)) return;
        const previous = OPTIONAL_DATA_COLUMN_IDS.slice(0, index).reverse().find((candidate) => result.includes(candidate));
        result.splice(previous ? result.indexOf(previous) + 1 : 0, 0, id);
    });
    return result;
}

function loadStoredConfig(key, fallback) {
    if (typeof window === "undefined") return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

function normalizeCustomFilters(filters) {
    const category = {};
    Object.entries(filters?.category || {}).forEach(([fieldId, values]) => {
        if (!getFilterField(fieldId)) return;
        // "" es la opción «No informado» (EMPTY_FILTER_VALUE): se conserva.
        const nextValues = Array.isArray(values)
            ? values.filter((value) => value != null)
            : [];
        if (nextValues.length) category[fieldId] = nextValues;
    });

    const normalizeRanges = (ranges) => {
        const nextRanges = {};
        Object.entries(ranges || {}).forEach(([fieldId, range]) => {
            // Campos que ya no existen (p. ej. "desv") se descartan del filtro guardado.
            if (!getFilterField(fieldId)) return;
            const min = range?.min ?? "";
            const max = range?.max ?? "";
            if (min !== "" || max !== "") nextRanges[fieldId] = { min, max };
        });
        return nextRanges;
    };

    return {
        category,
        number: normalizeRanges(filters?.number),
        date: normalizeRanges(filters?.date),
    };
}

function normalizeColumnQuickConfigs(configs) {
    if (!Array.isArray(configs)) return [];
    return configs
        .map((config) => {
            const columns = Array.isArray(config?.columns)
                ? config.columns.filter((columnId) => ALL_COLUMN_IDS.includes(columnId))
                : [];
            const name = String(config?.name || "").trim().slice(0, MAX_QUICK_CONFIG_NAME_LENGTH);
            if (!name || !columns.length) return null;
            return {
                id: config.id || `columns-${name}`,
                name,
                columns,
                createdAt: config.createdAt || null,
            };
        })
        .filter(Boolean)
        .slice(0, MAX_QUICK_CONFIGS);
}

function normalizeFilterQuickConfigs(configs) {
    if (!Array.isArray(configs)) return [];
    return configs
        .map((config) => {
            const name = String(config?.name || "").trim().slice(0, MAX_QUICK_CONFIG_NAME_LENGTH);
            if (!name) return null;
            return {
                id: config.id || `filters-${name}`,
                name,
                filters: {
                    search: config.filters?.search || "",
                    estado: config.filters?.estado || "Todos",
                    pais: config.filters?.pais || "Todos",
                    pmFilter: config.filters?.pmFilter || "Todos",
                    showArchived: Boolean(config.filters?.showArchived),
                    customFilters: normalizeCustomFilters(config.filters?.customFilters),
                },
                createdAt: config.createdAt || null,
            };
        })
        .filter(Boolean)
        .slice(0, MAX_QUICK_CONFIGS);
}

function countActiveCustomFilters(filters) {
    const categoryCount = Object.values(filters.category || {}).filter((values) => values && values.length).length;
    const numberCount = Object.values(filters.number || {}).filter((range) => range && ((range.min ?? "") !== "" || (range.max ?? "") !== "")).length;
    const dateCount = Object.values(filters.date || {}).filter((range) => range && ((range.min ?? "") !== "" || (range.max ?? "") !== "")).length;
    return categoryCount + numberCount + dateCount;
}

function getTableSortValue(obra, columnId) {
    if (columnId === "num") return Number(obra.codObra || 0);
    if (columnId === "codigo") return obra.codigoTienda || "";
    if (columnId === "tienda") return obra.tienda || "";
    if (columnId === "margen") return getCapexDeviation(obra);
    if (columnId === "eurom2") return Number(obra.euroM2Inv || 0);
    if (columnId === "bloq") return hitoAlertCount(obra);

    const fieldConfig = fieldConfigByName[columnId];
    const rawValue = obra[columnId];
    if (fieldConfig?.type === "date") return dateFromIso(rawValue)?.getTime() || 0;
    if (fieldConfig?.type === "number" || numericFields.has(columnId)) return Number(rawValue || 0);
    return String(rawValue ?? "");
}

function compareByOneColumn(a, b, criterion) {
    const aValue = getTableSortValue(a, criterion.id);
    const bValue = getTableSortValue(b, criterion.id);
    const direction = criterion.direction === "asc" ? 1 : -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
    }

    return String(aValue).localeCompare(String(bValue), "es", {
        numeric: true,
        sensitivity: "base",
    }) * direction;
}

// `sort` es una cadena ordenada de criterios (multi-columna): shift+clic en la
// cabecera añade un nivel, clic normal reemplaza la cadena por ese único criterio.
function compareTableRows(a, b, sort) {
    const criteria = Array.isArray(sort) ? sort : [sort];
    for (const criterion of criteria) {
        const result = compareByOneColumn(a, b, criterion);
        if (result !== 0) return result;
    }
    return 0;
}

// Sin límite de columnas visibles: la tabla resuelve el ancho con scroll horizontal
// (.obras-table-wrap { overflow: auto }), no recortando el número de campos mostrables.
function limitVisibleColumns(columnIds) {
    const fixedIds = TABLE_COLUMNS.filter((column) => column.fixed).map((column) => column.id);
    const normalized = Array.from(new Set([...fixedIds, ...columnIds]));
    const selectedOptional = normalized.filter((id) => OPTIONAL_DATA_COLUMN_IDS.includes(id));

    return [...fixedIds, ...selectedOptional];
}

export default function Obras({ role = OBRAS_ROLES.TOTAL }) {
    // Perfil en Obras (utils/obrasAccess.js): Imagen y Estudio son de consulta (Imagen puede
    // cambiar el PMI). El API lo hace cumplir; aquí solo se adapta la pantalla.
    const readOnly = isReadOnlyObrasRole(role);
    // /obras-mock es la ruta de desarrollo (JSON local). La ruta real (/admin/obras)
    // carga las obras desde el backend (ver efecto de carga más abajo).
    const useLocalData = isMockObrasRoute();
    const [obras, setObras] = useState(initialObras);
    const [loadState, setLoadState] = useState(useLocalData ? "ready" : "loading"); // loading | ready | error
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    // Concurrencia (sondeo ligero): aviso cuando otra persona modifica en el servidor.
    const [staleNotice, setStaleNotice] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [detailTab, setDetailTab] = useState("general");
    // Búsqueda, selectores rápidos y "Archivadas" se guardan como el resto de filtros de
    // la hoja: se conservan al cambiar de pestaña y al recargar.
    const isText = (value) => typeof value === "string";
    const [search, setSearch] = usePersistentState("obras.table.search.v1", "", { validate: isText });
    const [estado, setEstado] = usePersistentState("obras.table.estado.v1", "Todos", { validate: isText });
    const [pais, setPais] = usePersistentState("obras.table.pais.v1", "Todos", { validate: isText });
    const [pmFilter, setPmFilter] = usePersistentState("obras.table.pm.v1", "Todos", { validate: isText });
    const [showArchived, setShowArchived] = usePersistentState("obras.table.archived.v1", false, { validate: isOneOf([true, false]) });
    // Pantalla completa (botón del ojo): Obras tapa el menú y la cabecera de la app.
    const [fullscreen, setFullscreen] = usePersistentState("obras.fullscreen.v1", false, { validate: isOneOf([true, false]) });
    const [draftEdits, setDraftEdits] = useState({});
    // Refs con el último valor, para el sondeo de concurrencia (evita cerrar sobre estado obsoleto).
    const obrasRef = useRef(obras); obrasRef.current = obras;
    const draftsRef = useRef(draftEdits); draftsRef.current = draftEdits;
    const savingRef = useRef(saving); savingRef.current = saving;
    const [detailFieldSearch, setDetailFieldSearch] = useState("");
    const [highlightedDetailField, setHighlightedDetailField] = useState(null);
    const [changeRequest, setChangeRequest] = useState(null);
    // Hito crítico que se está confirmando/revisando: { codObra, field }.
    const [hitoReview, setHitoReview] = useState(null);
    // Bloque «Hitos críticos» del Resumen: tipo de alerta elegido y lista completa o no.
    const [hitoAlertFilter, setHitoAlertFilter] = useState("");
    const [showAllHitoAlerts, setShowAllHitoAlerts] = useState(false);
    const [changeReason, setChangeReason] = useState("");
    const [changeReasonError, setChangeReasonError] = useState(false);
    // Modal de confirmación/aviso in-app (sustituye a window.confirm/alert).
    const [confirmDialog, setConfirmDialog] = useState(null);
    const confirmResolveRef = useRef(null);
    const requestConfirm = (options) => new Promise((resolve) => {
        confirmResolveRef.current = resolve;
        setConfirmDialog({
            title: options.title || "¿Confirmar?",
            message: options.message || "",
            confirmLabel: options.confirmLabel || "Confirmar",
            cancelLabel: options.cancelLabel === null ? null : (options.cancelLabel || "Cancelar"),
            tone: options.tone || "default",
        });
    });
    const resolveConfirm = (result) => {
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        setConfirmDialog(null);
        if (resolve) resolve(result);
    };
    // Aviso de una sola acción. Si había una confirmación esperando respuesta se resuelve
    // como "cancelar": su promesa no se cumpliría nunca y el flujo que la espera se colgaría.
    const showNotice = ({ title, message, confirmLabel = "Entendido", tone = "default" }) => {
        const pending = confirmResolveRef.current;
        confirmResolveRef.current = null;
        if (pending) pending(false);
        setConfirmDialog({ title, message, confirmLabel, cancelLabel: null, tone });
    };
    const showSaveError = (err, fallback) => {
        const message = err?.response || err?.isAxiosError ? apiErrorMessage(err, fallback) : (err?.message || apiErrorMessage(err, fallback));
        setSaveError(message);
        // El usuario puede leer el motivo sin perder los cambios pendientes.
        showNotice({ title: "No se pudo guardar", message, tone: "danger" });
    };
    useEffect(() => {
        if (!confirmDialog) return undefined;
        const onKey = (event) => { if (event.key === "Escape") resolveConfirm(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmDialog]);
    // Carga de obras desde el backend en la ruta real. En /obras-mock se conservan
    // los datos locales (useLocalData). Las filas del API vienen de vw_Obras_Ficha
    // (camelCase) y se adaptan al modelo interno con buildApiObra.
    useEffect(() => {
        if (useLocalData) return undefined;
        let cancelled = false;
        setLoadState("loading");
        (async () => {
            try {
                const rows = await fetchObras();
                if (cancelled) return;
                setObras(rows.map(buildApiObra));
                setLoadState("ready");
            } catch (err) {
                if (cancelled) return;
                console.error("No se pudieron cargar las obras desde el API", err);
                setLoadState("error");
            }
        })();
        return () => { cancelled = true; };
    }, [useLocalData]);
    useEffect(() => {
        if (useLocalData) return;
        let cancelled = false;
        setCatalogs(current => ({ ...current, pais: [], ciudad: [] }));
        fetchGeografia().then(geo => {
            if (!cancelled) setCatalogs(current => ({ ...current, ...geo }));
        }).catch(err => {
            if (!cancelled) setSaveError(err?.response?.data?.message || "No se pudo cargar el catálogo de países y ciudades. Recarga la página.");
        });
        return () => { cancelled = true; };
    }, [useLocalData]);
    // Carga de catálogos desde el backend (solo tipos de dbo.Obras_Catalogos).
    // Se fusionan sobre los locales, que aportan pais/ciudad/proveedores.
    useEffect(() => {
        if (useLocalData) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchCatalogos();
                if (cancelled || !rows.length) return;
                const { byTipo, empresas: empresasApi } = catalogStateFromRows(rows);
                setEmpresas(empresasApi);
                setCatalogs((current) => ({ ...current, ...byTipo }));
            } catch (err) {
                console.error("No se pudieron cargar los catálogos desde el API", err);
            }
        })();
        return () => { cancelled = true; };
    }, [useLocalData]);
    // Responsables (PM/PMI) elegibles: se cargan del backend para los selectores.
    useEffect(() => {
        if (useLocalData) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchResponsables();
                if (cancelled) return;
                const next = { pm: [], pmi: [] };
                rows.forEach((row) => {
                    const key = Number(row.rol) === 2 ? "pmi" : "pm";
                    next[key].push({ codUsuario: Number(row.codUsuario), nombre: row.nombre || `Usuario ${row.codUsuario}` });
                });
                setResponsablesByRol(next);
            } catch (err) {
                console.error("No se pudieron cargar los responsables (PM/PMI)", err);
            }
        })();
        return () => { cancelled = true; };
    }, [useLocalData]);
    // Historial de la obra abierta: se carga del backend al abrir la ficha (ruta
    // real) y reemplaza las entradas de esa obra en el estado de auditoría.
    useEffect(() => {
        if (useLocalData || !selectedId) return undefined;
        const obra = obras.find((item) => item.codObra === selectedId);
        if (!obra || obra._isNew) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchAuditoria(selectedId);
                if (cancelled) return;
                const mapped = rows.map(mapAuditRow);
                setAuditEntries((current) => [
                    ...mapped,
                    ...current.filter((entry) => entry.codObra !== selectedId),
                ]);
            } catch (err) {
                console.error("No se pudo cargar el historial de la obra", err);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useLocalData, selectedId]);
    // En la ruta real el histórico arranca vacío (la auditoría se persiste en
    // Obras_Auditoria); las entradas de ejemplo son solo para /obras-mock.
    const [auditEntries, setAuditEntries] = useState(useLocalData ? initialAuditEntries : []);
    const [lastSavedChange, setLastSavedChange] = useState(null);
    const [catalogs, setCatalogs] = useState(createInitialCatalogs);
    // Empresas de dbo.Empresas que el API considera de Obras (proveedores o ya usadas).
    const [empresas, setEmpresas] = useState([]);
    // Proveedores: opciones = revisados + nombres que ya usan las obras de este entorno, sin
    // repetir equivalentes ("ATLANTE" / "Grupo Atlante"), con su CodEmpresa cuando existe.
    // Studio no: en la ruta real son las empresas de tipo 4 que devuelve el API.
    useEffect(() => {
        setCatalogs((current) => mergeEmpresaCatalogEntries(current, obras, empresas, useLocalData ? new Set() : STUDIO_FROM_API));
    }, [obras, empresas, useLocalData]);
    // Usuarios habilitados como PM/PMI (pool admin en BD). Solo lectura desde la app.
    const [responsablesByRol, setResponsablesByRol] = useState({ pm: [], pmi: [] });
    // Nombres de los PM/PMI por CodUsuario, para el historial y los cambios pendientes.
    const usuarioNames = useMemo(() => userNames(responsablesByRol, obras), [responsablesByRol, obras]);
    const [catalogManager, setCatalogManager] = useState(null);
    const [geographyManager, setGeographyManager] = useState(false);
    // Pestaña activa (Obras / Resumen / Cronograma / Agenda / CAPEX / Mapa / Proveedores).
    // Persistida por usuario para que el equipo aterrice en donde estaba.
    const [activeTab, setActiveTab] = useState(() => {
        const stored = loadStoredConfig(ACTIVE_TAB_STORAGE_KEY, "obras");
        return OBRAS_TAB_IDS.has(stored) ? stored : "obras";
    });
    // Compat: los consumidores existentes (efecto de scroll, botón de volver
    // desde la ficha, panel del cuadro) leen dashboardOpen/activeVisual; los
    // derivamos de activeTab para no tocar cada llamada.
    const dashboardOpen = OBRAS_ANALYSIS_TAB_IDS.has(activeTab);
    // La hoja (≈4.000 nodos) se monta la primera vez que se abre y después solo se
    // oculta: volver a "Obras" desde otra pestaña no la reconstruye entera.
    const [sheetMounted, setSheetMounted] = useState(activeTab === "obras");
    if (activeTab === "obras" && !sheetMounted) setSheetMounted(true);
    const activeVisual = dashboardOpen ? activeTab : "gantt";
    const [selectedObraIds, setSelectedObraIds] = useState(() => new Set());
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [dockEstadoOpen, setDockEstadoOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const stored = loadStoredConfig(COLUMNS_STORAGE_KEY, DEFAULT_VISIBLE_COLUMNS);
        const columns = Array.isArray(stored) && stored.length ? stored : DEFAULT_VISIBLE_COLUMNS;
        const added = COLUMNS_ADDED_ONCE.filter(({ flag }) => !loadStoredConfig(flag, false));
        try { added.forEach(({ flag }) => window.localStorage.setItem(flag, "true")); } catch { /* sin almacenamiento: se añade en cada visita */ }
        const missing = added.map(({ id }) => id).filter((id) => !columns.includes(id));
        return missing.length ? [...columns, ...missing] : columns;
    });
	    const [customFilters, setCustomFilters] = useState(() => {
	        const stored = loadStoredConfig(FILTERS_STORAGE_KEY, EMPTY_CUSTOM_FILTERS);
	        return normalizeCustomFilters(stored);
	    });
        const [savedColumnConfigs, setSavedColumnConfigs] = useState(() =>
            normalizeColumnQuickConfigs(loadStoredConfig(COLUMN_QUICK_CONFIGS_STORAGE_KEY, []))
        );
        const [savedFilterConfigs, setSavedFilterConfigs] = useState(() =>
            normalizeFilterQuickConfigs(loadStoredConfig(FILTER_QUICK_CONFIGS_STORAGE_KEY, []))
        );
	    // tableSort: [{ id, direction }] con un único criterio. Se guarda como lista para
	    // no romper lo ya almacenado; de un orden multicolumna antiguo solo se usa el primero.
	    const [tableSort, setTableSort] = useState(() => {
	        const stored = loadStoredConfig(TABLE_SORT_STORAGE_KEY, null);
	        const valid = (Array.isArray(stored) ? stored : [])
	            .filter((criterion) => criterion && ALL_COLUMN_IDS.includes(criterion.id))
	            .map((criterion) => ({ id: criterion.id, direction: criterion.direction === "desc" ? "desc" : "asc" }))
	            .slice(0, 1);
	        return valid.length ? valid : [{ id: "codigo", direction: "asc" }];
	    });
	    const [columnOrder, setColumnOrder] = useState(() => normalizeColumnOrder(loadStoredConfig(COLUMN_ORDER_STORAGE_KEY, [])));
	    const [columnWidths, setColumnWidths] = useState(() => loadStoredConfig(COLUMN_WIDTHS_STORAGE_KEY, {}));
	    // Vistas de analisis que NO heredan los filtros de la hoja: { gantt: true, ... }.
	    const [ignoreSheetFilters, setIgnoreSheetFilters] = useState(() => {
	        const stored = loadStoredConfig(IGNORE_SHEET_FILTERS_STORAGE_KEY, {});
	        return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
	    });
	    const [rowDensity, setRowDensity] = useState(() => {
	        const stored = loadStoredConfig(ROW_DENSITY_STORAGE_KEY, "normal");
	        return ROW_DENSITY_OPTIONS.some((option) => option.id === stored) ? stored : "normal";
	    });
	    const [draggedColumnId, setDraggedColumnId] = useState(null);
	    const [dragOverColumnId, setDragOverColumnId] = useState(null);
	    const draggedColumnIdRef = useRef(null);
	    const resizingRef = useRef(null);
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [reportMenuOpen, setReportMenuOpen] = useState(false);
    const reportMenuRef = useRef(null);
    const obrasPageRef = useRef(null);
    const [dockLeft, setDockLeft] = useState(null);


    // Tooltip de descripción de campo: una sola burbuja fija sobre <body>, colocada
    // por JS junto al nombre; nunca la recorta el overflow y se ajusta al viewport.
    useEffect(() => {
        const tip = document.createElement("div");
        tip.className = "obras-hint-tip";
        tip.setAttribute("role", "tooltip");
        tip.hidden = true;
        document.body.appendChild(tip);
        let current = null;
        const place = (el) => {
            const rect = el.getBoundingClientRect();
            const margin = 8;
            const tw = tip.offsetWidth;
            const th = tip.offsetHeight;
            let top = rect.bottom + margin;
            if (top + th > window.innerHeight - margin) top = Math.max(margin, rect.top - th - margin);
            let left = rect.left;
            if (left + tw > window.innerWidth - margin) left = window.innerWidth - margin - tw;
            if (left < margin) left = margin;
            tip.style.top = `${Math.round(top)}px`;
            tip.style.left = `${Math.round(left)}px`;
        };
        const show = (el) => {
            // Descripción de campo (data-hint) o texto completo de una celda de la hoja que
            // no cabe en su columna (solo si de verdad está recortado).
            const hint = el.getAttribute("data-hint")
                || (el.matches(SHEET_TRUNCATABLE_SELECTOR) && isTruncated(el) ? (el.textContent || "").trim() : "");
            if (!hint) return;
            current = el;
            tip.textContent = hint;
            tip.hidden = false;
            place(el);
        };
        const hide = () => { current = null; tip.hidden = true; };
        const matchEl = (event) => (event.target && event.target.closest ? event.target.closest(`.obras-detail-field__name.has-hint, ${SHEET_TRUNCATABLE_SELECTOR}`) : null);
        const onOver = (event) => { const el = matchEl(event); if (el) show(el); };
        const onOut = (event) => { const el = matchEl(event); if (el && el === current) hide(); };
        const onFocusIn = (event) => { const el = matchEl(event); if (el) show(el); };
        const onFocusOut = () => hide();
        const onScrollOrResize = () => { if (current) hide(); };
        document.addEventListener("mouseover", onOver);
        document.addEventListener("mouseout", onOut);
        document.addEventListener("focusin", onFocusIn);
        document.addEventListener("focusout", onFocusOut);
        document.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            document.removeEventListener("mouseover", onOver);
            document.removeEventListener("mouseout", onOut);
            document.removeEventListener("focusin", onFocusIn);
            document.removeEventListener("focusout", onFocusOut);
            document.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
            tip.remove();
        };
    }, []);

    // Cierra el menú de informe al hacer clic fuera o pulsar Escape.
    useEffect(() => {
        if (!reportMenuOpen) return undefined;
        const onPointerDown = (event) => {
            if (reportMenuRef.current && !reportMenuRef.current.contains(event.target)) setReportMenuOpen(false);
        };
        const onKeyDown = (event) => { if (event.key === "Escape") setReportMenuOpen(false); };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [reportMenuOpen]);

    // En pantalla completa se ocultan la barra superior, el menú lateral y el pie de la
    // app (clase en <body>, ver obras.scss). No se tapan con z-index: la barra de la app
    // está a 999 y los avisos globales a 1000, que deben seguir viéndose por encima.
    // Al salir de Obras (desmontar) la app recupera su marco.
    useEffect(() => {
        if (!fullscreen) return undefined;
        document.body.classList.add("obras-fullscreen");
        return () => document.body.classList.remove("obras-fullscreen");
    }, [fullscreen]);

    useEffect(() => {
        if (!fullscreen) return undefined;
        const onKeyDown = (event) => {
            if (event.key !== "Escape" || event.defaultPrevented) return;
            if (document.querySelector('[aria-modal="true"], .obras-panel-layer, .obras-modal-layer')) return;
            setFullscreen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [fullscreen, setFullscreen]);

    // La barra de selección (fixed) se centra respecto al área de contenido, no a
    // toda la ventana, para no quedar desplazada por el sidebar. Se remide si cambia
    // el ancho del contenido (p. ej. al plegar el sidebar).
    useEffect(() => {
        const el = obrasPageRef.current;
        if (!el) return undefined;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            setDockLeft(rect.left + rect.width / 2);
        };
        measure();
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
        if (observer) observer.observe(el);
        window.addEventListener("resize", measure);
        return () => { if (observer) observer.disconnect(); window.removeEventListener("resize", measure); };
    }, []);

    // Al abrir un cuadro de mandos se lleva a la vista el rail "Análisis detallado"
    // (título + cards para cambiar de cuadro), dejándolo arriba con el panel debajo.
    // No movemos si el rail ya está cerca de la parte superior (p. ej. al cambiar de
    // vista con las cards), para no dar saltos.
    useEffect(() => {
        if (!dashboardOpen) return undefined;
        const timer = window.setTimeout(() => {
            const launcher = document.querySelector(".obras-dashboard-launcher");
            const panel = document.querySelector(".obras-portfolio--inline");
            if (!launcher || !panel) return;
            const rect = launcher.getBoundingClientRect();
            const alreadyAnchored = rect.top >= 0 && rect.top <= window.innerHeight * 0.25;
            if (alreadyAnchored) return;
            const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            launcher.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
        }, 60);
        return () => window.clearTimeout(timer);
    }, [dashboardOpen, activeVisual]);

    // Bloqueo de scroll con cualquier modal a pantalla completa abierto. El contenedor
    // que scrollea es <html> (no <body>), así que hay que bloquearlo ahí; si no, el
    // fondo sigue moviéndose detrás del modal y el scroll se siente "pillado".
    useEffect(() => {
        const anyModalOpen = bulkEditOpen || Boolean(selectedId) || Boolean(catalogManager) || columnMenuOpen || filterMenuOpen;
        const root = document.documentElement;
        root.style.overflow = anyModalOpen ? "hidden" : "";
        return () => { root.style.overflow = ""; };
    }, [bulkEditOpen, selectedId, catalogManager, columnMenuOpen, filterMenuOpen]);
    // Campo que el panel de filtros abre al entrar (clic en un chip de filtro).
    const [filterPanelFocus, setFilterPanelFocus] = useState(null);
    const deferredSearch = useDeferredValue(search);

    useEffect(() => {
        try { window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns)); } catch { /* ignore quota errors */ }
    }, [visibleColumns]);
    useEffect(() => {
        try { window.localStorage.setItem(TABLE_SORT_STORAGE_KEY, JSON.stringify(tableSort)); } catch { /* ignore quota errors */ }
    }, [tableSort]);
    useEffect(() => {
        try { window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder)); } catch { /* ignore quota errors */ }
    }, [columnOrder]);
    useEffect(() => {
        try { window.localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths)); } catch { /* ignore quota errors */ }
    }, [columnWidths]);
    useEffect(() => {
        try { window.localStorage.setItem(ROW_DENSITY_STORAGE_KEY, JSON.stringify(rowDensity)); } catch { /* ignore quota errors */ }
    }, [rowDensity]);
    useEffect(() => {
        try { window.localStorage.setItem(IGNORE_SHEET_FILTERS_STORAGE_KEY, JSON.stringify(ignoreSheetFilters)); } catch { /* ignore quota errors */ }
    }, [ignoreSheetFilters]);
    useEffect(() => {
        try { window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, JSON.stringify(activeTab)); } catch { /* ignore quota errors */ }
    }, [activeTab]);
	    useEffect(() => {
	        try { window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(customFilters)); } catch { /* ignore quota errors */ }
	    }, [customFilters]);
        useEffect(() => {
            try { window.localStorage.setItem(COLUMN_QUICK_CONFIGS_STORAGE_KEY, JSON.stringify(savedColumnConfigs)); } catch { /* ignore quota errors */ }
        }, [savedColumnConfigs]);
        useEffect(() => {
            try { window.localStorage.setItem(FILTER_QUICK_CONFIGS_STORAGE_KEY, JSON.stringify(savedFilterConfigs)); } catch { /* ignore quota errors */ }
        }, [savedFilterConfigs]);
    const isColumnVisible = (id) => visibleColumns.includes(id);
    const visibleColumnCount = TABLE_COLUMNS.filter((column) => column.fixed || visibleColumns.includes(column.id)).length
        + GENERIC_COLUMNS.filter((column) => visibleColumns.includes(column.id)).length;
	    const activeFilterCount = countActiveCustomFilters(customFilters);
	    const visibleGenericColumns = GENERIC_COLUMNS.filter((column) => visibleColumns.includes(column.id));
        // Estado del botón Guardar de una vista: nombre, límite y sobrescritura.
        const quickConfigSaveStatus = (configs) => (name) => {
            if (!name) return { canSave: false, message: "" };
            const exists = configs.some((config) => config.name.toLowerCase() === name.toLowerCase());
            if (exists) return { canSave: true, message: `Sustituirá la vista «${name}».` };
            if (configs.length >= MAX_QUICK_CONFIGS) return { canSave: false, message: `Límite de ${MAX_QUICK_CONFIGS} vistas: elimina una para guardar otra.` };
            return { canSave: true, message: "" };
        };

	
	    const closeFilterMenu = () => { setFilterMenuOpen(false); setFilterPanelFocus(null); };
	    const openFilterPanel = (fieldId = null) => { setFilterPanelFocus(fieldId); setFilterMenuOpen(true); setColumnMenuOpen(false); };
	    const filterSummary = (field) => summarizeFilter(field.type, {
	        selected: customFilters.category[field.id] || [],
	        range: customFilters[field.type]?.[field.id] || null,
	    });
	    // Linea de chips bajo las pestañas: un chip por filtro activo (se quita con ×).
	    const activeFilterChips = [
	        ...(search.trim() ? [{ key: "search", label: "Búsqueda", value: `"${search.trim()}"`, onRemove: () => setSearch("") }] : []),
	        ...ALL_FILTER_FIELDS.filter((field) => {
	            if (field.type === "category") return (customFilters.category[field.id] || []).length > 0;
	            const range = customFilters[field.type]?.[field.id];
	            return range && ((range.min ?? "") !== "" || (range.max ?? "") !== "");
	        }).map((field) => ({
	            key: `${field.type}-${field.id}`,
	            fieldId: field.id,
	            label: field.label,
	            value: filterSummary(field),
	            onRemove: () => (field.type === "category" ? clearCategoryField(field.id) : clearRangeField(field.type, field.id)),
	        })),
	    ];
	    // Chips que caben en la fila de pestañas; el resto se resume en "+N" (abre Filtros).
	    // Se miden todos (los que no caben quedan invisibles pero con su ancho) y se
	    // recalcula al cambiar los chips o el ancho disponible.
	    const chipsRef = useRef(null);
	    const [visibleChipCount, setVisibleChipCount] = useState(Infinity);
	    const chipsSignature = activeFilterChips.map((chip) => `${chip.key}=${chip.value}`).join("|");
	    useLayoutEffect(() => {
	        const container = chipsRef.current;
	        if (!container) return undefined;
	        const measure = () => {
	            const chips = Array.from(container.querySelectorAll(":scope > .obras-filter-chip"));
	            const more = container.querySelector(":scope > .obras-filter-chip-more");
	            const available = container.clientWidth;
	            const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
	            const widths = chips.map((chip) => chip.offsetWidth);
	            const total = widths.reduce((acc, width) => acc + width, 0) + gap * Math.max(0, widths.length - 1);
	            let count = widths.length;
	            if (total > available) {
	                const reserve = (more?.offsetWidth || 64) + gap;
	                let used = 0;
	                count = 0;
	                for (const width of widths) {
	                    const next = used + (count ? gap : 0) + width;
	                    if (next + reserve > available) break;
	                    used = next;
	                    count += 1;
	                }
	            }
	            setVisibleChipCount((current) => (current === count ? current : count));
	        };
	        measure();
	        if (typeof ResizeObserver === "undefined") return undefined;
	        const observer = new ResizeObserver(measure);
	        observer.observe(container);
	        return () => observer.disconnect();
	    }, [chipsSignature]);
	    const hiddenChips = activeFilterChips.slice(visibleChipCount);

        // Columnas especiales (OPTIONAL_COLUMN_RENDERERS) + genericas, visibles, en el
        // orden que haya dejado el usuario al arrastrarlas. Las congeladas (casilla, #,
        // código, país, ubicación y tienda) nunca entran aquí: van fijas en el JSX, antes.
        // Memoizado: la identidad del array es parte de la comparación de SheetRow.
        const orderedReorderableColumns = useMemo(() => columnOrder
            .filter((id) => visibleColumns.includes(id))
            .map((id) => {
                const special = OPTIONAL_COLUMN_RENDERERS[id];
                if (special) return { id, label: special.label, cell: special.cell, text: special.text, align: special.align };
                const generic = GENERIC_COLUMNS.find((column) => column.id === id);
                if (!generic) return null;
                const unit = fieldUnits[id];
                const align = (generic.type === "number" && (unit === "EUR" || unit === "EUR/m2")) ? "right" : undefined;
                return { id, label: generic.label, cell: (obra) => renderGenericCell(obra, generic), text: (obra) => genericColumnText(obra, generic), align };
            })
            .filter(Boolean), [columnOrder, visibleColumns]);

        const moveColumn = (sourceId, targetId) => {
            if (sourceId === targetId) return;
            setColumnOrder((current) => {
                const next = current.filter((id) => id !== sourceId);
                const targetIndex = next.indexOf(targetId);
                if (targetIndex === -1) return current;
                next.splice(targetIndex, 0, sourceId);
                return next;
            });
        };

        const startColumnResize = (columnId, event) => {
            event.preventDefault();
            event.stopPropagation();
            const th = event.target.closest("th");
            if (!th) return;
            const startX = event.clientX;
            const startWidth = th.offsetWidth;
            resizingRef.current = { columnId, startX, startWidth };
            const onMouseMove = (moveEvent) => {
                const delta = moveEvent.clientX - startX;
                const newWidth = Math.max(60, startWidth + delta);
                setColumnWidths((prev) => ({ ...prev, [columnId]: newWidth }));
            };
            const onMouseUp = () => {
                resizingRef.current = null;
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };
            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        };
        // Doble clic en el borde de la cabecera: ajusta el ancho al contenido mas
        // ancho de la columna (cabecera incluida), como en Excel.
        // Navegacion por teclado en la hoja: flechas arriba/abajo (y Re/Av Pag, Inicio,
        // Fin) mueven el foco a la misma columna de otra fila; Intro abre la ficha
        // (los enlaces de codigo/tienda son botones) y Espacio marca la casilla.
        const handleSheetKeyDown = (event) => {
            const cell = event.target.closest("tbody td");
            const row = cell?.parentElement;
            const tbody = row?.parentElement;
            if (!cell || !tbody) return;
            if (row.dataset.rowIndex === undefined) return;
            // Índices absolutos (data-row-index): con la hoja pintada por ventana, la fila
            // destino puede no estar en el DOM; entonces se desplaza hasta ella y se
            // enfoca cuando se pinta (efecto de pendingSheetFocusRef).
            const total = sortedFilteredObras.length;
            const current = Number(row.dataset.rowIndex);
            const steps = { ArrowDown: 1, ArrowUp: -1, PageDown: 10, PageUp: -10 };
            let target = null;
            if (event.key in steps) target = Math.max(0, Math.min(total - 1, current + steps[event.key]));
            else if (event.key === "Home" && event.ctrlKey) target = 0;
            else if (event.key === "End" && event.ctrlKey) target = total - 1;
            if (target === null || target === current) return;
            event.preventDefault();
            if (!focusSheetRow(target, cell.cellIndex)) {
                pendingSheetFocusRef.current = { index: target, cellIndex: cell.cellIndex };
                const wrap = sheetWrapRef.current;
                if (wrap) wrap.scrollTop = rowOffset(sheetRowHeights, target, sheetRowEstimate) + (sheetTableRef.current?.tHead?.offsetHeight || 0) - wrap.clientHeight / 3;
            }
        };
        const autofitColumn = (columnId, event) => {
            event.preventDefault();
            event.stopPropagation();
            const th = event.target.closest("th");
            const table = th?.closest("table");
            if (!th || !table) return;
            // Texto de la columna para cada obra (todas, no solo las filas pintadas).
            const textOf = columnId === "codigo" ? (obra) => String(obra.codigoTienda ?? "")
                : columnId === "tienda" ? (obra) => String(obra.tienda ?? "")
                    : orderedReorderableColumns.find((column) => column.id === columnId)?.text;
            if (!textOf) return;
            const context = document.createElement("canvas").getContext("2d");
            if (!context) return;
            const fontOf = (element) => {
                const style = getComputedStyle(element);
                return `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
            };
            const paddingOf = (element) => {
                const style = getComputedStyle(element);
                return parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0);
            };
            const sampleCell = table.querySelector(`tbody tr[data-row-index] > td:nth-child(${th.cellIndex + 1})`);
            const sample = sampleCell?.querySelector(".obras-identity-link__text, .obras-table-generic, .obras-table-signal, .obras-badge, .obras-blocker-count") || sampleCell;
            const bodyFont = sample ? fontOf(sample) : fontOf(th);
            const measure = (text) => { context.font = bodyFont; return context.measureText(text).width; };
            // Extras de insignias/iconos (el contenido de bloque ocupa la celda entera: sin extras).
            const overhead = sample && sample !== sampleCell && getComputedStyle(sample).display !== "block"
                ? Math.min(48, Math.max(0, sample.getBoundingClientRect().width - measure(sample.textContent || "")))
                : 0;
            // La etiqueta de la cabecera (con su icono de orden) también debe caber.
            const sortButton = th.querySelector(".obras-table-sort");
            const labelSpan = sortButton?.querySelector("span");
            let headerNeed = MIN_COLUMN_WIDTH;
            if (sortButton && labelSpan) {
                context.font = fontOf(labelSpan);
                const gap = parseFloat(getComputedStyle(sortButton).columnGap) || 0;
                const icons = Array.from(sortButton.children).filter((child) => child !== labelSpan)
                    .reduce((acc, child) => acc + child.getBoundingClientRect().width + gap, 0);
                headerNeed = Math.ceil(context.measureText(labelSpan.textContent || "").width + icons + paddingOf(th) + 2);
            }
            const width = computeColumnWidth({
                texts: obras.map(textOf),
                measureText: measure,
                overhead,
                padding: (sampleCell ? paddingOf(sampleCell) : 24) + 2,
                min: Math.max(MIN_COLUMN_WIDTH, headerNeed),
                max: 640,
            });
            setColumnWidths((prev) => ({ ...prev, [columnId]: width }));
        };

        const renderSortableHeader = (id, label, extraClass = "", draggable = false) => {
            const sortIndex = tableSort.findIndex((criterion) => criterion.id === id);
            const active = sortIndex !== -1;
            const isDragging = draggedColumnId === id;
            const isDragOver = draggable && dragOverColumnId === id && draggedColumnId !== id;
            // Ancho fijo: el ajustado por el usuario o el por defecto de la columna.
            const width = columnWidthOf(id);
            return (
                <th
                    key={id}
                    className={[extraClass, isDragging ? "is-dragging" : "", isDragOver ? "is-drag-over" : ""].filter(Boolean).join(" ")}
                    style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
                    draggable={draggable}
                    onDragStart={draggable ? () => { draggedColumnIdRef.current = id; setDraggedColumnId(id); } : undefined}
                    onDragOver={draggable ? (event) => { event.preventDefault(); setDragOverColumnId(id); } : undefined}
                    onDragLeave={draggable ? () => setDragOverColumnId((current) => (current === id ? null : current)) : undefined}
                    onDrop={draggable ? (event) => {
                        event.preventDefault();
                        const sourceId = draggedColumnIdRef.current;
                        if (sourceId) moveColumn(sourceId, id);
                        draggedColumnIdRef.current = null;
                        setDraggedColumnId(null);
                        setDragOverColumnId(null);
                    } : undefined}
                    onDragEnd={draggable ? () => { draggedColumnIdRef.current = null; setDraggedColumnId(null); setDragOverColumnId(null); } : undefined}
                >
                    <button
                        type="button"
                        className={`obras-table-sort${active ? " active" : ""}${active && tableSort[sortIndex].direction === "desc" ? " desc" : ""}`}
                        onClick={() => toggleTableSort(id)}
                        title={draggable ? "Clic: ordenar (otro clic invierte) · Arrastrar: reordenar columna" : "Clic: ordenar (otro clic invierte)"}
                    >
                        <span>{label}</span>
                        {active
                            ? (tableSort[sortIndex].direction === "desc"
                                ? <FiChevronDown className="obras-table-sort__dir" aria-label="orden descendente" />
                                : <FiChevronUp className="obras-table-sort__dir" aria-label="orden ascendente" />)
                            : <FiChevronDown className="obras-table-sort__hint" aria-hidden="true" />}
                    </button>
                    <div className="obras-col-resize" title="Arrastrar: ancho · Doble clic: ajustar al contenido" onMouseDown={(event) => startColumnResize(id, event)} onDoubleClick={(event) => autofitColumn(id, event)} />
                </th>
            );
        };

	
        const setColumnsVisible = (ids, visible) => setVisibleColumns((current) => (visible
            ? limitVisibleColumns([...current, ...ids])
            : current.filter((columnId) => !ids.includes(columnId))));
	    const toggleColumn = (id) => setVisibleColumns((current) => {
            if (current.includes(id)) return current.filter((columnId) => columnId !== id);
            return limitVisibleColumns([...current, id]);
        });
        // Clic: ordena por esa columna; otro clic en la misma invierte el sentido.
        const toggleTableSort = (id) => setTableSort((current) => (current[0]?.id === id
            ? [{ id, direction: current[0].direction === "asc" ? "desc" : "asc" }]
            : [{ id, direction: "asc" }]));
	    const toggleCategoryValue = (fieldId, value) => setCustomFilters((current) => {
	        const currentValues = current.category[fieldId] || [];
        const nextValues = currentValues.includes(value)
            ? currentValues.filter((item) => item !== value)
            : [...currentValues, value];
        const nextCategory = { ...current.category, [fieldId]: nextValues };
	        if (!nextValues.length) delete nextCategory[fieldId];
	        return { ...current, category: nextCategory };
	    });
	    const setRangeFilter = (type, fieldId, bound, value) => setCustomFilters((current) => {
	        const currentRange = current[type]?.[fieldId] || {};
	        const nextRange = { ...currentRange, [bound]: value };
	        const nextRanges = { ...(current[type] || {}), [fieldId]: nextRange };
	        if ((nextRange.min ?? "") === "" && (nextRange.max ?? "") === "") delete nextRanges[fieldId];
	        return { ...current, [type]: nextRanges };
	    });
	    const clearCategoryField = (fieldId) => setCustomFilters((current) => {
	        const nextCategory = { ...current.category };
	        delete nextCategory[fieldId];
	        return { ...current, category: nextCategory };
	    });
		    const clearRangeField = (type, fieldId) => setCustomFilters((current) => {
		        const nextRanges = { ...(current[type] || {}) };
		        delete nextRanges[fieldId];
		        return { ...current, [type]: nextRanges };
		    });
		    const clearAllFilters = () => setCustomFilters({ category: {}, number: {}, date: {} });
    // Filtro de categoria fijado desde una vista de analisis (clic en pais, proveedor...).
    const applyCategoryFilter = (fieldId, values) => setCustomFilters((current) => ({ ...current, category: withCategoryValues(current.category, fieldId, values) }));
	        const selectAllAllowedColumns = () => setVisibleColumns(limitVisibleColumns(ALL_COLUMN_IDS));
	        const resetDefaultColumns = () => setVisibleColumns(limitVisibleColumns(DEFAULT_VISIBLE_COLUMNS));
	        const applyColumnView = (view) => setVisibleColumns(limitVisibleColumns(view.columns));
        // Vistas de columnas = predefinidas (semilla, no borrables) + las que guarde el usuario.
        const columnViews = [
            ...TABLE_COLUMN_PRESETS.map((preset) => ({ ...preset, builtin: true })),
            ...savedColumnConfigs.map((config) => ({ id: config.id, label: config.name, icon: FiSave, columns: config.columns, builtin: false })),
        ];
        // La vista activa es la que coincide exactamente con las columnas visibles.
        const activeColumnViewId = (() => {
            const current = [...visibleColumns].sort().join("|");
            const match = columnViews.find((view) => [...limitVisibleColumns(view.columns)].sort().join("|") === current);
            return match ? match.id : null;
        })();
        const saveColumnQuickConfig = (rawName) => {
            const name = String(rawName || "").trim().slice(0, MAX_QUICK_CONFIG_NAME_LENGTH);
            if (!name) return;

            setSavedColumnConfigs((current) => {
                const existingIndex = current.findIndex((config) => config.name.toLowerCase() === name.toLowerCase());
                const nextConfig = {
                    id: existingIndex >= 0 ? current[existingIndex].id : `columns-${Date.now()}`,
                    name,
                    columns: visibleColumns,
                    createdAt: existingIndex >= 0 ? current[existingIndex].createdAt : new Date().toISOString(),
                };

                if (existingIndex >= 0) {
                    return current.map((config, index) => index === existingIndex ? nextConfig : config);
                }
                if (current.length >= MAX_QUICK_CONFIGS) {
                    return current;
                }
                return [nextConfig, ...current];
            });
        };
        const deleteColumnQuickConfig = (configId) => {
            setSavedColumnConfigs((current) => current.filter((config) => config.id !== configId));
        };
        const saveFilterQuickConfig = (rawName) => {
            const name = String(rawName || "").trim().slice(0, MAX_QUICK_CONFIG_NAME_LENGTH);
            if (!name) return;

            setSavedFilterConfigs((current) => {
                const existingIndex = current.findIndex((config) => config.name.toLowerCase() === name.toLowerCase());
                const nextConfig = {
                    id: existingIndex >= 0 ? current[existingIndex].id : `filters-${Date.now()}`,
                    name,
                    filters: {
                        search,
                        estado,
                        pais,
                        pmFilter,
                        showArchived,
                        customFilters: normalizeCustomFilters(customFilters),
                    },
                    createdAt: existingIndex >= 0 ? current[existingIndex].createdAt : new Date().toISOString(),
                };

                if (existingIndex >= 0) {
                    return current.map((config, index) => index === existingIndex ? nextConfig : config);
                }
                if (current.length >= MAX_QUICK_CONFIGS) {
                    return current;
                }
                return [nextConfig, ...current];
            });
        };
        const applyFilterQuickConfig = (config) => {
            const nextFilters = config.filters || {};
            setSearch(nextFilters.search || "");
            setEstado(nextFilters.estado || "Todos");
            setPais(nextFilters.pais || "Todos");
            setPmFilter(nextFilters.pmFilter || "Todos");
            setShowArchived(Boolean(nextFilters.showArchived));
            setCustomFilters(normalizeCustomFilters(nextFilters.customFilters));
        };
        const deleteFilterQuickConfig = (configId) => {
            setSavedFilterConfigs((current) => current.filter((config) => config.id !== configId));
        };
        // Vistas de filtros = las que guarde el usuario (combinación completa de filtros).
        const applyFilterView = (view) => applyFilterQuickConfig(view);
        const filterViews = savedFilterConfigs.map((config) => ({ id: config.id, label: config.name, icon: FiSave, filters: config.filters, builtin: false }));
        const filterViewSignature = (source = {}) => JSON.stringify({
            search: source.search || "",
            estado: source.estado || "Todos",
            pais: source.pais || "Todos",
            pmFilter: source.pmFilter || "Todos",
            showArchived: Boolean(source.showArchived),
            customFilters: normalizeCustomFilters(source.customFilters),
        });
        const currentFilterViewSignature = filterViewSignature({ search, estado, pais, pmFilter, showArchived, customFilters });
        const activeFilterViewId = (filterViews.find((view) => filterViewSignature(view.filters) === currentFilterViewSignature) || {}).id || null;

	    const paises = useMemo(
        () => ["Todos", ...Array.from(new Set(obras.map((obra) => obra.pais)))],
        [obras]
    );

    const estados = useMemo(
        () => {
            const ordered = ["Planificada", "En curso", "En riesgo", "Completada", "Bloqueada", "Reserva"];
            const extras = Array.from(new Set(obras.map((obra) => obra.estado))).filter((estado) => estado && !ordered.includes(estado));
            return ["Todos", ...ordered, ...extras];
        },
        [obras]
    );

    const projectManagers = useMemo(
        () => ["Todos", ...Array.from(new Set(obras.map((obra) => obra.pm).filter(Boolean)))],
        [obras]
    );

    // Campos de la ficha que admite la Edición Múltiple (ver bulkEditModel.js), agrupados.
    const bulkFieldGroups = useMemo(() => buildBulkFieldGroups(excelFieldGroups, {
        catalogs,
        responsablesByRol,
        isCalculated: (field) => calculatedCostFields.has(field),
    }).map((group) => ({ ...group, icon: columnGroupIcon(group.label) })), [catalogs, responsablesByRol]);

	    // Distinct values for each categorical filter field, used to build the multi-filter menu.
	    const categoryFilterOptions = useMemo(() => {
	        const options = {};
	        CATEGORY_FILTER_FIELDS.forEach((field) => {
            const values = Array.from(new Set(obras.map((obra) => field.getValue(obra)).filter((value) => value != null && value !== "")))
                .sort((a, b) => String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" }));
            // «No informado» va siempre, el primero, para poder ver las obras sin ese dato. Los
            // campos calculados (situación de hitos) siempre tienen valor: no lo llevan.
            options[field.id] = DERIVED_FILTER_IDS.includes(field.id) ? values : [EMPTY_FILTER_VALUE, ...values];
        });
        return options;
    }, [obras]);

    // La hoja y las vistas filtran con valores diferidos: el control pulsado (casilla,
    // chip, selector) se actualiza en el acto y el recálculo de filas va en un render
    // de baja prioridad que React puede interrumpir si llega otro clic.
    const deferredCustomFilters = useDeferredValue(customFilters);

    const normalizedObraSearch = useMemo(
        () => new Map(obras.map((obra) => [obra.codObra, obraSearchText(obra)])),
        [obras]
    );

    // Filtro de la hoja como dato (búsqueda, selectores rápidos y filtros personalizados):
    // la regla de obraFilter.js. Es lo que guarda un calendario suscrito «por filtro», que
    // el API vuelve a aplicar en cada refresco (las obras nuevas que lo cumplen entran).
    const sheetFilterSpec = useMemo(
        () => obraFilterSpec({ search: deferredSearch, estado, pais, pm: pmFilter, customFilters: deferredCustomFilters, fieldIds: FILTER_FIELD_IDS }),
        [deferredSearch, estado, pais, pmFilter, deferredCustomFilters]
    );
    // El conmutador de archivadas no es parte del filtro: el calendario lleva solo activas.
    const matchesArchived = useCallback((obra) => (showArchived ? obra.activa === false : obra.activa !== false), [showArchived]);
    const matchesSheetSpec = useCallback(
        (obra, spec) => obraMatchesFilter(obra, spec, portfolioToday, normalizedObraSearch.get(obra.codObra) || ""),
        [normalizedObraSearch]
    );

    const contextFilteredObras = useMemo(
        () => obras.filter((obra) => matchesArchived(obra) && matchesSheetSpec(obra, sheetFilterSpec)),
        [obras, matchesArchived, matchesSheetSpec, sheetFilterSpec]
    );

    // Recuento por valor de un campo categórico con el RESTO de filtros aplicados
    // (sin el propio campo): cuántas obras quedarían al marcar cada valor.
    // Se cachea por campo mientras no cambien obras ni filtros (otros renders no recalculan).
    const facetCounts = useMemo(() => {
        const cache = new Map();
        return (fieldId) => {
            if (cache.has(fieldId)) return cache.get(fieldId);
            const field = getFilterField(fieldId);
            if (!field) return new Map();
            const categoria = { ...(sheetFilterSpec.categoria || {}) };
            delete categoria[fieldId];
            const spec = { ...sheetFilterSpec, categoria };
            const counts = countByValue(obras.filter((obra) => matchesArchived(obra) && matchesSheetSpec(obra, spec)), field.getValue);
            cache.set(fieldId, counts);
            return counts;
        };
    }, [sheetFilterSpec, matchesArchived, matchesSheetSpec, obras]);

    const archivedCount = useMemo(
        () => obras.filter((obra) => obra.activa === false).length,
        [obras]
    );
    const activeCount = obras.length - archivedCount;

	    // La hoja muestra también las obras nuevas sin guardar (hay que poder rellenarlas),
	    // pero no son cartera: KPIs, análisis, selección y exportaciones solo cuentan las
	    // que ya existen en la base de datos, para que un alta en blanco no sume.
	    const sheetObras = contextFilteredObras;
	    const filteredObras = useMemo(() => sheetObras.filter((obra) => !obra._isNew), [sheetObras]);
        const sortedFilteredObras = useMemo(
            () => [...sheetObras].sort((a, b) => {
                // Solo las filas aún provisionales se fijan arriba. Al asignar un
                // código de tienda real, pasan inmediatamente a su orden normal.
                const aProvisional = /^NUEVA-/i.test(String(a.codigoTienda || "").trim());
                const bProvisional = /^NUEVA-/i.test(String(b.codigoTienda || "").trim());
                if (aProvisional !== bProvisional) return aProvisional ? -1 : 1;
                return compareTableRows(a, b, tableSort);
            }),
            [sheetObras, tableSort]
        );


    // Fila de totales al pie: cuenta/suma/media según el tipo de cada columna visible,
    // sobre las obras filtradas (no solo las de la página, no hay paginado).
    const tableFooterTotals = useMemo(() => {
        const rows = sheetObras;
        const sum = (getValue) => rows.reduce((acc, obra) => acc + (Number(getValue(obra)) || 0), 0);
        const avg = (getValue) => (rows.length ? sum(getValue) / rows.length : 0);
        const distinctCount = (getValue) => new Set(rows.map(getValue).filter(Boolean)).size;

        const generic = {};
        visibleGenericColumns.forEach((column) => {
            if (column.type !== "number") return;
            generic[column.id] = sum((obra) => obra[column.id]);
        });

        return {
            count: rows.length,
            countries: distinctCount((obra) => obra.pais),
            pms: distinctCount((obra) => obra.pm),
            margenSum: sum((obra) => getCapexDeviation(obra)),
            eurom2Avg: avg((obra) => obra.euroM2Inv),
            bloqSum: sum(hitoAlertCount),
            generic,
        };
    }, [sheetObras, visibleGenericColumns]);

    // ── Selección de filas + acciones en lote (Edición Múltiple) ───────────────
    // Obras marcadas (de entre las filtradas) y ámbito de las vistas de análisis (Resumen,
    // Cronograma, Agenda, CAPEX, Mapa, Proveedores y el calendario suscrito): las marcadas si
    // hay alguna; si no, las filtradas. La hoja sigue mostrando todas las filtradas.
    const { selected: selectedObras, scope: scopeObras } = useMemo(
        () => selectionScope(filteredObras, selectedObraIds),
        [filteredObras, selectedObraIds]
    );
    const selectionCount = selectedObras.length;
    const allFilteredSelected = filteredObras.length > 0 && selectionCount === filteredObras.length;
    const someFilteredSelected = selectionCount > 0 && !allFilteredSelected;

    const toggleObraSelected = useCallback((codObra) => setSelectedObraIds((prev) => {
        const next = new Set(prev);
        if (next.has(codObra)) next.delete(codObra); else next.add(codObra);
        return next;
    }), []);
    const toggleSelectAllFiltered = () => setSelectedObraIds((prev) => {
        if (filteredObras.length > 0 && filteredObras.every((obra) => prev.has(obra.codObra))) return new Set();
        return new Set(filteredObras.map((obra) => obra.codObra));
    });
    const clearSelection = () => setSelectedObraIds(new Set());

    const applyBulkChanges = (changes) => {
        if (!selectionCount) return;
        const ids = new Set(selectedObras.map((obra) => obra.codObra));
        setObras((prev) => prev.map((obra) => (ids.has(obra.codObra) ? { ...obra, ...changes } : obra)));
    };
    // Un cambio masivo deja cada cambio como pendiente (draft) y abre a continuación la
    // caja de guardado, que es su única confirmación: ya no hay que pulsar además
    // "Guardar cambios". Al guardarlo se vacía la selección (clearSelectionOnSaveRef).
    const [bulkSaveRequested, setBulkSaveRequested] = useState(false);
    const clearSelectionOnSaveRef = useRef(false);
    const requestBulkSave = () => {
        clearSelectionOnSaveRef.current = true;
        setBulkSaveRequested(true);
    };
    const applyBulkEdit = (changes) => {
        selectedObras.forEach((obra) => {
            Object.entries(changes).forEach(([field, value]) => {
                stageObraField(obra.codObra, field, value, ALL_FIELD_LABELS[field] || field);
            });
        });
        setBulkEditOpen(false);
        requestBulkSave();
    };
    const applyBulkEstado = (estadoValue) => {
        if (!estadoValue) return;
        selectedObras.forEach((obra) => stageObraField(obra.codObra, "estado", estadoValue, "Estado"));
        requestBulkSave();
    };
    // Archivar/reactivar en lote: pasan por el change-dock (motivo obligatorio) y,
    // en la ruta real, persisten vía API por cada obra (igual que el individual).
    const requestBulkStatusChange = (nextActive) => {
        if (!selectionCount) return;
        setChangeRequest({
            kind: "bulk-status",
            nextActive,
            label: nextActive ? "Reactivar obras" : "Archivar obras",
            obraLabel: `${selectionCount} ${selectionCount === 1 ? "obra" : "obras"}`,
            previousValue: nextActive ? "Archivada" : "Activa",
            newValue: nextActive ? "Activa" : "Archivada",
            source: nextActive ? "REACTIVACION" : "ARCHIVO",
        });
        setChangeReason("");
        setChangeReasonError(false);
    };
    const archiveSelected = () => requestBulkStatusChange(false);
    const reactivateSelected = () => requestBulkStatusChange(true);

    // Vista imprimible de las fichas seleccionadas + diálogo de impresión del navegador.
    // Genera un informe Excel (CSV con formato es-ES) de las obras seleccionadas,
    // con las mismas columnas que el fichero base.
    // Excel con todas las columnas del fichero base (opción aparte de los informes).
    const exportExcelCompleto = () => {
        if (!selectedObras.length) return;
        // Los valores salen del estado real de cada obra (no del JSON mock, vacío en
        // producción) recorriendo el esquema completo del fichero base.
        const columns = EXCEL_COMPLETE_COLUMNS.map((col) => col.label);
        const data = selectedObras.map((obra) => {
            const record = {};
            EXCEL_COMPLETE_COLUMNS.forEach((col) => { record[col.label] = col.get(obra); });
            return record;
        });
        const worksheet = XLSX.utils.json_to_sheet(data, { header: columns });
        worksheet["!cols"] = columns.map((label) => ({ wch: Math.min(Math.max(label.length + 2, 10), 32) }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Obras");
        const stamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Obras_completo_${selectionCount}_${stamp}.xlsx`);
    };

    // Excel de la vista actual: exporta EXACTAMENTE lo que se ve en la hoja —
    // obras filtradas y ordenadas, columnas visibles en el orden actual, con tipos
    // Excel nativos (fechas como fecha, importes como número). Añade una cabecera
    // de trazabilidad para actas/comités con fecha, hora y filtros aplicados.
    // Texto de filtros activos para la cabecera de trazabilidad de los exports (Excel y PDF).
    const activeFiltersLine = useMemo(() => {
        const activeFilterParts = [];
        if (deferredSearch?.trim()) activeFilterParts.push(`Busqueda: "${deferredSearch.trim()}"`);
        if (estado !== "Todos") activeFilterParts.push(`Estado: ${estado}`);
        if (pais !== "Todos") activeFilterParts.push(`Pais: ${pais}`);
        if (pmFilter !== "Todos") activeFilterParts.push(`PM: ${pmFilter}`);
        if (showArchived) activeFilterParts.push("Solo archivadas");
        // Filtros diferidos: los mismos con los que se calcularon las filas exportadas.
        Object.entries(deferredCustomFilters.category || {}).forEach(([fieldId, values]) => {
            if (values?.length) {
                const field = CATEGORY_FILTER_FIELDS.find((f) => f.id === fieldId);
                activeFilterParts.push(`${field?.label || fieldId}: ${values.map(filterValueLabel).join(", ")}`);
            }
        });
        Object.entries(deferredCustomFilters.number || {}).forEach(([fieldId, range]) => {
            if (range?.min !== "" || range?.max !== "") {
                const field = NUMBER_FILTER_FIELDS.find((f) => f.id === fieldId);
                activeFilterParts.push(`${field?.label || fieldId}: ${range.min || "-inf"}..${range.max || "+inf"}`);
            }
        });
        Object.entries(deferredCustomFilters.date || {}).forEach(([fieldId, range]) => {
            if (range?.min !== "" || range?.max !== "") {
                const field = DATE_FILTER_FIELDS.find((f) => f.id === fieldId);
                activeFilterParts.push(`${field?.label || fieldId}: ${range.min || "-inf"}..${range.max || "+inf"}`);
            }
        });
        return activeFilterParts.length ? activeFilterParts.join(" · ") : "Sin filtros aplicados";
    }, [deferredSearch, estado, pais, pmFilter, showArchived, deferredCustomFilters]);

    // Exportar de la pestaña activa: la hoja sale a Excel; el resto de vistas a PDF
    // con lo mismo que se ve en pantalla (ver portfolio/exportPdf.js).
    const tabScrollRef = useRef(null);

    // Columnas congeladas (casilla, #, código, tienda): su desplazamiento "left"
    // sticky se calcula con el ancho REAL de las anteriores. Con anchos fijos en
    // CSS se solapaban al hacer scroll lateral, porque la tabla reparte el ancho
    // sobrante entre todas las columnas (y el usuario puede redimensionarlas).
    const sheetTableRef = useRef(null);
    useLayoutEffect(() => {
        const table = sheetTableRef.current;
        if (!table) return undefined;
        const headerCells = () => Array.from(table.querySelectorAll("thead tr:first-child > th")).slice(0, FROZEN_CELL_COUNT);
        // Cada variable escrita en <table> invalida el estilo de TODAS sus celdas
        // (~4.000 nodos, decenas de ms). Por eso: primero se leen los anchos, no se
        // escribe con la hoja oculta (anchos 0) y solo se escriben los que cambian.
        const update = () => {
            if (table.offsetParent === null) return;
            const widths = headerCells().map((cell) => cell.getBoundingClientRect().width);
            if (!widths.length || widths.every((width) => width === 0)) return;
            let left = 0;
            widths.forEach((width, index) => {
                const value = `${Math.round(left * 100) / 100}px`;
                const name = `--obras-frozen-left-${index + 1}`;
                if (table.style.getPropertyValue(name) !== value) table.style.setProperty(name, value);
                left += width;
            });
        };
        update();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(update);
        headerCells().forEach((cell) => observer.observe(cell));
        return () => observer.disconnect();
        // El observer sigue los cambios de ancho; se reengancha si la tabla se monta de nuevo.
    }, [activeTab, loadState]);

    // ── Anchos de columna fijos (ver sheetColumnWidths.js) ──────────────────────
    // El del usuario o el por defecto de la columna; el contenido nunca los cambia.
    const columnWidthOf = (id) => {
        if (columnWidths[id]) return columnWidths[id];
        const generic = GENERIC_COLUMNS.find((column) => column.id === id);
        return defaultColumnWidth(id, generic ? { type: generic.type, unit: fieldUnits[id] } : {});
    };
    const sheetTableWidth = tableWidth(["check", ...FIXED_DATA_COLUMN_IDS, ...orderedReorderableColumns.map((column) => column.id)], columnWidthOf);


    // ── Renderizado por ventana de la hoja ─────────────────────────────────────
    const sheetWrapRef = useRef(null);
    const pendingSheetFocusRef = useRef(null);
    const [sheetViewport, setSheetViewport] = useState({ top: 0, height: 1200 });
    // Altos medidos por obra (WeakMap por referencia: las obras editadas son objetos
    // nuevos). Densidad y columnas visibles cambian el alto de todas las filas: se
    // descartan. Ancho u orden de columnas (arrastrar un borde genera un cambio por
    // movimiento de ratón) no se invalidan: el efecto de medida corrige lo que cambie.
    const rowHeightsKey = `${rowDensity}|${visibleColumns.join(",")}`;
    const rowHeightsRef = useRef({ key: rowHeightsKey, map: new WeakMap() });
    if (rowHeightsRef.current.key !== rowHeightsKey) rowHeightsRef.current = { key: rowHeightsKey, map: new WeakMap() };
    const [, setRowHeightsVersion] = useState(0);
    const sheetRowHeights = sortedFilteredObras.map((obra) => rowHeightsRef.current.map.get(obra));
    const sheetRowEstimate = SHEET_ROW_ESTIMATE_PX[rowDensity] || 48;
    const sheetWindow = computeRowWindow({
        heights: sheetRowHeights,
        scrollTop: sheetViewport.top,
        viewportHeight: sheetViewport.height,
        overscan: SHEET_OVERSCAN_PX,
        fallbackHeight: sheetRowEstimate,
    });
    const readSheetViewport = () => {
        const wrap = sheetWrapRef.current;
        if (!wrap) return null;
        const headHeight = sheetTableRef.current?.tHead?.offsetHeight || 0;
        return { top: Math.max(0, wrap.scrollTop - headHeight), height: wrap.clientHeight };
    };
    const handleSheetScroll = () => {
        const next = readSheetViewport();
        if (next) setSheetViewport((previous) => (shouldUpdateViewport(previous, next, SHEET_SCROLL_STEP_PX) ? next : previous));
    };
    // Vuelve a la primera obra de la lista. Solo en vertical: se mantiene el scroll
    // horizontal para no perder la columna que se estaba mirando.
    const scrollSheetToTop = () => {
        const wrap = sheetWrapRef.current;
        if (!wrap) return;
        const reduceMotion = typeof window !== "undefined"
            && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        wrap.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    };
    // Alto visible del contenedor (cambia con la ventana, el zoom o al mostrar la hoja).
    useEffect(() => {
        const wrap = sheetWrapRef.current;
        if (!wrap) return undefined;
        const sync = () => {
            const next = readSheetViewport();
            if (next && next.height > 0) setSheetViewport((previous) => (shouldUpdateViewport(previous, next, 1) ? next : previous));
        };
        sync();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(sync);
        observer.observe(wrap);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheetMounted, loadState]);
    // Si la lista cambia (filtro, orden) estando abajo, el navegador recorta el scroll:
    // se relee antes de pintar para no mostrar un fotograma con la zona en blanco.
    useLayoutEffect(() => {
        pendingSheetFocusRef.current = null;
        if (activeTab !== "obras") return;
        const next = readSheetViewport();
        if (next && next.height > 0) setSheetViewport((previous) => (shouldUpdateViewport(previous, next, 1) ? next : previous));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedFilteredObras]);
    // Tras pintar: mide las filas visibles y, si alguna difiere de lo guardado, vuelve a
    // calcular los espaciadores (una pasada; con las medidas ya estables no repite).
    // Sin dependencias a propósito: debe medir tras cada render de la hoja; solo llama a
    // setRowHeightsVersion si una altura cambia, así que no entra en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (activeTab !== "obras") return;
        const table = sheetTableRef.current;
        if (!table || table.offsetParent === null) return;
        const map = rowHeightsRef.current.map;
        let changed = false;
        table.querySelectorAll("tbody tr[data-row-index]").forEach((tr) => {
            const obra = sortedFilteredObras[Number(tr.dataset.rowIndex)];
            const height = tr.getBoundingClientRect().height;
            if (obra && height > 0 && Math.abs((map.get(obra) || 0) - height) > 0.5) {
                map.set(obra, height);
                changed = true;
            }
        });
        if (changed) setRowHeightsVersion((version) => version + 1);
        const pending = pendingSheetFocusRef.current;
        if (pending) {
            const active = document.activeElement;
            const stillInSheet = !active || active === document.body || sheetWrapRef.current?.contains(active);
            if (!stillInSheet) pendingSheetFocusRef.current = null;
            else if (focusSheetRow(pending.index, pending.cellIndex)) pendingSheetFocusRef.current = null;
        }
    });
    const focusSheetRow = (index, cellIndex) => {
        const tr = sheetTableRef.current?.querySelector(`tbody tr[data-row-index="${index}"]`);
        if (!tr) return false;
        const focusable = tr.cells[cellIndex]?.querySelector("button, input, a, [tabindex]") || tr.querySelector("button, input");
        if (!focusable) return false;
        focusable.focus({ preventScroll: true });
        focusable.scrollIntoView({ block: "nearest", inline: "nearest" });
        return true;
    };
    const [tabExporting, setTabExporting] = useState(false);
    const exportActiveTab = async () => {
        if (activeTab === "obras") { exportExcelVista(); return; }
        const tabLabel = OBRAS_TABS.find((tab) => tab.id === activeTab)?.label || "Vista";
        setTabExporting(true);
        try {
            await exportElementToPdf(tabScrollRef.current, {
                viewLabel: tabLabel,
                title: `Obras · ${tabLabel}`,
                subtitle: activeTab === "resumen"
                    ? `${scopeObras.length} obra${scopeObras.length === 1 ? "" : "s"} · CAPEX ${moneyCompact(portfolioModel.totals.totalCapex)}`
                    : `${analysisModel.totals.projects} obra${analysisModel.totals.projects === 1 ? "" : "s"} · CAPEX ${moneyCompact(analysisModel.totals.totalCapex)}`,
                filtersLine: selectionCount
                    ? `Solo las ${selectionCount} obra${selectionCount === 1 ? "" : "s"} marcada${selectionCount === 1 ? "" : "s"} en la hoja`
                    : analysisIgnoresFilters ? "Filtros de la hoja desactivados en esta vista" : activeFiltersLine,
            });
        } catch (error) {
            console.error("No se pudo exportar la vista a PDF", error);
            showNotice({
                title: "No se pudo exportar",
                message: "Ha ocurrido un problema al generar el PDF de la vista. Inténtalo de nuevo.",
                tone: "danger",
            });
        } finally {
            setTabExporting(false);
        }
    };

    const exportExcelVista = () => {
        if (!filteredObras.length) return;

        // Especificación de tipo por columna reordenable (para tipos Excel nativos).
        const REORDERABLE_EXPORT_SPECS = {
            ciudad: { label: "Ubicación", type: "text", get: (o) => o.ciudad },
            pais: { label: "País", type: "text", get: (o) => o.pais },
            estado: { label: "Estado", type: "text", get: (o) => o.estado },
            apertura: { label: "Apertura", type: "date", get: (o) => o.apertura },
            margen: { label: "Desviación CAPEX", type: "number", get: (o) => getCapexDeviation(o) },
            eurom2: { label: "EUR/m2", type: "number", get: (o) => Number(o.euroM2Inv || 0) },
            bloq: { label: "Alertas de hitos", type: "number", get: hitoAlertCount },
            pm: { label: "PM", type: "text", get: (o) => o.pm },
        };

        // Columnas visibles, en el mismo orden que en pantalla (fijas + reordenables).
        const exportColumns = [
            { id: "codigo", label: "Código", type: "text", get: (o) => o.codigoTienda },
            { id: "pais", ...REORDERABLE_EXPORT_SPECS.pais },
            { id: "ciudad", ...REORDERABLE_EXPORT_SPECS.ciudad },
            { id: "tienda", label: "Tienda", type: "text", get: (o) => o.tienda },
            ...orderedReorderableColumns.map((column) => {
                if (REORDERABLE_EXPORT_SPECS[column.id]) return { id: column.id, ...REORDERABLE_EXPORT_SPECS[column.id] };
                const generic = GENERIC_COLUMNS.find((c) => c.id === column.id);
                return { id: column.id, label: generic?.label || column.id, type: generic?.type || "text", get: (o) => o[column.id] };
            }),
        ];

        // Convierte un valor al tipo Excel nativo esperado (Date, Number, String o null).
        const toCell = (rawValue, type) => {
            if (rawValue == null || rawValue === "") return null;
            if (type === "date") {
                const parsed = dateFromIso(rawValue);
                return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
            }
            if (type === "number") {
                const n = Number(rawValue);
                return Number.isFinite(n) ? n : null;
            }
            return String(rawValue);
        };

        // Cabecera de trazabilidad: fecha/hora del export + filtros aplicados, para
        // que el fichero adjunto a un acta/comité sea auditable por si solo.
        const sortDescription = tableSort.map((c) => `${c.id} ${c.direction === "desc" ? "↓" : "↑"}`).join(", ");
        const filtersLine = activeFiltersLine;
        const now = new Date();
        const nowIso = now.toISOString().replace("T", " ").slice(0, 16);

        // AOA (array-of-arrays) para poder mezclar cabecera de metadatos y datos.
        const aoa = [
            [`Vista de Obras - ${filteredObras.length} obra${filteredObras.length === 1 ? "" : "s"} - exportado ${nowIso}`],
            [`Filtros: ${filtersLine}`],
            [`Orden: ${sortDescription || "(por defecto)"}`],
            [],
            exportColumns.map((col) => col.label),
            ...sortedFilteredObras.map((obra) => exportColumns.map((col) => toCell(col.get(obra), col.type))),
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

        // Anchos de columna proporcionales al header (con topes razonables).
        worksheet["!cols"] = exportColumns.map((col) => ({ wch: Math.min(Math.max(col.label.length + 2, 12), 32) }));

        // Formato de fecha legible en Excel para las columnas de tipo date.
        // (SheetJS necesita `z` en la celda; recorremos por columna y aplicamos.)
        const dateColIndexes = exportColumns
            .map((col, idx) => (col.type === "date" ? idx : -1))
            .filter((idx) => idx >= 0);
        const headerRowIdx = 4;
        dateColIndexes.forEach((colIdx) => {
            for (let r = headerRowIdx + 1; r < aoa.length; r++) {
                const cellAddr = XLSX.utils.encode_cell({ r, c: colIdx });
                const cell = worksheet[cellAddr];
                if (cell && cell.v instanceof Date) {
                    cell.t = "d";
                    cell.z = "dd/mm/yyyy";
                }
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Vista Obras");
        const stamp = now.toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Obras_vista_${filteredObras.length}_${stamp}.xlsx`, { cellDates: true });
    };

    // Genera y DESCARGA un PDF del informe (KPIs + gráficos) sin diálogo de impresión.
    const exportReportPdf = async (kind) => {
        const meta = REPORT_META[kind];
        const html = buildObraReport(kind, selectedObras);
        if (!meta || !html) return;
        // Renderiza el informe en un iframe oculto y lo captura a canvas → PDF.
        const iframe = document.createElement("iframe");
        iframe.setAttribute("aria-hidden", "true");
        iframe.style.cssText = "position:fixed;left:-10000px;top:0;width:1123px;height:794px;border:0;background:#fff;";
        document.body.appendChild(iframe);
        const cleanup = () => { iframe.remove(); };
        try {
            await new Promise((resolve) => { iframe.onload = resolve; iframe.srcdoc = html; });
            await new Promise((resolve) => setTimeout(resolve, 200));
            const doc = iframe.contentDocument;
            const target = doc.body;
            const canvas = await html2canvas(target, {
                scale: 2,
                backgroundColor: "#ffffff",
                windowWidth: 1123,
                width: 1123,
                height: target.scrollHeight,
            });
            const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const imgW = pageW;
            const imgH = (canvas.height * imgW) / canvas.width;
            const imgData = canvas.toDataURL("image/jpeg", 0.92);
            if (imgH <= pageH) {
                pdf.addImage(imgData, "JPEG", 0, 0, imgW, imgH);
            } else {
                let position = 0;
                let remaining = imgH;
                while (remaining > 0) {
                    pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
                    remaining -= pageH;
                    if (remaining > 0) { pdf.addPage(); position -= pageH; }
                }
            }
            const stamp = new Date().toISOString().slice(0, 10);
            pdf.save(`${meta.column === "economico" ? "Informe_economico" : meta.column === "temporal" ? "Informe_temporal" : "Informe_proveedores"}_${selectionCount}_${stamp}.pdf`);
        } catch (error) {
            requestConfirm({
                title: "No se pudo generar el informe",
                message: "Ha ocurrido un problema al generar el PDF. Inténtalo de nuevo.",
                confirmLabel: "Entendido",
                cancelLabel: null,
                tone: "danger",
            });
        } finally {
            cleanup();
        }
    };

    const exportReport = (kind) => {
        if (!selectionCount) return;
        setReportMenuOpen(false);
        if (kind === "excel") exportExcelCompleto();
        else exportReportPdf(kind);
    };

    // Eliminación definitiva (solo desde Archivadas), con justificación en el change-dock.
    const requestBulkDelete = () => {
        if (!selectionCount) return;
        setChangeRequest({
            kind: "bulk-delete",
            label: `${selectionCount} ${selectionCount === 1 ? "obra archivada" : "obras archivadas"}`,
            obraLabel: "Eliminación definitiva",
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    const selectedObra = selectedId
        ? obras.find((obra) => obra.codObra === selectedId) || null
        : null;

    const selectedAuditEntries = selectedId
        ? auditEntries.filter((entry) => entry.codObra === selectedId)
        : [];

    const detailFieldMatches = useMemo(() => {
        const normalized = normalizeSearchValue(detailFieldSearch.trim());
        if (!normalized) return [];

        return DETAIL_SEARCH_INDEX
            .filter((item) => item.searchText.includes(normalized))
            .slice(0, 8);
    }, [detailFieldSearch]);

    useEffect(() => {
        if (!highlightedDetailField || ["historial", "cronograma", "agenda"].includes(detailTab)) return;

        const timer = window.setTimeout(() => {
            const target = document.getElementById(`obra-field-${highlightedDetailField}`);
            target?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);

        const clearTimer = window.setTimeout(() => {
            setHighlightedDetailField((current) =>
                current === highlightedDetailField ? null : current
            );
        }, 2600);

        return () => {
            window.clearTimeout(timer);
            window.clearTimeout(clearTimer);
        };
    }, [detailTab, highlightedDetailField]);

    const summarizeObras = (list) => {
        const totalInversion = list.reduce(
            (acc, obra) => acc + Number(obra.totalInversion || 0),
            0
        );
        const totalSupVta = list.reduce(
            (acc, obra) => acc + Number(obra.supVta || 0),
            0
        );
        const riesgos = list.filter(isObraAtRisk).length;
        const pendientes = list.reduce(
            (acc, obra) =>
                acc +
                obra.secciones.filter((section) =>
                    section.estado === "Riesgo" || (section.estado === "Pendiente" && !!section.deadline)
                ).length,
            0
        );

        return {
            total: list.length,
            totalInversion,
            euroM2: totalSupVta ? totalInversion / totalSupVta : 0,
            riesgos,
            pendientes,
        };
    };
    // Cifras de la hoja (obras filtradas) y del Resumen (obras del ámbito).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const summary = useMemo(() => summarizeObras(filteredObras), [filteredObras]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const scopeSummary = useMemo(() => (selectionCount ? summarizeObras(scopeObras) : summary), [selectionCount, scopeObras, summary]);

    const portfolioModel = useMemo(() => buildPortfolioModel(scopeObras), [scopeObras]);
    // Modelo de la vista de analisis activa: hereda los filtros de la hoja salvo que
    // el usuario los haya desactivado para esa vista (entonces, todas las obras activas).
    // Con obras marcadas, la vista trabaja con ellas: el ajuste «Sin filtros de la hoja» no aplica.
    const analysisIgnoresFilters = !selectionCount && OBRAS_ANALYSIS_TAB_IDS.has(activeTab) && Boolean(ignoreSheetFilters[activeTab]);
    const unfilteredActiveObras = useMemo(() => obras.filter((obra) => obra.activa !== false && !obra._isNew), [obras]);
    const unfilteredPortfolioModel = useMemo(
        () => (analysisIgnoresFilters ? buildPortfolioModel(unfilteredActiveObras) : null),
        [analysisIgnoresFilters, unfilteredActiveObras]
    );
    const analysisModel = analysisIgnoresFilters ? unfilteredPortfolioModel : portfolioModel;

    // Qué guarda un calendario suscrito desde la Agenda. Con obras marcadas, esa lista fija;
    // si no, el filtro de la hoja, que el API vuelve a aplicar en cada refresco (las obras
    // nuevas que lo cumplen entran solas). El conmutador de archivadas no se guarda: el
    // calendario solo lleva obras activas.
    const calendarScope = useMemo(() => {
        if (selectionCount) return { modo: "seleccion" };
        if (analysisIgnoresFilters) return { modo: "filtro", filtro: { v: sheetFilterSpec.v }, resumen: "Todas las obras activas" };
        if (showArchived) return { modo: "filtro", error: "Estás viendo las obras archivadas y el calendario solo lleva obras activas: vuelve a las activas para crearlo." };
        return { modo: "filtro", filtro: sheetFilterSpec, resumen: isEmptyObraFilter(sheetFilterSpec) ? "Todas las obras activas" : activeFiltersLine };
    }, [selectionCount, analysisIgnoresFilters, showArchived, sheetFilterSpec, activeFiltersLine]);

    const portfolioHealth = useMemo(() => {
        if (!scopeObras.length) {
            return {
                readyIndex: 0,
                peakLoad: 0,
                peakLoadMonth: "-",
                alertCount: 0,
                capexMargin: 0,
                capexPercent: 0,
                blockedCapex: 0,
                providerConcentration: 0,
                topProviderName: "Sin proveedor",
                blockers: 0,
                dueSoon: 0,
                confidence: 0,
            };
        }

        const count = scopeObras.length || 1;
        const readyIndex = scopeObras.reduce((acc, obra) => {
            const totalSections = obra.secciones.length || 1;
            const completed = obra.secciones.filter((section) => section.estado === "Completado").length;
            return acc + ((completed / totalSections) * 100);
        }, 0) / count;
        const loadYear = portfolioToday.getFullYear();
        const monthlyLoad = monthLabels.map((label, month) => {
            const openings = scopeObras.filter((obra) => {
                const opening = dateFromIso(obra.apertura);
                return opening && opening.getFullYear() === loadYear && opening.getMonth() === month;
            }).length;
            const starts = scopeObras.filter((obra) => {
                const start = dateFromIso(obra.inicioObra);
                return start && start.getFullYear() === loadYear && start.getMonth() === month;
            }).length;

            return { label, load: openings + starts, openings };
        });
        const peakLoad = monthlyLoad.reduce((max, item) => item.load > max.load ? item : max, monthlyLoad[0]);
        const approvedCapex = scopeObras.reduce(
            (acc, obra) => acc + Number(obra.capex || 0),
            0
        );
        const capexMargin = approvedCapex - scopeSummary.totalInversion;
        const blockers = scopeObras.reduce(
            (acc, obra) => acc + blockingHitoAlerts(obra).length,
            0
        );
        // Hitos críticos pendientes (sin confirmar) que vencen en los próximos 14 días.
        const dueSoon = scopeObras.reduce(
            (acc, obra) => acc + obraHitos(obra, portfolioToday).filter((hito) => {
                if (hito.status !== "pendiente") return false;
                const days = (new Date(`${hito.date}T12:00:00`) - mockToday) / 86400000;
                return days >= 0 && days <= 14;
            }).length,
            0
        );
        const confidence = Math.max(
            38,
            Math.min(96, 88 - blockers * 7 - scopeSummary.riesgos * 8 + dueSoon * 0.5)
        );
        const highRiskRatio = (scopeSummary.riesgos / count) * 100;
        const alertCount = [
            peakLoad.openings > count * 0.3,
            approvedCapex && Math.abs(capexMargin / approvedCapex) > 0.05,
            portfolioModel.totals.providerConcentration > 40,
            highRiskRatio > 15,
            blockers > 0,
        ].filter(Boolean).length;

        return {
            readyIndex,
            peakLoad: peakLoad.load,
            peakLoadMonth: peakLoad.label,
            loadYear,
            alertCount,
            capexMargin,
            capexPercent: approvedCapex ? (capexMargin / approvedCapex) * 100 : 0,
            blockedCapex: portfolioModel.totals.blockedCapex,
            providerConcentration: portfolioModel.totals.providerConcentration,
            topProviderName: portfolioModel.totals.topProvider?.provider || "Sin proveedor",
            blockers,
            dueSoon,
            confidence,
        };
        }, [scopeObras, portfolioModel.totals.blockedCapex, portfolioModel.totals.providerConcentration, portfolioModel.totals.topProvider, scopeSummary.riesgos, scopeSummary.totalInversion]);

    // Alertas de hitos críticos de la cartera filtrada (una fila por hito).
    const hitoAlertRows = useMemo(() => listHitoAlerts(scopeObras, portfolioToday), [scopeObras]);
    const hitoAlertCounts = useMemo(() => hitoAlertRows.reduce((acc, { hito }) => {
        acc[hito.status] = (acc[hito.status] || 0) + 1;
        return acc;
    }, {}), [hitoAlertRows]);

    const attentionObras = useMemo(() => scopeObras
        .map((obra) => {
            const alerts = hitoAlertsOf(obra);
            const blockers = alerts.filter((hito) => hito.tone === "danger").length;
            const needsData = alerts.length - blockers;
            const capexDelta = Number(obra.totalInversion || 0) - Number(obra.capex || 0);
            const score = (isObraAtRisk(obra) ? 100 : 0) + blockers * 24 + needsData * 8 + (capexDelta > 0 ? 18 : 0);
            const reason = alerts.length
                ? alerts.length === 1
                    ? `${alerts[0].label}: ${alerts[0].statusLabel.toLowerCase()}`
                    : `${alerts.length} hitos con alerta (${alerts.map((hito) => hito.label).join(", ")})`
                : capexDelta > 0
                    ? `${moneyCompact(capexDelta)} sobre CAPEX`
                    : "Revisión operativa";
            return { obra, score, reason, tone: getRiskTone(obra) };
        })
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score || Number(b.obra.capex || 0) - Number(a.obra.capex || 0))
        .slice(0, 4), [scopeObras]);

    // Obra "efectiva" = original + cambios en borrador, con secciones y totales
    // recalculados. Así los KPIs (hitos, cronograma, financiero) reaccionan en
    // vivo a cada edición sin esperar a guardar.
    const selectedDraftObra = useMemo(() => {
        if (!selectedObra) return null;
        const merged = { ...selectedObra };
        Object.values(draftEdits).forEach((draft) => {
            if (draft.codObra !== selectedObra.codObra) return;
            merged[draft.field] = numericFields.has(draft.field)
                ? parseLocaleNumber(draft.newValue)
                : draft.newValue;
        });
        merged.secciones = buildObraSections(merged).map((section) => {
            const physical = PHYSICAL_DELIVERABLES.find((item) => item.seccion === section.seccion);
            if (!physical) return section;
            const bufferDays = getBufferDays(merged, physical.bufferField);
            const effectiveDeadline = section.deadline ? addDaysToIso(section.deadline, bufferDays) : "";
            return {
                ...section,
                bufferDays,
                effectiveDeadline,
                // El estado (riesgo/plazo) se mide contra la fecha lista para montaje.
                estado: !section.estadoRegistrado && section.deadline ? sectionStatus(effectiveDeadline, merged.apertura) : section.estado,
            };
        });
        return recalculateDerivedCosts(merged);
    }, [selectedObra, draftEdits]);
    // Ficha con sus cambios pendientes recalculada (totales y días), solo si los hay: sin
    // cambios se enseña lo que devuelve la BD tal cual.
    const selectedPreviewObra = useMemo(() => {
        if (!selectedObra || !selectedDraftObra) return null;
        if (!Object.values(draftEdits).some((draft) => draft.codObra === selectedObra.codObra)) return null;
        const recalculated = recalculateDerivedCosts(selectedDraftObra);
        const { inicioObra, apertura } = recalculated;
        return { ...recalculated, dias: inicioObra && apertura ? daysBetween(inicioObra, apertura) : recalculated.dias };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDraftObra]);


    const addObra = () => {
        const nextId = nextTempObraId(obras);
        // Obra en blanco: sin valores de ejemplo (imagen, iluminación, proveedores...) que
        // se guardarían como datos reales. Solo estado inicial y días de gestión por defecto.
        const newObra = recalculateDerivedCosts({
            codObra: nextId,
            codigoTienda: `NUEVA-${-nextId}`,
            tienda: "",
            pais: "",
            ciudad: "",
            estado: "Planificada",
            activa: true,
            inicioObra: "",
            apertura: "",
            dias: 0,
            bufMobiliario: 7,
            bufIluminacion: 7,
            bufSuelo: 7,
            costes: [],
        });
        newObra.secciones = buildObraSections(newObra);

        // _isNew marca la obra como alta pendiente: al guardarla en la ruta real
        // se envía con codObra 0 (POST) y se reemplaza por la ficha del backend. Si se
        // cierra sin nombre de tienda, se descarta (newObraCloseAction).
        setObras((current) => [{ ...newObra, _isNew: true }, ...current]);
        // Abrir la ficha de la obra recién creada da feedback inmediato (antes se
        // insertaba la fila en silencio y parecía que el botón no hacía nada) y
        // deja al usuario listo para rellenar sus datos.
        openDetail(nextId);
    };

    // Duplicar obra: alta nueva (pendiente de guardar) con los datos de otra obra,
    // salvo identidad, estado, fechas y notas, que son propios de cada apertura. No se
    // persiste hasta guardar y, si se cierra sin ningún cambio, se descarta siempre. Al
    // guardarla, su historial registra de qué obra se duplicó (_duplicatedFrom).
    const DUPLICATE_RESET_FIELDS = ["inicioObra", "apertura", "fechaCierre", "ventanaInicio", "ventanaFin", "zonif", "legal", "planoP3", "deadlineMob", "fechaIluminacion", "fechaSuelo", "p3Constructora", "p3Mobiliario", "inicioMontajeMob", "finMontajeMob", "zonifReal", "legalReal", "planoP3Real", "deadlineMobReal", "p3ConstructoraReal", "p3MobiliarioReal", "zonifNotas", "legalNotas", "planoP3Notas", "deadlineMobNotas", "p3ConstructoraNotas", "p3MobiliarioNotas"];
    const duplicateObra = (source) => {
        if (!source) return;
        const nextId = nextTempObraId(obras);
        const copy = { ...source };
        ["rowVersion", "_sourceIndex", "fechaAlta", "fechaModificacion", "usuarioAlta", "usuarioModificacion"].forEach((field) => { delete copy[field]; });
        DUPLICATE_RESET_FIELDS.forEach((field) => { if (field in copy) copy[field] = ""; });
        // Los estados de hito se quitan (no se envían): el API rechaza un estado nulo y una
        // obra nueva no hereda el avance de la original.
        Object.keys(copy).filter((field) => /Estado$/.test(field)).forEach((field) => { delete copy[field]; });
        const duplicated = {
            ...copy,
            codObra: nextId,
            codigoTienda: `NUEVA-${-nextId}`,
            tienda: `${source.tienda || "OBRA"} (copia)`,
            estado: "Planificada",
            activa: true,
            dias: 0,
            observaciones: "",
            liquidacionNotas: "",
        };
        duplicated.secciones = buildObraSections(duplicated);
        const origin = { codObra: source.codObra, codigoTienda: source.codigoTienda || "", tienda: source.tienda || "" };
        setObras((current) => [{ ...duplicated, _isNew: true, _duplicatedFrom: origin }, ...current]);
        openDetail(nextId);
    };

    const requestObraStatusChange = (obra, nextActive) => {
        setChangeRequest({
            kind: "obra-status",
            key: `obra-status:${obra.codObra}`,
            codObra: obra.codObra,
            field: "obra",
            label: nextActive ? "Reactivar obra" : "Archivar obra",
            obraLabel: `${obra.codigoTienda} - ${obra.tienda}`,
            previousValue: nextActive ? "Archivada" : "Activa",
            newValue: nextActive ? "Activa" : "Archivada",
            nextActive,
            source: nextActive ? "REACTIVACION" : "ARCHIVO",
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    // Una obra puede guardar una grafía antigua del mismo valor ("Grupo Atlante" por
    // "ATLANTE"): se compara por el valor canónico o el recuento se queda corto y se
    // ofrecería borrar un valor que sí está en uso.
    const sameCatalogValue = (catalog, a, b) => {
        const key = (value) => catalogKey(canonicalCatalogValue(catalog, value));
        return Boolean(key(a)) && key(a) === key(b);
    };

    const getCatalogUsage = (catalog, entry) =>
        obras.filter((obra) => {
            const matchesValue = (CATALOG_FIELDS[catalog] || [catalog]).some((field) =>
                sameCatalogValue(catalog, obra[field], entry.value));
            const matchesParent =
                catalog !== "ciudad" || !entry.parent || obra.pais === entry.parent;
            return matchesValue && matchesParent;
        }).length;

    // Un proveedor se guarda en dbo.Empresas (tipo 'empresa' del API), no en el catálogo.
    // Studio: el API lo da de alta como empresa de tipo 4 (estudio); el resto de proveedores, como proveedor.
    const catalogApiType = (catalog) => (catalog === "studio" ? "studio" : EMPRESA_CATALOG_TYPES.has(catalog) ? "empresa" : catalog);

    const registerCatalogOption = async (obra, field, catalog, rawValue, label) => {
        const value = rawValue.trim();
        const parent = catalog === "ciudad" ? (draftEdits[getEditKey(obra.codObra, "pais")]?.newValue ?? obra.pais) : null;
        if (catalog === "ciudad" && !parent) return { status: "error", message: "Selecciona primero un país." };
        const existing = catalogs[catalog].find((entry) =>
            normalizeSearchValue(entry.value) === normalizeSearchValue(value)
            && (entry.parent || null) === parent
        );

        if (existing && !existing.active) {
            return { status: "inactive", entry: existing };
        }

        if (!useLocalData && (catalog === "pais" || catalog === "ciudad")) {
            if (catalog === "ciudad" && !existing) {
                const selectedCountry = catalogs.pais.find((entry) => entry.value === parent && entry.active);
                setGeographyManager({ country: selectedCountry?.id || "", codObra: obra.codObra });
                return { status: "opened" };
            }
            if (!existing) {
                const country = catalogs.pais.find(entry => entry.value === parent && entry.active);
                if (catalog === "ciudad" && !country) throw new Error("Selecciona un país activo.");
                await guardarGeografia(catalog, { valor: value, codPais: country?.id, motivo: "Alta desde ficha de obra" });
                const geo = await fetchGeografia();
                setCatalogs(current => ({ ...current, ...geo }));
            }
            stageObraField(obra.codObra, field, existing?.value || value, label);
            return { status: "created" };
        }
        const registeredValue = existing?.value || value;

        setCatalogs((current) => ({
            ...current,
            [catalog]: existing
                ? current[catalog]
                : [
                    ...current[catalog],
                    {
                        id: `${catalog}-custom-${Date.now()}`,
                        value: registeredValue,
                        parent,
                        active: true,
                    },
                ],
        }));

        stageObraField(obra.codObra, field, registeredValue, label);

        // En la ruta real, un valor nuevo de un catálogo de BD se persiste de
        // inmediato (no bloqueante) y se sustituye su id temporal por el real,
        // para que activar/desactivar/eliminar funcionen sin recargar.
        if (!existing && !useLocalData && (DB_CATALOG_TYPES.has(catalog) || EMPRESA_CATALOG_TYPES.has(catalog))) {
            (async () => {
                try {
                    const saved = await guardarCatalogo(catalogApiType(catalog), 0, registeredValue, "Alta de valor desde ficha");
                    if (saved?.codCatalogo) {
                        setCatalogs((current) => ({
                            ...current,
                            [catalog]: current[catalog].map((entry) =>
                                entry.value === registeredValue && String(entry.id).startsWith(`${catalog}-custom-`)
                                    ? { ...entry, id: Number(saved.codCatalogo) }
                                    : entry
                            ),
                        }));
                    }
                } catch (err) {
                    console.error("No se pudo persistir el valor de catálogo", err);
                }
            })();
        }

        return { status: existing ? "active" : "created", entry: existing || null };
    };

    const requestCatalogStatusChange = ({
        obra,
        field,
        catalog,
        entry,
        nextActive,
        selectAfter,
        label,
    }) => {
        const usageCount = getCatalogUsage(catalog, entry);
        const actionLabel = nextActive ? "Reactivar valor" : "Deshabilitar valor";

        setChangeRequest({
            kind: "catalog",
            catalogAction: nextActive ? "reactivate" : "deactivate",
            key: `catalog:${catalog}:${entry.id}`,
            codObra: obra.codObra,
            field,
            catalog,
            catalogEntryId: entry.id,
            catalogValue: entry.value,
            label: `${label} - catalogo`,
            obraLabel: `${actionLabel} - ${entry.value}`,
            previousValue: nextActive ? "Deshabilitado" : "Activo",
            newValue: nextActive ? "Activo" : "Deshabilitado",
            assignedPreviousValue: obra[field] ?? "",
            nextActive,
            selectAfter,
            usageCount,
            source: "CATALOGO",
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    // Renombrar un valor de catálogo: pide justificación y lo aplica a todas las obras.
    const requestCatalogRename = ({ obra, field, catalog, entry, label, value }) => {
        setCatalogManager(null);
        setChangeRequest({
            kind: "catalog",
            catalogAction: "rename",
            key: `catalog:${catalog}:${entry.id}`,
            codObra: obra.codObra,
            field,
            catalog,
            catalogEntryId: entry.id,
            catalogValue: entry.value,
            label: `${label} - catalogo`,
            obraLabel: `Renombrar valor - ${entry.value}`,
            previousValue: entry.value,
            newValue: value,
            usageCount: getCatalogUsage(catalog, entry),
            source: "CATALOGO",
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    const requestCatalogDelete = ({
        obra,
        field,
        catalog,
        entry,
        label,
    }) => {
        const usageCount = getCatalogUsage(catalog, entry);
        if (usageCount > 0) return;

        setCatalogManager(null);
        setChangeRequest({
            kind: "catalog",
            catalogAction: "delete",
            key: `catalog:${catalog}:${entry.id}`,
            codObra: obra.codObra,
            field,
            catalog,
            catalogEntryId: entry.id,
            catalogValue: entry.value,
            label: `${label} - catalogo`,
            obraLabel: `Eliminar valor - ${entry.value}`,
            previousValue: entry.active ? "Activo" : "Deshabilitado",
            newValue: "Eliminado",
            assignedPreviousValue: obra[field] ?? "",
            usageCount,
            source: "CATALOGO",
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    const applyObraField = (codObra, field, rawValue) => {
        setObras((current) =>
            current.map((obra) => {
                if (obra.codObra !== codObra) return obra;

                const parsedValue = numericFields.has(field)
                    ? parseLocaleNumber(rawValue)
                    : rawValue;
                const value = numericFields.has(field)
                    ? parsedValue === ""
                        ? ""
                        : roundToTwoDecimals(parsedValue)
                    : rawValue;
                const updated = { ...obra, [field]: value };

                // Se aplican cambios ya guardados (tecleados y deducidos): el triángulo
                // del CAPEX no se vuelve a calcular aquí, solo los totales.
                return recalculateDerivedCosts(updated);
            })
        );
    };

    const stageObraField = (codObra, field, rawValue, label) => {
        if (!canEditObraField(role, field)) return;
        const obra = obras.find((item) => item.codObra === codObra);
        if (!obra) return;

        const key = getEditKey(codObra, field);
        const originalValue = obra[field] ?? "";

        setDraftEdits((current) => {
            if (field === "pais" && String(current[key]?.newValue ?? originalValue) !== String(rawValue)) {
                const cityKey = getEditKey(codObra, "ciudad");
                current = { ...current };
                // Al cambiar de país se borra la ubicación, que era de otro país. Si la obra
                // no tenía ninguna (un alta), no hay nada que borrar: sin cambio fantasma
                // "Ciudad: sin dato -> sin dato" en el panel ni en el historial.
                if (String(rawValue) === String(originalValue) || !String(obra.ciudad ?? "")) delete current[cityKey];
                else current[cityKey] = { key: cityKey, codObra, field: "ciudad", label: "Ciudad", obraLabel: obra.tienda, previousValue: obra.ciudad ?? "", newValue: "" };
            }
            const unchanged = numericFields.has(field)
                ? parseLocaleNumber(originalValue) === parseLocaleNumber(rawValue)
                : String(originalValue) === String(rawValue);

            if (unchanged) {
                const next = { ...current };
                delete next[key];
                return stageCapexTriangle(next, obra, field);
            }

            return stageCapexTriangle({
                ...current,
                [key]: {
                    key,
                    codObra,
                    field,
                    label,
                    obraLabel: `${obra.codigoTienda} · ${obra.tienda}`,
                    previousValue: originalValue,
                    newValue: rawValue,
                },
            }, obra, field);
        });
    };

    // Aplica la decisión del modal de un hito: estado, fecha real y nota quedan como
    // cambios pendientes de la ficha (se guardan con el resto y dejan historial).
    // Un hito sin fila en BD vale «Pendiente»: volver a Pendiente no es un cambio.
    const applyHitoReview = (changes) => {
        const review = hitoReview;
        setHitoReview(null);
        const obra = review && obras.find((item) => item.codObra === review.codObra);
        if (!obra) return;
        const label = criticalHito(review.field)?.label || review.field;
        const partLabel = { Estado: "estado", Real: "fecha real", Notas: "nota" };
        changes.forEach(({ field, value }) => {
            const part = field.slice(review.field.length);
            const rawValue = part === "Estado" && value === "PENDIENTE" && !obra[field] ? "" : value;
            stageObraField(obra.codObra, field, rawValue, `${label} · ${partLabel[part] || part}`);
        });
    };

    // CAPEX = coste m² ref. × sup. construida. Al teclear uno de los tres, el que se
    // deduce queda pendiente de guardar con su propia línea (panel de cambios e
    // historial): es un dato que se escribe en la ficha, no un total calculado por SQL.
    const stageCapexTriangle = (drafts, obra, field) => {
        if (!CAPEX_TRIANGLE_FIELDS.includes(field)) return drafts;
        const result = { ...drafts };
        // Lo tecleado a mano en los tres campos; lo deducido antes se descarta y se
        // vuelve a calcular desde lo guardado (capexTriangleDraft, en costes.js).
        const typed = {};
        CAPEX_TRIANGLE_FIELDS.forEach((name) => {
            const key = getEditKey(obra.codObra, name);
            const draft = result[key];
            if (!draft) return;
            if (draft.derivedFrom) { delete result[key]; return; }
            const parsed = parseLocaleNumber(draft.newValue);
            typed[name] = parsed === "" ? "" : parsed;
        });
        const saved = Object.fromEntries(CAPEX_TRIANGLE_FIELDS.map((name) => [name, obra[name]]));
        const derived = capexTriangleDraft(saved, typed, field);
        if (!derived) return result;
        const key = getEditKey(obra.codObra, derived.field);
        result[key] = {
            key,
            codObra: obra.codObra,
            field: derived.field,
            label: ALL_FIELD_LABELS[derived.field] || derived.field,
            obraLabel: `${obra.codigoTienda} · ${obra.tienda}`,
            previousValue: obra[derived.field] ?? "",
            newValue: derived.value,
            derivedFrom: field,
        };
        return result;
    };

    // Guardar una obra nueva (o duplicada) es un alta: se envía la ficha entera con TODOS
    // sus cambios pendientes y solo si tiene el mínimo (el nombre de la tienda). Antes cada
    // campo se guardaba por separado, así que la obra se creaba a medias con el primero y
    // los demás cambios se quedaban apuntando a un codObra temporal que ya no existía: el
    // país nunca llegaba a la base de datos.
    const requestAltaSave = async (codObra) => {
        const original = obras.find((obra) => obra.codObra === codObra);
        if (!original) return;
        const drafts = Object.values(draftEdits).filter((draft) => draft.codObra === codObra);
        const merged = mergeDraftsIntoObra(original, drafts);
        const { errors } = issuesForSave(merged, drafts.map((draft) => draft.field), true);
        if (errors.length) {
            showNotice({
                title: original._duplicatedFrom ? "Faltan datos de la copia" : "Faltan datos de la obra nueva",
                message: `${errors.map((issue) => `• ${issue.message}`).join("\n")}\n\nCompleta estos campos y vuelve a guardar.`,
                tone: "danger",
            });
            return;
        }
        setChangeRequest({
            kind: "alta",
            codObra,
            label: original._duplicatedFrom ? "Guardar la obra duplicada" : "Guardar la obra nueva",
            obraLabel: `${merged.codigoTienda || ""} · ${merged.tienda || ""}`.trim(),
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    const requestChangeSave = (codObra, field, source) => {
        const draft = draftEdits[getEditKey(codObra, field)];
        if (!draft) return;
        // Una obra nueva no se guarda campo a campo: se da de alta entera.
        if (obras.find((obra) => obra.codObra === codObra)?._isNew) {
            requestAltaSave(codObra);
            return;
        }

        if (field === "pais" || field === "ciudad") {
            setChangeRequest({ kind: "geography", codObra, label: "Guardar país y ciudad", obraLabel: draft.obraLabel });
        } else setChangeRequest({ ...draft, source });
        setChangeReason("");
        setChangeReasonError(false);
    };

    // Guardado genérico: confirma TODOS los cambios pendientes con una sola justificación.
    const requestBatchSave = async () => {
        const drafts = Object.values(draftEdits);
        if (!drafts.length) return;
        // Validacion en origen: obra resultante (original + borradores) por cada obra tocada.
        const problems = [];
        new Set(drafts.map((draft) => draft.codObra)).forEach((codObra) => {
            const original = obras.find((obra) => obra.codObra === codObra);
            if (!original) return;
            const obraDrafts = drafts.filter((draft) => draft.codObra === codObra);
            const merged = { ...original };
            obraDrafts.forEach((draft) => { merged[draft.field] = draft.newValue; });
            const { errors, warnings } = issuesForSave(merged, obraDrafts.map((draft) => draft.field), Boolean(original._isNew));
            const name = `${merged.codigoTienda || ""} ${merged.tienda || ""}`.trim();
            errors.forEach((issue) => problems.push({ severity: "error", text: `${name}: ${issue.message}` }));
            warnings.forEach((issue) => problems.push({ severity: "warning", text: `${name}: ${issue.message}` }));
        });
        const errorLines = problems.filter((item) => item.severity === "error").map((item) => `• ${item.text}`);
        const warningLines = problems.filter((item) => item.severity === "warning").map((item) => `• ${item.text}`);
        if (errorLines.length) {
            await requestConfirm({
                title: "Revisa los datos antes de guardar",
                message: errorLines.join("\n"),
                confirmLabel: "Entendido",
                cancelLabel: null,
                tone: "danger",
            });
            return;
        }
        if (warningLines.length) {
            const ok = await requestConfirm({
                title: "Avisos en los datos",
                message: `${warningLines.join("\n")}\n\n¿Guardar igualmente?`,
                confirmLabel: "Continuar",
                cancelLabel: "Revisar",
                tone: "default",
            });
            if (!ok) return;
        }
        const obraCount = new Set(drafts.map((draft) => draft.codObra)).size;
        setChangeRequest({
            kind: "batch",
            label: `${drafts.length} ${drafts.length === 1 ? "cambio pendiente" : "cambios pendientes"}`,
            obraLabel: `${obraCount} ${obraCount === 1 ? "obra afectada" : "obras afectadas"}`,
        });
        setChangeReason("");
        setChangeReasonError(false);
    };

    // Cambio masivo: con sus cambios ya en pendientes (este efecto corre tras ese
    // render), se abre la caja de guardado sin pasar por "Guardar cambios".
    useEffect(() => {
        if (!bulkSaveRequested) return;
        setBulkSaveRequested(false);
        requestBatchSave();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bulkSaveRequested]);

    const discardAllDrafts = async () => {
        const count = Object.keys(draftEdits).length;
        if (!count) return;
        const newObras = obras.filter((obra) => obra._isNew && obra.codObra !== selectedId);
        const ok = await requestConfirm({
            title: "Descartar cambios",
            message: `Se descartarán ${count} ${count === 1 ? "cambio sin guardar" : "cambios sin guardar"} y los valores volverán a como estaban.${newObras.length ? ` También se descartarán ${newObras.length === 1 ? "la obra nueva sin guardar" : `las ${newObras.length} obras nuevas sin guardar`}.` : ""}`,
            confirmLabel: "Descartar",
            tone: "danger",
        });
        if (!ok) return;
        setDraftEdits({});
        clearSelectionOnSaveRef.current = false;
        // Una obra nueva sin cambios no existe: se quita con sus cambios.
        if (newObras.length) {
            const ids = new Set(newObras.map((obra) => obra.codObra));
            setObras((current) => current.filter((obra) => !ids.has(obra.codObra)));
        }
    };

    // Quita una obra nueva o duplicada que nunca se guardó (y sus cambios pendientes).
    const discardNewObra = (codObra) => {
        // Una justificación abierta para esa obra ya no tiene sentido.
        if (changeRequest?.codObra === codObra) cancelChangeSave();
        setObras((current) => current.filter((obra) => !(obra._isNew && obra.codObra === codObra)));
        setDraftEdits((current) => Object.fromEntries(Object.entries(current).filter(([, draft]) => draft.codObra !== codObra)));
        setSelectedId(null);
    };

    // Al guardarse, un alta cambia de codObra: el temporal (negativo) pasa a ser el que
    // asigna el servidor. Hay que reapuntar todo lo que va por codObra o la ficha abierta
    // se queda sin obra, y los cambios pendientes y el historial quedan huérfanos.
    const remapObraIds = (idMap) => {
        setSelectedId((current) => (current != null && idMap.has(current) ? idMap.get(current) : current));
        setDraftEdits((current) => remapDrafts(current, idMap));
        setAuditEntries((current) => remapAuditEntries(current, idMap));
        setSelectedObraIds((current) => remapIdSet(current, idMap));
    };

    // Tras guardar en la ruta mock, la obra deja de ser un alta pendiente.
    const markObrasSaved = (codObras) => {
        const ids = new Set(codObras);
        setObras((current) => current.map((obra) => {
            if (!ids.has(obra.codObra) || !obra._isNew) return obra;
            const { _isNew, _duplicatedFrom, ...saved } = obra;
            return saved;
        }));
    };

    // Historial del alta: "Alta de obra" o "Duplicar obra" con la obra de origen.
    const altaAuditEntry = (obra, reason) => (obra?._isNew ? {
        entidad: "OBRA",
        campo: "obra",
        etiqueta: obra._duplicatedFrom ? "Duplicar obra" : "Alta de obra",
        valorAnterior: obra._duplicatedFrom ? `${obra._duplicatedFrom.codigoTienda} · ${obra._duplicatedFrom.tienda}`.trim() : "",
        valorNuevo: `${obra.codigoTienda || ""} · ${obra.tienda || ""}`.trim(),
        motivo: reason,
        origen: obra._duplicatedFrom ? "DUPLICADO" : "ALTA",
    } : null);
    const toLocalAuditEntry = (entry, codObra, id) => ({
        id, codObra, field: entry.campo, label: entry.etiqueta, previousValue: entry.valorAnterior, newValue: entry.valorNuevo,
        reason: entry.motivo, user: "Usuario actual", date: "Ahora", source: entry.origen,
    });

    // Descarta el cambio pendiente de un único campo (el resto sigue pendiente).
    const discardObraField = (codObra, field) => {
        setDraftEdits((current) => {
            const key = getEditKey(codObra, field);
            if (!(key in current)) return current;
            const next = { ...current };
            delete next[key];
            return next;
        });
    };

    // Descarta todos los cambios pendientes de una obra (con confirmación si son varios).
    const discardObraDrafts = async (codObra) => {
        const keys = Object.keys(draftEdits).filter((key) => draftEdits[key].codObra === codObra);
        if (!keys.length) return;
        if (keys.length > 1) {
            const ok = await requestConfirm({
                title: "Descartar cambios de la obra",
                message: `Se descartarán los ${keys.length} cambios sin guardar de esta obra y los valores volverán a como estaban.`,
                confirmLabel: "Descartar",
                tone: "danger",
            });
            if (!ok) return;
        }
        setDraftEdits((current) => {
            const next = { ...current };
            keys.forEach((key) => { delete next[key]; });
            return next;
        });
    };

    // Cierra la ficha avisando si la obra abierta tiene cambios sin guardar. Una obra nueva
    // o duplicada sin el mínimo (tienda y país) o sin cambios se descarta (newObraCloseAction).
    const closeDetailGuarded = async () => {
        const closingObra = selectedId != null
            ? obras.find((obra) => obra.codObra === selectedId)
            : null;
        const obraDrafts = selectedId != null
            ? Object.values(draftEdits).filter((draft) => draft.codObra === selectedId)
            : [];
        const pending = obraDrafts.length;
        const newObraAction = newObraCloseAction(closingObra, obraDrafts);
        if (newObraAction === "confirm-discard") {
            const ok = await requestConfirm({
                title: closingObra._duplicatedFrom ? "Descartar la copia" : "Descartar la obra nueva",
                message: `Para conservar una obra nueva hace falta al menos el nombre de la tienda. Si cierras ahora se descartará junto con ${pending === 1 ? "su cambio sin guardar" : `sus ${pending} cambios sin guardar`}.`,
                confirmLabel: "Descartar obra",
                cancelLabel: "Seguir editando",
                tone: "danger",
            });
            if (!ok) return;
        }
        if (newObraAction !== "keep") {
            discardNewObra(closingObra.codObra);
            return;
        }
        if (pending > 0) {
            const ok = await requestConfirm({
                title: "Cambios sin guardar",
                message: `Tienes ${pending} ${pending === 1 ? "cambio sin guardar" : "cambios sin guardar"} en esta obra. Al cerrar seguirán pendientes (podrás guardarlos o descartarlos después).`,
                confirmLabel: "Cerrar igualmente",
                cancelLabel: "Seguir editando",
                tone: "default",
            });
            if (!ok) return;
        }
        setSelectedId(null);
    };

    // Un valor del historial se puede volver a poner si su campo se edita en la ficha, el
    // valor no es el que ya tiene y, en un catálogo, la opción sigue existiendo (no se
    // revive desde aquí un valor borrado).
    const restorableHistoryField = (entry) => {
        if (readOnly) return null;
        const config = fieldConfigByName[entry.field];
        if (!config || config.usuario || config.calculated || calculatedCostFields.has(entry.field)) return null;
        return config;
    };
    const canRestoreHistoryValue = (entry, value) => {
        if (!selectedObra || entry.codObra !== selectedObra.codObra || selectedObra.activa === false) return false;
        const config = restorableHistoryField(entry);
        if (!config) return false;
        const texto = value == null ? "" : String(value);
        const actual = draftEdits[getEditKey(entry.codObra, entry.field)]?.newValue ?? selectedObra[entry.field];
        if (texto === String(actual ?? "")) return false;
        if (!config.catalog || !texto) return true;
        const opciones = getCatalogValues(config.catalog, selectedObra, actual, catalogs);
        return opciones.some((opcion) => opcion.value === texto && opcion.active !== false);
    };
    const restoreHistoryValue = (entry, value) => {
        const config = restorableHistoryField(entry);
        if (!config) return;
        stageObraField(entry.codObra, entry.field, value == null ? "" : value, config.label);
        // Se lleva al usuario al campo para que vea el cambio antes de guardarlo.
        goToDetailField({ field: entry.field });
    };

    const goToDetailField = (match) => {
        if (!match) return;

        setDetailTab(detailTabOfField(match.field) || "general");
        setHighlightedDetailField(match.field);
    };

    const cancelChangeSave = () => {
        setChangeRequest(null);
        setChangeReason("");
        setChangeReasonError(false);
        setSaveError("");
    };

    // ── Persistencia en backend (solo ruta real /admin/obras) ────────────────
    // Aplica una lista de drafts sobre una obra y devuelve la copia recalculada,
    // sin tocar el estado (para construir el payload que se envía al API).
    const mergeDraftsIntoObra = (obra, drafts) => {
        let next = obra;
        drafts.forEach(({ field, newValue }) => {
            let value = newValue;
            if (numericFields.has(field)) {
                const parsed = parseLocaleNumber(newValue);
                value = parsed === "" ? "" : roundToTwoDecimals(parsed);
            }
            next = { ...next, [field]: value };
        });
        return recalculateDerivedCosts(next);
    };
    // Las altas envían la ficha; las ediciones, solo los campos cambiados.
    // El API conserva lo omitido y guarda los cambios y su auditoría en una transacción.
    const persistObraSave = async (obra, fieldAudit = []) => {
        // Un alta registra además "Alta de obra" o "Duplicar obra" (con la obra de origen).
        const alta = altaAuditEntry(obra, fieldAudit[0]?.motivo || "Alta de obra");
        const auditoria = alta ? [alta, ...fieldAudit] : fieldAudit;
        const cambios = Object.fromEntries(fieldAudit
            .filter(({ campo }) => !calculatedCostFields.has(campo))
            .map(({ campo }) => [campo, obra[campo] === "" ? null : obra[campo]]));
        const payload = obra._isNew
            // SQL Server interpreta una cadena de fecha vacía como 01/01/1900.
            // Las altas deben enviar null para que las fechas permanezcan sin planificar.
            // Alta: se envía la ficha entera, sin campos internos (_isNew...) y sin estados de
            // hito vacíos (el API solo admite PENDIENTE, OK, NA o RIESGO; omitirlos los deja
            // en su valor por defecto).
            ? Object.fromEntries(Object.entries({ ...obra, codObra: 0, rowVersion: null, auditoria })
                .filter(([key, value]) => !key.startsWith("_") && !(/Estado$/.test(key) && (value === null || value === "")))
                .map(([key, value]) => [key, value === "" ? null : value]))
            : { ...cambios, codObra: obra.codObra, rowVersion: obra.rowVersion, auditoria };
        if (obra._isNew || Object.hasOwn(cambios, "pais") || Object.hasOwn(cambios, "ciudad")) {
            Object.assign(payload, geoPayload(obra, catalogs));
        }
        const ficha = await guardarObra(payload);
        return ficha ? buildApiObra(ficha, 0) : { ...obra, _isNew: false };
    };
    // Convierte un draft/changeRequest en una entrada de auditoría para el backend.
    const toAuditEntry = (source, reason) => ({
        entidad: "OBRA",
        campo: source.field,
        etiqueta: source.label,
        valorAnterior: source.previousValue == null ? "" : String(source.previousValue),
        valorNuevo: source.newValue == null ? "" : String(source.newValue),
        motivo: reason,
        origen: source.source || "OBRA",
    });
    // Conflicto de concurrencia optimista (HTTP 409 desde el backend).
    const isConflictError = (err) => err?.response?.status === 409;
    // Recarga toda la cartera desde el API (tras un conflicto, para tener rowVersion fresco).
    const reloadObrasFromApi = async () => {
        try {
            const rows = await fetchObras();
            setObras(rows.map(buildApiObra));
        } catch (err) {
            console.error("No se pudo recargar la cartera tras el conflicto", err);
        }
    };

    // Sondeo ligero de concurrencia: refresca la cartera en segundo plano. Las obras
    // sin cambios pendientes adoptan el dato fresco; las que estás editando se
    // conservan pero, si cambiaron en el servidor, se avisa (además del 409 al guardar).
    const refreshObrasBackground = async () => {
        if (useLocalData || savingRef.current) return;
        let rows;
        try { rows = await fetchObras(); } catch { return; }
        // Catálogos y geografía: si otra persona renombró o desactivó un valor, las listas
        // dejan de ofrecer el nombre viejo (guardarlo lo volvía a crear). Un fallo aquí no
        // impide refrescar las obras.
        try {
            const [catalogRows, geo] = await Promise.all([fetchCatalogos(), fetchGeografia()]);
            if (catalogRows.length) {
                const { byTipo, empresas: empresasApi } = catalogStateFromRows(catalogRows);
                setEmpresas(empresasApi);
                setCatalogs((current) => ({ ...current, ...byTipo, ...geo }));
            } else {
                setCatalogs((current) => ({ ...current, ...geo }));
            }
        } catch (err) {
            console.warn("No se pudieron refrescar los catálogos", err);
        }
        const fresh = rows.map(buildApiObra);
        const freshByCod = new Map(fresh.map((o) => [o.codObra, o]));
        const draftCods = new Set(Object.values(draftsRef.current).map((d) => d.codObra));
        const current = obrasRef.current;
        const changedWhileEditing = [...draftCods].filter((cod) => {
            const f = freshByCod.get(cod);
            const cur = current.find((o) => o.codObra === cod);
            return f && cur && f.rowVersion && cur.rowVersion && f.rowVersion !== cur.rowVersion;
        });
        setObras((prev) => {
            const seen = new Set();
            const next = prev.map((o) => {
                seen.add(o.codObra);
                const f = freshByCod.get(o.codObra);
                if (!f) return o;
                if (f.rowVersion && o.rowVersion && f.rowVersion !== o.rowVersion) {
                    return draftCods.has(o.codObra) ? o : f; // en edición: conservar; si no, adoptar
                }
                // Misma versión pero otro dato (un catálogo o proveedor renombrado): se adopta
                // si no se está editando.
                return !draftCods.has(o.codObra) && apiRowChanged(o, f) ? f : o;
            });
            fresh.forEach((f) => { if (!seen.has(f.codObra)) next.unshift(f); });
            return next;
        });
        if (changedWhileEditing.length) {
            setStaleNotice(`Otra persona modificó ${changedWhileEditing.length} obra${changedWhileEditing.length === 1 ? "" : "s"} que estás editando. Al guardar se pedirá recargar (no se pisarán cambios).`);
        }
    };

    // Sondeo ligero: refresca al volver a la pestaña y cada 45s (solo ruta real).
    useEffect(() => {
        if (useLocalData) return undefined;
        const onFocus = () => { refreshObrasBackground(); };
        window.addEventListener("focus", onFocus);
        const id = window.setInterval(() => {
            if (document.visibilityState === "visible") refreshObrasBackground();
        }, 45000);
        return () => { window.removeEventListener("focus", onFocus); window.clearInterval(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useLocalData]);

    // El aviso de concurrencia se retira cuando ya no quedan cambios pendientes.
    useEffect(() => {
        if (Object.keys(draftEdits).length === 0) setStaleNotice("");
    }, [draftEdits]);

    // Un cambio pendiente cuya obra ya no está en la cartera (alta descartada, obra
    // borrada) no se puede guardar: se retira para no anunciarlo luego como guardado.
    useEffect(() => {
        setDraftEdits((current) => pruneOrphanDrafts(current, obras));
    }, [obras]);

    const commitChange = async (event) => {
        event.preventDefault();
        // Justificación obligatoria solo para inicio/apertura y acciones que no son
        // editar datos; en el resto, vacía se guarda como «N/A».
        const reason = effectiveReason(changeReason, reasonRequired);
        if (!changeRequest || !reason) {
            setChangeReasonError(true);
            return;
        }
        setSaveError("");

        if (changeRequest.kind === "batch" || changeRequest.kind === "geography" || changeRequest.kind === "alta") {
            const drafts = draftsToPersist(draftEdits, changeRequest, obras);
            let idMap = new Map();
            if (!useLocalData) {
                // Ruta real: se agrupan los cambios por obra y se guarda cada una en
                // el backend; el estado se reconcilia con la ficha devuelta.
                setSaving(true);
                try {
                    const byObra = new Map();
                    drafts.forEach((draft) => {
                        const list = byObra.get(draft.codObra) || [];
                        list.push(draft);
                        byObra.set(draft.codObra, list);
                    });
                    const saved = [];
                    for (const [codObra, obraDrafts] of byObra.entries()) {
                        const current = obras.find((obra) => obra.codObra === codObra);
                        if (!current) continue;
                        const merged = mergeDraftsIntoObra(current, obraDrafts);
                        const auditoria = obraDrafts.map((draft) => toAuditEntry(draft, reason));
                        saved.push([codObra, await persistObraSave(merged, auditoria)]);
                    }
                    setObras((current) => current.map((obra) => {
                        const hit = saved.find(([tempId]) => tempId === obra.codObra);
                        return hit ? hit[1] : obra;
                    }));
                    idMap = savedIdMap(saved);
                    if (idMap.size) remapObraIds(idMap);
                } catch (err) {
                    if (isConflictError(err)) {
                        await reloadObrasFromApi();
                        setDraftEdits(current => Object.fromEntries(Object.entries(current).filter(([key]) => !drafts.some(d => d.key === key))));
                        showSaveError(new Error("Otra persona modificó alguna de estas obras mientras editabas. Se recargaron los datos; revisa y vuelve a guardar."));
                        setSaving(false);
                        return;
                    }
                    console.error("No se pudieron guardar los cambios en el backend", err);
                    showSaveError(err, "No se pudieron guardar los cambios en el servidor. Revisa la conexión e inténtalo de nuevo.");
                    setSaving(false);
                    return;
                }
                setSaving(false);
            } else {
                // Ruta mock: los cambios se aplican en memoria (estado local).
                drafts.forEach((draft) => applyObraField(draft.codObra, draft.field, draft.newValue));
                markObrasSaved(drafts.map((draft) => draft.codObra));
            }
            const now = Date.now();
            // El historial y los cambios ya guardados se archivan con el codObra definitivo.
            const realId = (codObra) => (idMap.has(codObra) ? idMap.get(codObra) : codObra);
            // Altas guardadas en este lote: su entrada de historial va antes que los campos.
            const altaEntries = [...new Set(drafts.map((draft) => draft.codObra))]
                .map((codObra) => obras.find((obra) => obra.codObra === codObra))
                .filter(Boolean)
                .map((obra) => [obra, altaAuditEntry(mergeDraftsIntoObra(obra, drafts.filter((draft) => draft.codObra === obra.codObra)), reason)])
                .filter(([, entry]) => entry)
                .map(([obra, entry], index) => toLocalAuditEntry(entry, realId(obra.codObra), now - 1 - index));
            const savedEntries = drafts.map((draft, index) => ({
                id: now + index,
                codObra: realId(draft.codObra),
                field: draft.field,
                label: draft.label,
                previousValue: draft.previousValue,
                newValue: draft.newValue,
                reason,
                user: "Usuario actual",
                date: "Ahora",
                source: draft.source || "LOTE",
            }));
            setAuditEntries((current) => [...savedEntries, ...altaEntries, ...current]);
            // Por la clave con el codObra definitivo: si el alta cambió de id, la clave
            // antigua ya no está en el estado y los cambios guardados se quedarían pendientes.
            const guardadas = savedDraftKeys(drafts, idMap);
            setDraftEdits(current => Object.fromEntries(Object.entries(current).filter(([key]) => !guardadas.has(key))));
            if (savedEntries.length) setLastSavedChange(savedEntries[0]);
            cancelChangeSave();
            // Guardado un cambio masivo, su selección ya cumplió: se vacía.
            if (changeRequest.kind === "batch" && clearSelectionOnSaveRef.current) {
                clearSelectionOnSaveRef.current = false;
                clearSelection();
            }
            return;
        }

        if (changeRequest.kind === "bulk-delete") {
            if (!useLocalData) {
                // El backend solo admite baja lógica (archivar); no hay borrado físico.
                setSaveError("La eliminación definitiva no está disponible en el servidor. Archiva las obras en su lugar.");
                return;
            }
            const targets = selectedObras;
            const ids = new Set(targets.map((obra) => obra.codObra));
            setObras((current) => current.filter((obra) => !ids.has(obra.codObra)));
            const now = Date.now();
            const deletedEntries = targets.map((obra, index) => ({
                id: now + index,
                codObra: obra.codObra,
                field: "__deleted",
                label: "Eliminación definitiva",
                previousValue: `${obra.codigoTienda} · ${obra.tienda}`,
                newValue: "—",
                reason,
                user: "Usuario actual",
                date: "Ahora",
                source: "ELIMINACION",
            }));
            setAuditEntries((current) => [...deletedEntries, ...current]);
            if (deletedEntries.length) setLastSavedChange(deletedEntries[0]);
            clearSelection();
            cancelChangeSave();
            return;
        }

        if (changeRequest.kind === "bulk-status") {
            const targets = selectedObras;
            const applyLocal = (obra) => ({
                ...obra,
                activa: changeRequest.nextActive,
                fechaBaja: changeRequest.nextActive ? null : "Ahora",
                usuarioBaja: changeRequest.nextActive ? null : "Usuario actual",
                motivoBaja: changeRequest.nextActive ? null : reason,
            });
            if (!useLocalData) {
                // Ruta real: archivar/reactivar cada obra vía API (usp_Obras_Archivar).
                setSaving(true);
                try {
                    const saved = [];
                    for (const obra of targets) {
                        const ficha = await archivarObra(obra.codObra, changeRequest.nextActive, reason);
                        saved.push([obra.codObra, ficha ? buildApiObra(ficha, 0) : applyLocal(obra)]);
                    }
                    setObras((current) => current.map((obra) => {
                        const hit = saved.find(([cod]) => cod === obra.codObra);
                        return hit ? hit[1] : obra;
                    }));
                } catch (err) {
                    console.error("No se pudo archivar/reactivar en lote", err);
                    setSaveError("No se pudo completar la operación en el servidor. Inténtalo de nuevo.");
                    setSaving(false);
                    return;
                }
                setSaving(false);
            } else {
                setObras((current) => {
                    const ids = new Set(targets.map((o) => o.codObra));
                    return current.map((o) => (ids.has(o.codObra) ? applyLocal(o) : o));
                });
            }
            const now = Date.now();
            const entries = targets.map((obra, index) => ({
                id: now + index,
                codObra: obra.codObra,
                field: "obra",
                label: changeRequest.label,
                previousValue: changeRequest.previousValue,
                newValue: changeRequest.newValue,
                reason,
                user: "Usuario actual",
                date: "Ahora",
                source: changeRequest.source,
            }));
            setAuditEntries((current) => [...entries, ...current]);
            if (entries.length) setLastSavedChange(entries[0]);
            clearSelection();
            cancelChangeSave();
            return;
        }

        if (changeRequest.kind === "obra-status") {
            const applyStatusLocal = (obra) => ({
                ...obra,
                activa: changeRequest.nextActive,
                fechaBaja: changeRequest.nextActive ? null : "Ahora",
                usuarioBaja: changeRequest.nextActive ? null : "Usuario actual",
                motivoBaja: changeRequest.nextActive ? null : reason,
            });
            if (!useLocalData) {
                // Ruta real: baja/reactivación contra el backend (usp_Obras_Archivar).
                setSaving(true);
                try {
                    const ficha = await archivarObra(changeRequest.codObra, changeRequest.nextActive, reason);
                    const mapped = ficha ? buildApiObra(ficha, 0) : null;
                    setObras((current) => current.map((obra) =>
                        obra.codObra === changeRequest.codObra ? (mapped || applyStatusLocal(obra)) : obra
                    ));
                } catch (err) {
                    console.error("No se pudo archivar/reactivar la obra en el backend", err);
                    setSaveError("No se pudo completar la operación en el servidor. Inténtalo de nuevo.");
                    setSaving(false);
                    return;
                }
                setSaving(false);
            } else {
                // Ruta mock: baja/reactivación en memoria (estado local).
                setObras((current) =>
                    current.map((obra) => obra.codObra === changeRequest.codObra ? applyStatusLocal(obra) : obra)
                );
            }
            if (!changeRequest.nextActive) {
                setDraftEdits((current) =>
                    Object.fromEntries(
                        Object.entries(current).filter(
                            ([, draft]) => draft.codObra !== changeRequest.codObra
                        )
                    )
                );
            }
            setSelectedId(null);

            const statusEntry = {
                id: Date.now(),
                codObra: changeRequest.codObra,
                field: "obra",
                label: changeRequest.label,
                previousValue: changeRequest.previousValue,
                newValue: changeRequest.newValue,
                reason,
                user: "Usuario actual",
                date: "Ahora",
                source: changeRequest.source,
            };

            setAuditEntries((current) => [statusEntry, ...current]);
            setLastSavedChange(statusEntry);
            cancelChangeSave();
            return;
        }

        if (changeRequest.kind === "catalog") {
            // Persistencia en backend para catálogos de BD con id real (numérico).
            // Los valores solo-locales (id temporal) se gestionan en memoria hasta
            // que la obra que los usa se guarda (el proc los materializa).
            const codCatalogo = Number(changeRequest.catalogEntryId);
            const persistCatalog = !useLocalData
                && (DB_CATALOG_TYPES.has(changeRequest.catalog)
                    || EMPRESA_CATALOG_TYPES.has(changeRequest.catalog)
                    || ["pais", "ciudad"].includes(changeRequest.catalog))
                && Number.isInteger(codCatalogo) && codCatalogo > 0;
            if (persistCatalog) {
                setSaving(true);
                try {
                    if (changeRequest.catalogAction === "rename") {
                        await guardarCatalogo(catalogApiType(changeRequest.catalog), codCatalogo, changeRequest.newValue, reason);
                    } else if (changeRequest.catalogAction === "delete") {
                        if (["pais", "ciudad"].includes(changeRequest.catalog)) await eliminarGeografia(changeRequest.catalog, codCatalogo, reason);
                        else await eliminarCatalogo(codCatalogo, reason);
                    } else {
                        if (["pais", "ciudad"].includes(changeRequest.catalog)) await estadoGeografia(changeRequest.catalog, codCatalogo, changeRequest.nextActive, reason);
                        else await cambiarEstadoCatalogo(codCatalogo, changeRequest.nextActive, reason);
                    }
                } catch (err) {
                    if (isConflictError(err)) {
                        setSaveError(err?.response?.data?.message
                            || (changeRequest.catalogAction === "rename"
                                ? "Ya existe ese valor en el catálogo."
                                : "El valor está en uso por alguna obra; desactívalo en vez de eliminarlo."));
                        setSaving(false);
                        return;
                    }
                    console.error("No se pudo actualizar el catálogo en el backend", err);
                    showSaveError(err, "No se pudo actualizar el catálogo en el servidor.");
                    setSaving(false);
                    return;
                }
                setSaving(false);
            }
            // Un proveedor es la misma empresa en todos los roles (consulting, mobiliario...):
            // al renombrarlo se actualiza su entrada en todos los catálogos de proveedor.
            const renamedCatalogs = EMPRESA_CATALOG_TYPES.has(changeRequest.catalog)
                ? [...EMPRESA_CATALOG_TYPES]
                : [changeRequest.catalog];
            setCatalogs((current) => ({
                ...current,
                ...Object.fromEntries(renamedCatalogs.map((catalog) => [catalog, changeRequest.catalogAction === "delete"
                    ? (current[catalog] || []).filter(
                        (entry) => entry.id !== changeRequest.catalogEntryId
                    )
                    : (current[catalog] || []).map((entry) =>
                        entry.id !== changeRequest.catalogEntryId
                            ? entry
                            : changeRequest.catalogAction === "rename"
                                ? { ...entry, value: changeRequest.newValue }
                                : { ...entry, active: changeRequest.nextActive }
                    )])),
            }));
            if (changeRequest.catalogAction === "rename") {
                // Las obras guardan el texto del valor: se actualiza en todas las que lo usan
                // (y en los cambios pendientes que lo tenían elegido).
                const fields = renamedCatalogs.flatMap((catalog) => CATALOG_FIELDS[catalog] || [catalog]);
                const renameIn = (record) => {
                    const next = fields.reduce((item, field) => (
                        item[field] === changeRequest.previousValue ? { ...item, [field]: changeRequest.newValue } : item
                    ), record);
                    // `secciones` guarda el proveedor ya resuelto: si no se renombra también,
                    // la tabla de entregables y el mapa de proveedores siguen con el nombre viejo.
                    if (next === record || !next.secciones?.length) return next;
                    return {
                        ...next,
                        secciones: next.secciones.map((seccion) => (seccion.proveedor === changeRequest.previousValue
                            ? { ...seccion, proveedor: changeRequest.newValue }
                            : seccion)),
                    };
                };
                setObras((current) => current.map(renameIn));
                setDraftEdits((current) => Object.fromEntries(Object.entries(current).map(([key, draft]) => [
                    key,
                    fields.includes(draft.field) && draft.newValue === changeRequest.previousValue ? { ...draft, newValue: changeRequest.newValue } : draft,
                ])));
            }

            const catalogEntry = {
                id: Date.now(),
                codObra: changeRequest.codObra,
                field: `catalog:${changeRequest.catalog}`,
                label: changeRequest.label,
                previousValue: changeRequest.previousValue,
                newValue: changeRequest.newValue,
                reason,
                user: "Usuario actual",
                date: "Ahora",
                source: changeRequest.source,
            };
            const auditItems = [catalogEntry];

            if (
                changeRequest.nextActive
                && changeRequest.selectAfter
                && String(changeRequest.assignedPreviousValue) !== String(changeRequest.catalogValue)
            ) {
                stageObraField(changeRequest.codObra, changeRequest.field, changeRequest.catalogValue, changeRequest.label);
                auditItems.push({
                    ...catalogEntry,
                    id: catalogEntry.id + 1,
                    field: changeRequest.field,
                    label: changeRequest.label.replace(" - catalogo", ""),
                    previousValue: changeRequest.assignedPreviousValue,
                    newValue: changeRequest.catalogValue,
                    source: "REACTIVACION",
                });
            }

            setAuditEntries((current) => [...auditItems, ...current]);
            setLastSavedChange(catalogEntry);
            cancelChangeSave();
            return;
        }

        // Cambio de un solo campo. La ficha sigue editable con el panel de justificación
        // abierto, así que se guarda el valor que tiene el campo ahora, no el que tenía al
        // abrirlo (si no, el cambio más reciente se perdía sin avisar).
        const liveDraft = draftEdits[changeRequest.key];
        const request = liveDraft && liveDraft.newValue !== changeRequest.newValue
            ? { ...changeRequest, newValue: liveDraft.newValue }
            : changeRequest;
        if (!useLocalData) {
            // Ruta real: se guarda el campo modificado y se recibe la ficha completa.
            const current = obras.find((obra) => obra.codObra === changeRequest.codObra);
            if (current) {
                setSaving(true);
                try {
                    const merged = mergeDraftsIntoObra(current, [{ field: request.field, newValue: request.newValue }]);
                    const result = await persistObraSave(merged, [toAuditEntry(request, reason)]);
                    setObras((cur) => cur.map((obra) => (obra.codObra === changeRequest.codObra ? result : obra)));
                } catch (err) {
                    if (isConflictError(err)) {
                        await reloadObrasFromApi();
                        setDraftEdits({});
                        showSaveError(new Error("Otra persona modificó esta obra mientras editabas. Se recargaron los datos; revisa y vuelve a guardar."));
                        setSaving(false);
                        return;
                    }
                    console.error("No se pudo guardar el cambio en el backend", err);
                    showSaveError(err, "No se pudo guardar el cambio en el servidor. Inténtalo de nuevo.");
                    setSaving(false);
                    return;
                }
                setSaving(false);
            }
        } else {
            // Ruta mock: se aplica en memoria (estado local).
            applyObraField(request.codObra, request.field, request.newValue);
            markObrasSaved([request.codObra]);
        }
        const savedObra = obras.find((obra) => obra.codObra === request.codObra);
        const singleAlta = savedObra ? altaAuditEntry({ ...savedObra, [request.field]: request.newValue }, reason) : null;
        setDraftEdits((current) => {
            const next = { ...current };
            delete next[changeRequest.key];
            return next;
        });

        const savedEntry = {
            id: Date.now(),
            codObra: request.codObra,
            field: request.field,
            label: request.label,
            previousValue: request.previousValue,
            newValue: request.newValue,
            reason,
            user: "Usuario actual",
            date: "Ahora",
            source: request.source,
        };

        setAuditEntries((current) => [savedEntry, ...(singleAlta ? [toLocalAuditEntry(singleAlta, request.codObra, savedEntry.id - 1)] : []), ...current]);
        setLastSavedChange(savedEntry);
        cancelChangeSave();
    };

    // Cambios que enseña el panel de justificación: en un alta, los de esa obra (se guarda
    // entera); en un lote, todos los pendientes.
    const dockDrafts = () => (changeRequest?.kind === "alta"
        ? Object.values(draftEdits).filter((draft) => draft.codObra === changeRequest.codObra)
        : Object.values(draftEdits));
    // ¿Este guardado exige justificación? (los cambios que se guardarían con ella).
    const reasonRequired = changeRequest ? isReasonRequired(changeRequest, draftsToPersist(draftEdits, changeRequest, obras)) : true;

    const openDetail = useCallback((codObra) => {
        setDetailTab("general");
        setDetailFieldSearch("");
        setHighlightedDetailField(null);
        setSelectedId(codObra);
    }, []);
    // Abre la ficha en la pestaña del campo y lo resalta (p. ej. un hito con alerta).
    const openDetailAtField = (codObra, field) => {
        openDetail(codObra);
        setDetailTab(detailTabOfField(field) || "general");
        setHighlightedDetailField(field);
    };

    const reloadGeography = async () => {
        const geo = await fetchGeografia();
        setCatalogs((current) => ({ ...current, ...geo }));
    };
    const catalogManagerObra = catalogManager
        ? obras.find((obra) => obra.codObra === catalogManager.obra.codObra)
        : null;
    const catalogManagerEntries = catalogManager && catalogManagerObra
        ? getCatalogValues(
            catalogManager.catalog,
            catalogManagerObra,
            catalogManagerObra[catalogManager.field],
            catalogs
        )
        : [];

    const renderDetailField = (config) => {
        if (!selectedObra) return null;

        const effectiveObra = { ...selectedObra, pais: draftEdits[getEditKey(selectedObra.codObra, "pais")]?.newValue ?? selectedObra.pais };
        const effectiveCountry = catalogs.pais.find((entry) => entry.value === effectiveObra.pais);
        effectiveObra.codPais = effectiveCountry?.id || selectedObra.codPais;
        const stagingField = config.usuario || config.field;
        const draft = draftEdits[getEditKey(selectedObra.codObra, stagingField)];
        const currentValue = draft?.newValue ?? selectedObra[stagingField];
        const options = config.catalog
            ? getCatalogValues(config.catalog, effectiveObra, currentValue, catalogs)
            : null;
        const responsables = config.usuario ? (responsablesByRol[config.rol] || []) : null;
        const hito = isCriticalHito(config.field) ? hitoState(selectedDraftObra || effectiveObra, config.field, portfolioToday) : null;
        // Calculados (días, totales, €/m²...): con cambios pendientes se ve ya el resultado
        // de esos cambios; antes seguían en el valor guardado hasta guardar.
        const calculatedPreview = selectedPreviewObra && (config.calculated || calculatedCostFields.has(config.field))
            ? { ...effectiveObra, [config.field]: selectedPreviewObra[config.field] }
            : null;

        return (
            <DetailField
                key={config.field}
                hito={hito}
                onReviewHito={(field) => setHitoReview({ codObra: selectedObra.codObra, field })}
                readOnly={!canEditObraField(role, config.usuario || config.field)}
                config={config}
                highlighted={highlightedDetailField === config.field}
                obra={calculatedPreview || effectiveObra}
                draft={draft}
                options={options}
                responsables={responsables}
                onCatalogStatusChange={requestCatalogStatusChange}
                onChange={stageObraField}
                onCreateOption={registerCatalogOption}
                onOpenCatalogManager={(context) => context.catalog === "ciudad" ? setGeographyManager({ country: effectiveObra.codPais || "", codObra: context.obra.codObra }) : setCatalogManager(context)}
                onRequestSave={requestChangeSave}
                onDiscardField={discardObraField}
                onBlockedCatalogSelect={() => setConfirmDialog({
                    title: "Selecciona un país primero",
                    message: "No puedes editar ni crear una ubicación hasta seleccionar el país de la obra.",
                    confirmLabel: "Entendido",
                    cancelLabel: null,
                    tone: "danger",
                })}
            />
        );
    };

    // Campos de una pestaña agrupados en sub-tarjetas con título + descripción,
    // igual que Costes/CAPEX, para dar el mismo nivel de detalle a todas.
    const renderDetailSections = (sections) => (
        <div className="obras-field-sections">
            {sections.map((section) => (
                <section className={`obras-field-card${section.wide ? " obras-field-card--wide" : ""}${isZoneSection(section) ? " obras-field-card--zones" : ""}`} key={section.id}>
                    <header>
                        <div>
                            <span>{section.label}</span>
                            <p>{section.note}</p>
                        </div>
                        <strong>{section.fields.length}</strong>
                    </header>
                    <div className="obras-detail-fields obras-detail-fields--cost-card">
                        {sectionLayout(section, (field) => Boolean(fieldConfigByName[field])).map((row) => (
                            // Cada fila empieza en la primera columna (display: contents).
                            <div className="obras-detail-row" key={row.map((item) => item.field || item.pair.join("-")).join("|")}>
                                {row.map((item) => (item.pair ? (
                                    // Proveedor -> tipo de una zona: siempre juntos en la misma fila.
                                    <div className="obras-detail-pair" key={item.pair.join("-")}>
                                        {item.pair.map((field) => renderDetailField(fieldConfigByName[field]))}
                                    </div>
                                ) : renderDetailField(fieldConfigByName[item.field])))}
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );

    // Constructor de filtros reutilizable: se usa en el toolbar y en las cabeceras de los modales.
    const renderFilterBuilder = () => {
        const filterMenuVisible = filterMenuOpen;
        return (
                <div className="obras-filter-builder">
                    <button
                        type="button"
                        className={`obras-filter-builder__toggle${activeFilterCount ? " has-active" : ""}${filterMenuVisible ? " open" : ""}`}
                        aria-expanded={filterMenuVisible}
                        onClick={() => { if (filterMenuVisible) closeFilterMenu(); else openFilterPanel(); }}
                    >
                        <FiFilter />
                        <span>Filtros</span>
                        {activeFilterCount ? <strong>{activeFilterCount}</strong> : null}
                        <FiChevronDown />
                    </button>
                    {filterMenuVisible ? (
                        <FiltersPanel
                            fields={ALL_FILTER_FIELDS}
                            groupOrder={FILTER_GROUP_ORDER}
                            resultLabel={`${filteredObras.length} ${filteredObras.length === 1 ? "obra" : "obras"} · ${moneyCompact(filteredObras.reduce((acc, obra) => acc + Number(obra.capex || 0), 0))}`}
                            activeCount={activeFilterCount}
                            today={new Date()}
                            getSelected={(fieldId) => customFilters.category[fieldId] || []}
                            getValues={(fieldId) => categoryFilterOptions[fieldId] || []}
                            getCounts={facetCounts}
                            getRange={(type, fieldId) => customFilters[type]?.[fieldId] || null}
                            getUnit={(fieldId) => fieldUnits[fieldId]}
                            onToggleValue={toggleCategoryValue}
                            onSetValues={applyCategoryFilter}
                            onSetRange={setRangeFilter}
                            onClearField={(field) => (field.type === "category" ? clearCategoryField(field.id) : clearRangeField(field.type, field.id))}
                            onClearAll={clearAllFilters}
                            views={filterViews}
                            activeViewId={activeFilterViewId}
                            onApplyView={applyFilterView}
                            onDeleteView={deleteFilterQuickConfig}
                            onSaveView={saveFilterQuickConfig}
                            saveStatus={quickConfigSaveStatus(savedFilterConfigs)}
                            maxNameLength={MAX_QUICK_CONFIG_NAME_LENGTH}
                            initialFieldId={filterPanelFocus}
                            onClose={closeFilterMenu}
                        />
                    ) : null}
                </div>
        );
    };

    if (loadState === "loading") {
        return (
            <div className="obras-page">
                <SectionHeader
                    title="Obras"
                    subtitle="Control operativo, presupuestario y seguimiento de hitos de construcción"
                />
                <div className="obras-state-message">
                    <span className="obras-state-message__spinner" aria-hidden="true" />
                    <p>Cargando obras…</p>
                </div>
            </div>
        );
    }

    if (loadState === "error") {
        return (
            <div className="obras-page">
                <SectionHeader
                    title="Obras"
                    subtitle="Control operativo, presupuestario y seguimiento de hitos de construcción"
                />
                <div className="obras-state-message obras-state-message--error">
                    <FiAlertTriangle />
                    <p><strong>Datos no disponibles</strong></p>
                    <span>No se pudo conectar con el servidor de obras. Vuelve a intentarlo más tarde.</span>
                    <button type="button" className="obras-primary-button" onClick={() => window.location.reload()}>
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`obras-page obras-page--bounded${fullscreen ? " obras-page--fullscreen" : ""}`} ref={obrasPageRef}>
            {fullscreen ? null : (
                <SectionHeader
                    title="Obras"
                    subtitle="Control operativo, presupuestario y seguimiento de hitos de construcción"
                />
            )}
            <div className="obras-command-bar">
                <div className="obras-page-header__stats">
                    <strong>{activeCount} obras activas</strong>
                    <span className={summary.riesgos ? "tone-danger" : ""}>{summary.riesgos} en riesgo crítico</span>
                </div>
                <div className="obras-search">
                    <FiSearch />
                    <input
                        aria-label="Buscar obras"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Buscar en cualquier campo de texto (tienda, pais, PM, proveedor...)"
                    />
                    {search ? (
                        <button
                            type="button"
                            aria-label="Limpiar busqueda"
                            title="Limpiar busqueda"
                            onClick={() => setSearch("")}
                        >
                            <FiX />
                        </button>
                    ) : null}
                </div>
{renderFilterBuilder()}
                <button
                    type="button"
                    className={`obras-archive-filter${showArchived ? " active" : ""}`}
                    aria-pressed={showArchived}
                    onClick={() => {
                        setShowArchived((current) => !current);
                        setSelectedId(null);
                    }}
                >
                    <FiArchive />
                    <span>{showArchived ? "Ver activas" : "Archivadas"}</span>
                    <strong>{showArchived ? activeCount : archivedCount}</strong>
                </button>
                {Object.keys(draftEdits).length ? (
                    <div className="obras-save-group">
                        <button type="button" className="obras-discard-all" onClick={discardAllDrafts}>
                            <FiX />
                            No guardar
                        </button>
                        <button type="button" className="obras-save-all" onClick={requestBatchSave}>
                            <FiSave />
                            Guardar cambios
                            <strong>{Object.keys(draftEdits).length}</strong>
                        </button>
                    </div>
                ) : null}
                {readOnly ? (
                    // Perfil de consulta: sin «Nueva obra»; se dice qué puede hacer.
                    <span className="obras-role-badge" title={role === OBRAS_ROLES.IMAGEN ? "Puedes ver todas las obras y cambiar el PMI." : "Puedes ver las obras de tu estudio, sin modificarlas."}>
                        <FiEye aria-hidden="true" />
                        {role === OBRAS_ROLES.IMAGEN ? "Solo consulta · PMI editable" : "Solo consulta · obras de tu estudio"}
                    </span>
                ) : (
                <button type="button" className="obras-primary-button obras-command-bar__cta" onClick={addObra}>
                    <FiPlus />
                    Nueva obra
                </button>
                )}
                <button
                    type="button"
                    className={`obras-fullscreen-toggle${fullscreen ? " is-on" : ""}`}
                    aria-pressed={fullscreen}
                    title={fullscreen ? "Salir de pantalla completa (Esc)" : "Pantalla completa: Obras ocupa toda la ventana"}
                    aria-label={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                    onClick={() => setFullscreen((current) => !current)}
                >
                    {fullscreen ? <FiEyeOff /> : <FiEye />}
                </button>
            </div>

            <div className="obras-view-row">
                <nav className="obras-view-band" aria-label="Vistas de Obras">
                    {OBRAS_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={`obras-view-band__tab${activeTab === tab.id ? " active" : ""}`}
                            aria-current={activeTab === tab.id ? "page" : undefined}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
                {/* Filtros aplicados en la misma fila que las pestañas: aparecer o
                    desaparecer no cambia el alto de la cabecera ni redimensiona la vista. */}
                <div className="obras-filter-chipline" aria-label="Filtros aplicados">
                    {activeFilterChips.length ? (
                        <>
                            <span className="obras-filter-chipline__count">{filteredObras.length} {filteredObras.length === 1 ? "obra" : "obras"} · {moneyCompact(filteredObras.reduce((acc, obra) => acc + Number(obra.capex || 0), 0))}</span>
                            <div className="obras-filter-chips" ref={chipsRef}>
                                {activeFilterChips.map((chip, index) => (
                                    <span key={chip.key} className={`obras-filter-chip${index >= visibleChipCount ? " is-overflow" : ""}`} aria-hidden={index >= visibleChipCount || undefined}>
                                        {chip.fieldId ? (
                                            <button type="button" className="obras-filter-chip__edit" title={`${chip.label}: ${chip.value} (clic para editar)`} onClick={() => openFilterPanel(chip.fieldId)}>
                                                {chip.label}: <b>{chip.value}</b>
                                            </button>
                                        ) : <>{chip.label}: <b title={chip.value}>{chip.value}</b></>}
                                        <button type="button" aria-label={`Quitar filtro ${chip.label}`} onClick={chip.onRemove} tabIndex={index >= visibleChipCount ? -1 : undefined}><FiX /></button>
                                    </span>
                                ))}
                                <button
                                    type="button"
                                    className={`obras-filter-chip-more${hiddenChips.length ? "" : " is-overflow"}`}
                                    aria-hidden={!hiddenChips.length || undefined}
                                    tabIndex={hiddenChips.length ? undefined : -1}
                                    title={hiddenChips.map((chip) => `${chip.label}: ${chip.value}`).join(" · ")}
                                    onClick={() => openFilterPanel()}
                                >
                                    {visibleChipCount > 0 ? "+" : ""}{hiddenChips.length || 0} {hiddenChips.length === 1 ? "filtro" : "filtros"}
                                </button>
                            </div>
                            <button type="button" className="obras-filter-chipline__clear" onClick={() => { clearAllFilters(); setSearch(""); }}>Limpiar</button>
                        </>
                    ) : null}
                </div>
                {selectionCount && activeTab !== "obras" ? (
                    <span className="obras-view-selection" role="status">
                        <FiCheckSquare aria-hidden="true" />
                        <span>Solo {selectionCount} {selectionCount === 1 ? "obra marcada" : "obras marcadas"}</span>
                        <button type="button" onClick={clearSelection} title="Quitar la selección de la hoja: la vista vuelve a todas las obras filtradas">Ver todas</button>
                    </span>
                ) : OBRAS_ANALYSIS_TAB_IDS.has(activeTab) ? (
                    <button
                        type="button"
                        className={`obras-view-inherit${analysisIgnoresFilters ? "" : " active"}`}
                        aria-pressed={!analysisIgnoresFilters}
                        title="Si está activo, esta vista usa los mismos filtros que la hoja"
                        onClick={() => setIgnoreSheetFilters((current) => ({ ...current, [activeTab]: !current[activeTab] }))}
                    >
                        <FiFilter />
                        <span>{analysisIgnoresFilters ? "Sin filtros de la hoja" : "Con filtros de la hoja"}</span>
                    </button>
                ) : null}
                <button
                    type="button"
                    className="obras-view-export"
                    onClick={exportActiveTab}
                    disabled={tabExporting || !filteredObras.length}
                    title={activeTab === "obras" ? "Exporta a Excel las obras filtradas con las columnas visibles en el orden actual" : "Exporta a PDF la vista tal como se ve, con fecha y filtros aplicados"}
                >
                    <FiDownload />
                    <span>{tabExporting ? "Generando PDF..." : activeTab === "obras" ? "Exportar Excel" : "Exportar PDF"}</span>
                </button>
            </div>

            <div className="obras-tab-scroll" ref={tabScrollRef}>
            {activeTab === "resumen" ? (<>
            <section className="obras-readout" aria-label="Lectura operativa de la cartera">
                <header className="obras-readout__head">
                    <div className="obras-eyebrow"><FiActivity /><span>Lectura operativa</span></div>
                    <small title={RESUMEN_FORMULAS.riesgo}>{moneyCompact(portfolioModel.totals.totalCapex)} de CAPEX aprobado · {scopeSummary.riesgos} en riesgo</small>
                </header>
                <div className="obras-readout__cards">
                    <article className={`obras-kpi-card ${portfolioHealth.blockedCapex ? "is-warning" : "is-good"}`}>
                        <span>CAPEX expuesto</span>
                        <strong>{moneyCompact(portfolioHealth.blockedCapex)}</strong>
                        <small>{portfolioHealth.blockedCapex ? "Ligado a bloqueos" : "Sin riesgos financieros"}</small>
                        <em className="obras-kpi-card__formula" title={RESUMEN_FORMULAS.capexExpuesto}>{RESUMEN_FORMULAS.capexExpuesto}</em>
                        <i><b style={{ width: `${Math.max(5, Math.min(100, (portfolioHealth.blockedCapex / Math.max(1, portfolioModel.totals.totalCapex)) * 100))}%` }} /></i>
                    </article>
                    <article className="obras-kpi-card">
                        <span>Ritmo máximo</span>
                        <strong>{portfolioHealth.peakLoad} obras</strong>
                        <small>Pico previsto: {portfolioHealth.peakLoadMonth} {portfolioHealth.loadYear}</small>
                        <em className="obras-kpi-card__formula" title={RESUMEN_FORMULAS.ritmo}>{RESUMEN_FORMULAS.ritmo}</em>
                        <i><b style={{ width: `${Math.max(5, Math.min(100, portfolioHealth.peakLoad * 5))}%` }} /></i>
                    </article>
                    <article className={`obras-kpi-card ${portfolioHealth.alertCount ? "is-danger" : "is-good"}`}>
                        <span>Señales de control</span>
                        <strong>{portfolioHealth.alertCount}</strong>
                        <small>{portfolioHealth.dueSoon} {portfolioHealth.dueSoon === 1 ? "hito crítico vence" : "hitos críticos vencen"} en 14 días</small>
                        <em className="obras-kpi-card__formula" title={RESUMEN_FORMULAS.senalesDetalle}>{RESUMEN_FORMULAS.senales}</em>
                        <i><b style={{ width: `${Math.max(5, Math.min(100, portfolioHealth.alertCount * 18))}%` }} /></i>
                    </article>
                    <article className={`obras-kpi-card ${portfolioHealth.providerConcentration > 40 ? "is-warning" : "is-good"}`}>
                        <span>Concentración</span>
                        <strong>{Math.round(portfolioHealth.providerConcentration)}%</strong>
                        <small>Proveedor {portfolioHealth.topProviderName}</small>
                        <em className="obras-kpi-card__formula" title={RESUMEN_FORMULAS.concentracion}>{RESUMEN_FORMULAS.concentracion}</em>
                        <i><b style={{ width: `${Math.max(5, Math.min(100, portfolioHealth.providerConcentration))}%` }} /></i>
                    </article>
                </div>
            </section>

            <section className="obras-attention" aria-label="Atención inmediata">
                <header className="obras-attention__head">
                    <div className="obras-eyebrow obras-eyebrow--alert"><FiAlertTriangle /><span>Atención inmediata</span></div>
                    <small title={RESUMEN_FORMULAS.atencion}>{attentionObras.length} {attentionObras.length === 1 ? "alerta crítica" : "alertas críticas"}</small>
                </header>
                <p className="obras-attention__formula">{RESUMEN_FORMULAS.atencion}</p>
                <div className="obras-attention__grid">
                    {attentionObras.length ? attentionObras.map(({ obra, reason, tone }, index) => (
                        <button key={`${obra.codObra}-${index}`} type="button" className={`obras-attention-card tone-${tone}`} onClick={() => openDetail(obra.codObra)}>
                            <div className="obras-attention-card__title">
                                <strong>{obra.codigoTienda} · {obra.tienda}</strong>
                                <FiArrowRight />
                            </div>
                            <small>{reason} · {obra.pm || "Sin PM"}</small>
                        </button>
                    )) : <p className="obras-attention__empty">Sin alertas prioritarias en el filtro actual.</p>}
                </div>
            </section>

            <section className="obras-hito-alerts" aria-label="Hitos críticos">
                <header className="obras-attention__head">
                    <div className="obras-eyebrow obras-eyebrow--alert"><FiCalendar /><span>Hitos críticos</span></div>
                    <small>
                        {hitoAlertRows.length} {hitoAlertRows.length === 1 ? "alerta" : "alertas"} en {new Set(hitoAlertRows.map(({ obra }) => obra.codObra)).size} obras
                    </small>
                </header>
                <p className="obras-attention__formula">
                    ZONIF, LEGAL, PLANO P3, Deadline MOB, P3 constructora y P3 mobiliario. No completado = fecha pasada sin confirmar · Necesita datos = sin fecha a menos de {NEEDS_DATA_WINDOW_DAYS} días de la apertura · En riesgo = marcado al revisarlo. No cuentan las obras archivadas ni las completadas.
                </p>
                <div className="obras-hito-alerts__counts" role="group" aria-label="Filtrar por tipo de alerta">
                    {ALERT_STATUSES.map((status) => (
                        <button
                            key={status}
                            type="button"
                            className={`obras-hito-chip tone-${HITO_STATUS[status].tone}${hitoAlertFilter === status ? " is-selected" : ""}`}
                            aria-pressed={hitoAlertFilter === status}
                            onClick={() => setHitoAlertFilter((current) => (current === status ? "" : status))}
                        >
                            {HITO_STATUS[status].label} · {hitoAlertCounts[status] || 0}
                        </button>
                    ))}
                    {hitoAlertFilter ? (
                        <button
                            type="button"
                            className="obras-hito-alerts__sheet-link"
                            onClick={() => { applyCategoryFilter("hitoSituacion", [HITO_STATUS[hitoAlertFilter].label]); setActiveTab("obras"); }}
                        >
                            Ver estas obras en la hoja <FiArrowRight />
                        </button>
                    ) : null}
                </div>
                {(() => {
                    const rows = hitoAlertRows.filter(({ hito }) => !hitoAlertFilter || hito.status === hitoAlertFilter);
                    if (!rows.length) return <p className="obras-attention__empty">Sin alertas de hitos en el filtro actual.</p>;
                    const visible = showAllHitoAlerts ? rows : rows.slice(0, 12);
                    return (
                        <>
                            <div className="obras-hito-alerts__table-wrap">
                                <table className="obras-hito-alerts__table">
                                    <thead>
                                        <tr><th>Obra</th><th>Hito</th><th>Situación</th><th>Detalle</th><th>Apertura</th><th>PM</th></tr>
                                    </thead>
                                    <tbody>
                                        {visible.map(({ obra, hito }) => (
                                            <tr key={`${obra.codObra}-${hito.field}`}>
                                                <td>
                                                    <button type="button" onClick={() => openDetailAtField(obra.codObra, hito.field)}>
                                                        {obra.codigoTienda} · {obra.tienda}
                                                    </button>
                                                </td>
                                                <td>{hito.label}</td>
                                                <td><span className={`obras-hito-chip tone-${hito.tone}`}>{hito.statusLabel}</span></td>
                                                <td>{hito.message}</td>
                                                <td>{formatCompactDate(obra.apertura)}</td>
                                                <td>{obra.pm || "Sin PM"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {rows.length > 12 ? (
                                <button type="button" className="obras-hito-alerts__more" onClick={() => setShowAllHitoAlerts((current) => !current)}>
                                    {showAllHitoAlerts ? "Ver menos" : `Ver las ${rows.length} alertas`}
                                </button>
                            ) : null}
                        </>
                    );
                })()}
            </section>
            </>) : null}

            {dashboardOpen ? (
                <PortfolioControlCenter
                    inline
                    activeView={activeVisual}
                    model={analysisModel}
                    onClose={() => setActiveTab("obras")}
                    onOpenDetail={openDetail}
                    onViewChange={setActiveTab}
                    onEditField={stageObraField}
                    draftEdits={draftEdits}
                    onApplyFilter={applyCategoryFilter}
                    categoryFilters={customFilters.category}
                    activeFilterCount={activeFilterCount}
                    canSubscribe={!useLocalData}
                    calendarScope={calendarScope}
                />
            ) : null}

            {sheetMounted ? (<>
            <div className="obras-page__toolbar obras-page__toolbar--legacy" hidden={activeTab !== "obras"}>
                <div className="obras-search">
                    <FiSearch />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Buscar en cualquier campo de texto"
                    />
                </div>
                <select value={estado} onChange={(event) => setEstado(event.target.value)}>
                    {estados.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                <select value={pais} onChange={(event) => setPais(event.target.value)}>
                    {paises.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                <button className="obras-primary-button" onClick={addObra}>
                    <FiPlus />
                    Insertar fila
                </button>
            </div>

            <section className="obras-kpis obras-kpis--legacy" aria-label="KPIs principales" hidden={activeTab !== "obras"}>
                <article className="obras-kpi">
                    <FiActivity />
                    <div>
                        <span>Obras</span>
                        <strong>{summary.total}</strong>
                    </div>
                </article>
                <article className="obras-kpi">
                    <FiDollarSign />
                    <div>
                        <span>Inversion</span>
                        <strong>{currency.format(summary.totalInversion)}</strong>
                    </div>
                </article>
                <article className="obras-kpi">
                    <FiTrendingUp />
                    <div>
                        <span>€/m2 medio</span>
                        <strong>{currency.format(summary.euroM2)}</strong>
                    </div>
                </article>
                <article className="obras-kpi obras-kpi--alert">
                    <FiCalendar />
                    <div>
                        <span>Puntos abiertos</span>
                        <strong>{summary.pendientes}</strong>
                    </div>
                </article>
            </section>

            <div className="obras-layout" hidden={activeTab !== "obras"}>
                <section className="obras-main-panel">
                    <div className="obras-workbench-header">
                        <div>
                            <span>Obras filtradas</span>
                            <strong>{filteredObras.length}</strong>
                        </div>
                        <div>
                            <span>Fila activa</span>
                            <strong>{selectedObra ? selectedObra.codigoTienda || selectedObra.codObra : "-"}</strong>
                        </div>
                        <div>
                            <span>Riesgos</span>
                            <strong>{summary.riesgos}</strong>
                        </div>
                    </div>

                    <div className="obras-table-controls">
                        <div>
                            <strong>{filteredObras.length}</strong>
                            <span>{filteredObras.length === 1 ? "obra filtrada" : "obras filtradas"}</span>
                        </div>
                        <div className="obras-density" role="group" aria-label="Densidad de fila">
                            {ROW_DENSITY_OPTIONS.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={rowDensity === option.id ? "active" : ""}
                                    aria-pressed={rowDensity === option.id}
                                    onClick={() => setRowDensity(option.id)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <div className="obras-column-picker">
                            <button
                                type="button"
                                className={`obras-column-picker__toggle${columnMenuOpen ? " open" : ""}`}
                                aria-expanded={columnMenuOpen}
                                aria-label="Configurar columnas"
                                onClick={() => { setColumnMenuOpen((current) => !current); closeFilterMenu(); }}
                            >
                                <FiSettings />
                                <span>Columnas</span>
                            </button>
                            {columnMenuOpen ? (
                                <ColumnsPanel
                                    groups={COLUMN_PANEL_GROUPS}
                                    isVisible={isColumnVisible}
                                    onToggle={toggleColumn}
                                    onSetMany={setColumnsVisible}
                                    views={columnViews}
                                    activeViewId={activeColumnViewId}
                                    onApplyView={applyColumnView}
                                    onDeleteView={deleteColumnQuickConfig}
                                    onSaveView={saveColumnQuickConfig}
                                    saveStatus={quickConfigSaveStatus(savedColumnConfigs)}
                                    maxNameLength={MAX_QUICK_CONFIG_NAME_LENGTH}
                                    onReset={resetDefaultColumns}
                                    onShowAll={selectAllAllowedColumns}
                                    onClose={() => setColumnMenuOpen(false)}
                                />
                            ) : null}
                        </div>
                    </div>

                    <div className="obras-table-wrap" ref={sheetWrapRef} onScroll={handleSheetScroll} onKeyDown={handleSheetKeyDown}>
                        <table ref={sheetTableRef} className={`obras-table obras-table--density-${rowDensity}`} aria-rowcount={sortedFilteredObras.length + 1} style={{ width: `${sheetTableWidth}px` }}>
	                            <thead>
	                                <tr>
	                                    <th className="obras-col-check" style={{ width: `${columnWidthOf("check")}px`, minWidth: `${columnWidthOf("check")}px`, maxWidth: `${columnWidthOf("check")}px` }}>
	                                        <input
	                                            type="checkbox"
	                                            aria-label="Seleccionar todas las obras filtradas"
	                                            checked={allFilteredSelected}
	                                            ref={(el) => { if (el) el.indeterminate = someFilteredSelected; }}
	                                            onChange={toggleSelectAllFiltered}
	                                        />
	                                    </th>
	                                    <th className="obras-col-num" style={{ width: `${columnWidthOf("num")}px`, minWidth: `${columnWidthOf("num")}px`, maxWidth: `${columnWidthOf("num")}px` }}>#</th>
	                                    {renderSortableHeader("codigo", "Código", sheetBlockClass("codigo"))}
	                                    {renderSortableHeader("pais", "País", sheetBlockClass("pais"))}
	                                    {renderSortableHeader("ciudad", "Ubicación", sheetBlockClass("ciudad"))}
	                                    {renderSortableHeader("tienda", "Tienda", sheetBlockClass("tienda"))}
	                                    {orderedReorderableColumns.map((column) => renderSortableHeader(column.id, column.label, sheetColumnClass(column) || "", true))}
	                                </tr>
	                                {filteredObras.length ? (
	                                <tr className="obras-table-totals">
	                                    <td className="obras-col-check" />
	                                    <td className="obras-col-num">Σ</td>
	                                    <td className={sheetBlockClass("codigo") || undefined}>{tableFooterTotals.count} obras</td>
	                                    <td className={sheetBlockClass("pais") || undefined}>{tableFooterTotals.countries} países</td>
	                                    <td className={sheetBlockClass("ciudad") || undefined}>—</td>
	                                    <td className={sheetBlockClass("tienda") || undefined}>—</td>
	                                    {orderedReorderableColumns.map((column) => {
	                                        let content = "—";
	                                        if (column.id === "margen") content = `${tableFooterTotals.margenSum > 0 ? "+" : ""}${currency.format(tableFooterTotals.margenSum)}`;
	                                        else if (column.id === "eurom2") content = `${currency.format(tableFooterTotals.eurom2Avg)} media`;
	                                        else if (column.id === "bloq") content = tableFooterTotals.bloqSum;
	                                        else if (column.id === "pm") content = `${tableFooterTotals.pms} PM`;
	                                        else if (!OPTIONAL_COLUMN_RENDERERS[column.id]) {
	                                            const generic = GENERIC_COLUMNS.find((item) => item.id === column.id);
	                                            if (generic?.type === "number") {
	                                                content = (fieldUnits[column.id] === "EUR" || fieldUnits[column.id] === "EUR/m2")
	                                                    ? currency.format(tableFooterTotals.generic[column.id] || 0)
	                                                    : plainNumberFormatter.format(tableFooterTotals.generic[column.id] || 0);
	                                            }
	                                        }
	                                        return <td key={column.id} className={sheetColumnClass(column)}>{content}</td>;
	                                    })}
	                                </tr>
	                                ) : null}
	                            </thead>
                            <tbody>
                                {sheetWindow.topPad > 0 ? (
                                    <tr className="obras-table-spacer" aria-hidden="true"><td colSpan={visibleColumnCount} style={{ height: `${sheetWindow.topPad}px` }} /></tr>
                                ) : null}
                                {sortedFilteredObras.slice(sheetWindow.start, sheetWindow.end).map((obra, offset) => {
                                    const index = sheetWindow.start + offset;
                                    return (
                                        <SheetRow
                                            key={index}
                                            obra={obra}
                                            index={index}
                                            columns={orderedReorderableColumns}
                                            isOpen={selectedObra?.codObra === obra.codObra}
                                            isChecked={selectedObraIds.has(obra.codObra)}
                                            onToggleChecked={toggleObraSelected}
                                            onOpenDetail={openDetail}
                                        />
                                    );
                                })}
                                {sheetWindow.bottomPad > 0 ? (
                                    <tr className="obras-table-spacer" aria-hidden="true"><td colSpan={visibleColumnCount} style={{ height: `${sheetWindow.bottomPad}px` }} /></tr>
                                ) : null}
                                {!filteredObras.length ? (
                                    <tr className="obras-table-empty">
                                        <td colSpan={visibleColumnCount}>
                                            <FiSearch />
                                            <strong>Sin obras que coincidan</strong>
                                            <span>Prueba con otro texto o ajusta los filtros activos.</span>
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>

                    {/* Volver a la primera obra: flota sobre la esquina de la hoja (no se mueve con
                        el scroll). Sube si está la barra de selección y no sale con el panel de
                        justificación abierto, que ocupa esa esquina. */}
                    {showBackToTop(sheetViewport) && !changeRequest ? (
                        <button
                            type="button"
                            className={`obras-back-to-top${selectionCount ? " obras-back-to-top--raised" : ""}`}
                            aria-label="Volver a la primera obra"
                            title="Volver a la primera obra"
                            onClick={scrollSheetToTop}
                        >
                            <FiArrowUp />
                        </button>
                    ) : null}

                    {selectionCount && !changeRequest ? (
                        <div
                            className="obras-selection-dock"
                            role="region"
                            aria-label="Acciones sobre la selección"
                            style={dockLeft != null ? { left: `${dockLeft}px` } : undefined}
                        >
                            <div className="obras-selection-dock__count">
                                <b>{selectionCount}</b>
                                <span>{selectionCount === 1 ? "obra seleccionada" : "obras seleccionadas"}</span>
                            </div>
                            <div className="obras-selection-dock__actions">
                                {!readOnly ? (<>
                                <button type="button" onClick={() => setBulkEditOpen(true)}>
                                    <FiEdit3 /><span>Editar masivo</span>
                                </button>
                                <div className="obras-selection-dock__estado-wrap">
                                    <button type="button" aria-expanded={dockEstadoOpen} onClick={() => setDockEstadoOpen((open) => !open)}>
                                        <FiRefreshCw /><span>Cambiar estado</span>
                                    </button>
                                    {dockEstadoOpen ? (
                                        <>
                                            <div className="obras-menu-backdrop" onClick={() => setDockEstadoOpen(false)} />
                                            <div className="obras-selection-dock__estado-menu" role="menu">
                                                {estados.filter((option) => option !== "Todos").map((option) => (
                                                    <button key={option} type="button" role="menuitem" onClick={() => { applyBulkEstado(option); setDockEstadoOpen(false); }}>
                                                        {option}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                                </>) : null}
                                <div className="obras-report-menu" ref={reportMenuRef}>
                                    <button
                                        type="button"
                                        className={`obras-report-menu__trigger${reportMenuOpen ? " open" : ""}`}
                                        onClick={() => setReportMenuOpen((open) => !open)}
                                        aria-haspopup="menu"
                                        aria-expanded={reportMenuOpen}
                                    >
                                        <FiDownload />
                                        <span className="obras-report-menu__label">Generar informe <FiChevronDown /></span>
                                    </button>
                                    {reportMenuOpen ? (
                                        <div className="obras-report-menu__list" role="menu">
                                            <button type="button" role="menuitem" onClick={() => exportReport("economico")}>Informe económico (PDF)</button>
                                            <button type="button" role="menuitem" onClick={() => exportReport("temporal")}>Informe temporal (PDF)</button>
                                            <button type="button" role="menuitem" onClick={() => exportReport("proveedores")}>Informe de proveedores (PDF)</button>
                                            <div className="obras-report-menu__sep" />
                                            <button type="button" role="menuitem" onClick={() => exportReport("excel")}>Excel completo (todos los campos)</button>
                                        </div>
                                    ) : null}
                                </div>
                                {readOnly ? null : showArchived ? (
                                    <>
                                        <button type="button" onClick={reactivateSelected}>
                                            <FiRefreshCw /><span>Reactivar</span>
                                        </button>
                                        {/* El backend no admite borrado físico: solo baja lógica (archivar).
                                            Se oculta la eliminación definitiva en la ruta real. */}
                                        {useLocalData ? (
                                            <button type="button" className="obras-selection-dock__danger" onClick={requestBulkDelete}>
                                                <FiTrash2 /><span>Eliminar</span>
                                            </button>
                                        ) : null}
                                    </>
                                ) : (
                                    <button type="button" className="obras-selection-dock__danger" onClick={archiveSelected}>
                                        <FiArchive /><span>Archivar</span>
                                    </button>
                                )}
                            </div>
                            <button type="button" className="obras-selection-dock__close" aria-label="Vaciar selección" onClick={clearSelection}>
                                <FiX />
                            </button>
                        </div>
                    ) : null}
                </section>
            </div>
            </>) : null}
            </div>

            {bulkEditOpen ? (
                <BulkEditModal
                            obras={selectedObras}
                            groups={bulkFieldGroups}
                            cityCatalog={catalogs.ciudad}
                            onApply={applyBulkEdit}
                            onClose={() => setBulkEditOpen(false)}
                        />
                    ) : null}

                    {selectedObra && (
                        <div
                            className="obras-modal-layer"
                            role="presentation"
                            onMouseDown={closeDetailGuarded}
                        >
                            <section
                                className="obras-detail"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="obras-detail-title"
                                onMouseDown={(event) => event.stopPropagation()}
                            >
                                <header>
                                    <div className="obras-detail__identity">
                                        <div>
                                            <strong>{selectedObra.codigoTienda}</strong>
                                            <h2 id="obras-detail-title">{selectedObra.tienda || "Sin nombre"}</h2>
                                        </div>
                                        <p>{[selectedObra.ciudad, selectedObra.pais].filter(Boolean).join(" - ") || "Sin ubicación"}</p>
                                        {selectedObra._isNew ? (
                                            // Aviso visible solo en altas: qué hace falta para conservarla.
                                            <span className="obras-detail__new-hint" role="note">
                                                {selectedObra._duplicatedFrom
                                                    ? `Copia sin guardar de ${[selectedObra._duplicatedFrom.codigoTienda, selectedObra._duplicatedFrom.tienda].filter(Boolean).join(" · ")}: si la cierras sin cambios se descarta`
                                                    : "Obra nueva sin guardar: indica al menos el nombre de la tienda o se descartará al cerrar"}
                                            </span>
                                        ) : null}
                                        {selectedDraftObra ? (
                                            <ul className="obras-detail__facts" aria-label="Datos clave de la obra">
                                                {buildDetailFacts(selectedDraftObra, portfolioToday, { formatDate: formatCompactDate, formatMoney: (value) => currency.format(value) }).map((fact) => (
                                                    <li key={fact.id} className={`is-${fact.tone}`}>
                                                        <span>{fact.label}</span>
                                                        <strong>{fact.value}</strong>
                                                        {fact.detail ? <em>{fact.detail}</em> : null}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </div>
                                    <div className="obras-detail__header-actions">
                                        <StatusBadge value={selectedObra.estado} />
                                        {!selectedObra._isNew && !readOnly ? (
                                            <button type="button" className="obras-detail__duplicate" title="Crear una obra nueva con estos datos (sin fechas ni código)" onClick={() => duplicateObra(selectedObra)}>
                                                <FiCopy /><span>Duplicar</span>
                                            </button>
                                        ) : null}
                                        {dashboardOpen ? <button type="button" aria-label="Volver al cuadro de mandos" onClick={closeDetailGuarded}><FiChevronLeft /></button> : null}
                                        <button
                                            type="button"
                                            aria-label="Cerrar detalle"
                                            onClick={closeDetailGuarded}
                                        >
                                            <FiX />
                                        </button>
                                    </div>
                                </header>

                                <div className="obras-field-finder">
                                    <label>
                                        <FiSearch />
                                        <input
                                            type="search"
                                            value={detailFieldSearch}
                                            placeholder="Buscar campo de la ficha"
                                            aria-label="Buscar campo de la ficha"
                                            onChange={(event) => setDetailFieldSearch(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    goToDetailField(detailFieldMatches[0]);
                                                }
                                            }}
                                        />
                                    </label>
                                    {detailFieldSearch.trim() ? (
                                        <div className="obras-field-finder__results">
                                            {detailFieldMatches.length ? (
                                                detailFieldMatches.map((match) => (
                                                    <button
                                                        key={`${match.groupId}-${match.field}`}
                                                        type="button"
                                                        onMouseDown={(event) => event.preventDefault()}
                                                        onClick={() => goToDetailField(match)}
                                                    >
                                                        <strong>{match.label}</strong>
                                                        <span>{match.groupLabel}</span>
                                                        {match.calculated || calculatedCostFields.has(match.field)
                                                            ? <small>Calculado</small>
                                                            : null}
                                                    </button>
                                                ))
                                            ) : (
                                                <p>Sin campos coincidentes</p>
                                            )}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="obras-detail__body">
                                    <div className="obras-detail-tabsbar">
                                        <nav className="obras-detail-tabs" aria-label="Secciones de la ficha">
                                            {DETAIL_TABS.map((tab) => {
                                                // Hitos: además del nº de campos, cuántos hitos críticos tienen alerta.
                                                const alerts = tab.id === "hitos" && selectedDraftObra ? obraHitoAlerts(selectedDraftObra, portfolioToday) : [];
                                                return (
                                                    <button
                                                        key={tab.id}
                                                        type="button"
                                                        className={detailTab === tab.id ? "active" : ""}
                                                        onClick={() => setDetailTab(tab.id)}
                                                    >
                                                        {tab.label}
                                                        <span>{detailTabFields(tab).filter((field) => fieldConfigByName[field]).length}</span>
                                                        {alerts.length ? (
                                                            <em
                                                                className={`obras-detail-tabs__alert tone-${alerts[0].tone}`}
                                                                title={alerts.map((hito) => `${hito.label}: ${hito.statusLabel}`).join("\n")}
                                                                aria-label={`${alerts.length} ${alerts.length === 1 ? "hito con alerta" : "hitos con alerta"}`}
                                                            >
                                                                <FiAlertTriangle aria-hidden="true" />{alerts.length}
                                                            </em>
                                                        ) : null}
                                                    </button>
                                                );
                                            })}
                                            <button
                                                type="button"
                                                className={detailTab === "cronograma" ? "active" : ""}
                                                onClick={() => setDetailTab("cronograma")}
                                                title="Mini-cronograma de esta obra: fechas, montaje y situación de los hitos"
                                            >
                                                <FiBarChart2 />
                                                Cronograma
                                            </button>
                                            <button
                                                type="button"
                                                className={detailTab === "agenda" ? "active" : ""}
                                                onClick={() => setDetailTab("agenda")}
                                                title="Fechas de esta obra en orden, exportables a Outlook (.ics)"
                                            >
                                                <FiCalendar />
                                                Agenda
                                            </button>
                                            <button
                                                type="button"
                                                className={detailTab === "historial" ? "active" : ""}
                                                onClick={() => setDetailTab("historial")}
                                            >
                                                <FiClock />
                                                Historial
                                                <span>{selectedAuditEntries.length}</span>
                                            </button>
                                        </nav>
                                        {(() => {
                                            const obraDrafts = selectedObra ? Object.values(draftEdits).filter((draft) => draft.codObra === selectedObra.codObra) : [];
                                            if (!obraDrafts.length) return null;
                                            const draftLabel = (draft) => draft.label || fieldConfigByName[draft.field]?.label || draft.field;
                                            const summary = obraDrafts.length === 1
                                                ? draftLabel(obraDrafts[0])
                                                : `${obraDrafts.length} campos a cambiar`;
                                            return (
                                                <div className="obras-detail-pending">
                                                    <span className="obras-detail-pending__summary" title={obraDrafts.length === 1 ? `Cambio pendiente en ${summary}` : "Pasa el ratón para ver todos los cambios pendientes"}>
                                                        <FiEdit3 />
                                                        {summary}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="obras-field-action obras-field-action--save"
                                                        onMouseDown={(event) => event.preventDefault()}
                                                        onClick={requestBatchSave}
                                                    >
                                                        <FiSave /> Guardar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="obras-field-action obras-field-action--discard"
                                                        onMouseDown={(event) => event.preventDefault()}
                                                        onClick={() => discardObraDrafts(selectedObra.codObra)}
                                                    >
                                                        <FiRotateCcw /> Deshacer
                                                    </button>
                                                    <div className="obras-detail-pending__list" role="list">
                                                        <p className="obras-detail-pending__list-head">Cambios sin guardar ({obraDrafts.length})</p>
                                                        {obraDrafts.map((draft) => (
                                                            <div key={draft.field} className="obras-detail-pending__item" role="listitem">
                                                                <span className="obras-detail-pending__item-name">{draftLabel(draft)}</span>
                                                                <button
                                                                    type="button"
                                                                    className="obras-detail-pending__item-discard"
                                                                    title={`Descartar el cambio en ${draftLabel(draft)}`}
                                                                    aria-label={`Descartar el cambio en ${draftLabel(draft)}`}
                                                                    onMouseDown={(event) => event.preventDefault()}
                                                                    onClick={() => discardObraField(selectedObra.codObra, draft.field)}
                                                                >
                                                                    <FiRotateCcw /> Deshacer
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className={`obras-detail__panel obras-detail__panel--${detailTab}`}>
                                        {detailTab === "historial" ? (
                                            <AuditTimeline
                                                entries={selectedAuditEntries}
                                                canRestore={canRestoreHistoryValue}
                                                onRestore={restoreHistoryValue}
                                                userNames={usuarioNames}
                                            />
                                        ) : (detailTab === "cronograma" || detailTab === "agenda") && selectedDraftObra ? (
                                            <ObraPlanView
                                                key={selectedObra.codObra}
                                                obra={selectedDraftObra}
                                                savedObra={selectedObra}
                                                mode={detailTab}
                                                today={portfolioToday}
                                                formatDate={formatCompactDate}
                                                onGoToField={(field) => goToDetailField({ field })}
                                            />
                                        ) : (
                                            renderDetailSections((DETAIL_TABS.find((tab) => tab.id === detailTab) || DETAIL_TABS[0]).sections)
                                        )}
                                    </div>
                                </div>
                            </section>
                        </div>
                    )}

            {geographyManager ? (
                <GeographyManagerModal
                    initialCountry={String(geographyManager.country || "")}
                    codObra={geographyManager.codObra}
                    catalogs={catalogs}
                    assignedValue={obras.find((obra) => obra.codObra === geographyManager.codObra)?.ciudad || ""}
                    usageCount={(entry) => getCatalogUsage("ciudad", entry)}
                    onClose={() => setGeographyManager(false)}
                    onReload={reloadGeography}
                    onAssign={(valor) => { if (valor && geographyManager.codObra) stageObraField(geographyManager.codObra, "ciudad", valor, "Ubicación"); }}
                    onRenamed={(previous, next, pais) => {
                        // Las obras guardan el nombre de la ubicación: al renombrarla se actualizan
                        // las de ese país (el mismo nombre puede existir en otro).
                        const affected = new Set(obras.filter((obra) => obra.ciudad === previous && obra.pais === pais).map((obra) => obra.codObra));
                        setObras((current) => current.map((obra) => (affected.has(obra.codObra) ? { ...obra, ciudad: next } : obra)));
                        setDraftEdits((current) => renameCityInDrafts(current, { previous, next, pais }, (codObra) => obras.find((obra) => obra.codObra === codObra)?.pais));
                    }}
                />
            ) : null}
            {catalogManager && catalogManagerObra ? (
                <CatalogManagerModal
                    ariaLabel={catalogManager.ariaLabel}
                    entries={catalogManagerEntries}
                    usageCount={(entry) => getCatalogUsage(catalogManager.catalog, entry)}
                    onClose={() => setCatalogManager(null)}
                    // Un proveedor es una fila de dbo.Empresas, compartida con otros módulos:
                    // se crea y se renombra desde aquí, pero no se deshabilita ni se borra.
                    note={EMPRESA_CATALOG_TYPES.has(catalogManager.catalog)
                        ? "Los proveedores se guardan en la tabla de empresas, compartida con otros módulos: renombrar uno cambia su nombre en todas las obras y en el resto de la aplicación."
                        : catalogManager.catalog === "pais"
                            ? "Los países son una lista cerrada: no se crean ni se eliminan desde aquí, solo se deshabilitan o se reactivan."
                            : undefined}
                    onStatusChange={EMPRESA_CATALOG_TYPES.has(catalogManager.catalog) ? undefined : (entry, nextActive) => {
                        setCatalogManager(null);
                        requestCatalogStatusChange({
                            ...catalogManager,
                            obra: catalogManagerObra,
                            entry,
                            nextActive,
                            selectAfter: false,
                        });
                    }}
                    // Un país no se borra: todos tienen sus regiones (ADM1) y el API lo rechazaría
                    // siempre. Se puede deshabilitar.
                    onDelete={EMPRESA_CATALOG_TYPES.has(catalogManager.catalog) || catalogManager.catalog === "pais" ? undefined : (entry) => requestCatalogDelete({
                        ...catalogManager,
                        obra: catalogManagerObra,
                        entry,
                    })}
                    // En la ruta real solo se renombran los valores ya guardados en BD (id numérico):
                    // uno recién escrito en la ficha se guarda con la obra y ya se podrá renombrar.
                    canRename={(entry) => useLocalData || Number(entry.id) > 0}
                    onRename={RENAMEABLE_CATALOG_TYPES.has(catalogManager.catalog)
                        ? (entry, value) => requestCatalogRename({ ...catalogManager, obra: catalogManagerObra, entry, value })
                        : undefined}
                />
            ) : null}

            {lastSavedChange ? (
                <div className="obras-save-confirmation" role="status">
                    <FiCheck />
                    <span><strong>{lastSavedChange.label}</strong> guardado en el historial</span>
                </div>
            ) : null}

            {staleNotice ? (
                <div className="obras-stale-notice" role="alert">
                    <FiAlertTriangle />
                    <span>{staleNotice}</span>
                    <button type="button" onClick={() => setStaleNotice("")} aria-label="Descartar aviso">×</button>
                </div>
            ) : null}

            {hitoReview && selectedDraftObra && selectedDraftObra.codObra === hitoReview.codObra ? (
                <HitoConfirmModal
                    obraLabel={`${selectedDraftObra.codigoTienda || ""} · ${selectedDraftObra.tienda || ""}`}
                    hito={hitoState(selectedDraftObra, hitoReview.field, portfolioToday)}
                    todayIso={toIsoDate(portfolioToday)}
                    formatDate={formatCompactDate}
                    onSubmit={applyHitoReview}
                    onClose={() => setHitoReview(null)}
                />
            ) : null}

            {confirmDialog ? (
                <div
                    className="obras-confirm-layer"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="obras-confirm-title"
                    onMouseDown={() => resolveConfirm(false)}
                >
                    <div
                        className={`obras-confirm${confirmDialog.tone === "danger" ? " obras-confirm--danger" : ""}`}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <h3 id="obras-confirm-title" className="obras-confirm__title">{confirmDialog.title}</h3>
                        {confirmDialog.message ? <p className="obras-confirm__message">{confirmDialog.message}</p> : null}
                        <div className="obras-confirm__actions">
                            {confirmDialog.cancelLabel ? (
                                <button type="button" className="obras-confirm__cancel" onClick={() => resolveConfirm(false)}>{confirmDialog.cancelLabel}</button>
                            ) : null}
                            <button type="button" className="obras-confirm__confirm" onClick={() => resolveConfirm(true)} autoFocus>{confirmDialog.confirmLabel}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {changeRequest ? (
                <aside className="obras-change-dock" aria-label="Justificar cambio">
                    <form onSubmit={commitChange}>
                        <header>
                            <div className="obras-change-dock__title">
                                <FiMessageSquare />
                                <div>
                                    <span>
                                        {changeRequest.kind === "alta"
                                            ? changeRequest.label
                                            : changeRequest.kind === "batch"
                                            ? "Guardar cambios"
                                            : changeRequest.kind === "bulk-delete"
                                                ? "Eliminar definitivamente"
                                                : (changeRequest.kind === "obra-status" || changeRequest.kind === "bulk-status")
                                                    ? changeRequest.nextActive
                                                        ? "Confirmar reactivacion"
                                                        : "Confirmar archivo"
                                                    : "Guardar cambio"}
                                    </span>
                                    <strong>{changeRequest.label}</strong>
                                </div>
                            </div>
                            <button type="button" className="obras-icon-button" onClick={cancelChangeSave} aria-label="Cerrar justificacion">
                                <FiX />
                            </button>
                        </header>

                        <div className="obras-change-dock__context">
                            <span>{changeRequest.obraLabel}</span>
                            {changeRequest.kind === "batch" || changeRequest.kind === "alta" ? (
                                <ul className="obras-change-dock__batch">
                                    {dockDrafts().slice(0, 6).map((draft) => (
                                        <li key={draft.key}>
                                            <b>{draft.obraLabel}</b>
                                            <span>
                                                {draft.label}: <del>{formatChangeValue(draft.previousValue, draft.field, usuarioNames)}</del>
                                                <FiArrowRight />
                                                <ins>{formatChangeValue(draft.newValue, draft.field, usuarioNames)}</ins>
                                            </span>
                                        </li>
                                    ))}
                                    {dockDrafts().length > 6 ? (
                                        <li className="obras-change-dock__batch-more">+{dockDrafts().length - 6} cambios más</li>
                                    ) : null}
                                </ul>
                            ) : changeRequest.kind === "bulk-delete" ? null : (
                                <div>
                                    <del>{formatChangeValue(changeRequest.previousValue, changeRequest.field, usuarioNames)}</del>
                                    <FiArrowRight />
                                    <ins>{formatChangeValue(changeRequest.newValue, changeRequest.field, usuarioNames)}</ins>
                                </div>
                            )}
                            {changeRequest.kind === "bulk-delete" ? (
                                <p className="obras-change-dock__impact obras-change-dock__impact--danger">
                                    <FiAlertTriangle />
                                    Se eliminarán definitivamente {changeRequest.label}. Esta acción no se puede deshacer.
                                </p>
                            ) : null}
                            {changeRequest.kind === "catalog" ? (
                                <p className="obras-change-dock__impact">
                                    <FiAlertTriangle />
                                    {changeRequest.catalogAction === "rename"
                                        ? changeRequest.usageCount
                                            ? `${changeRequest.usageCount} ${changeRequest.usageCount === 1 ? "obra pasará" : "obras pasarán"} a mostrar el nuevo nombre.`
                                            : "Ninguna obra usa este valor todavía."
                                        : changeRequest.catalogAction === "delete"
                                        ? "Se eliminara definitivamente del catalogo. No tiene obras asignadas."
                                        : changeRequest.nextActive
                                        ? "Volvera a estar disponible en todos los desplegables."
                                        : changeRequest.usageCount
                                            ? `${changeRequest.usageCount} ${changeRequest.usageCount === 1 ? "obra conserva" : "obras conservan"} este valor y se mostrara como deshabilitado.`
                                            : "No hay obras asignadas a este valor."}
                                </p>
                            ) : null}
                            {changeRequest.kind === "obra-status" ? (
                                <p className="obras-change-dock__impact">
                                    <FiAlertTriangle />
                                    {changeRequest.nextActive
                                        ? "La obra volvera al listado activo con todos sus datos y relaciones."
                                        : "La obra pasara a Archivadas. Se conservaran todos sus datos, relaciones e historial."}
                                </p>
                            ) : null}
                        </div>

                        <div className="obras-reason-presets">
                            {(changeRequest.kind === "catalog"
                                ? catalogReasonPresets
                                : changeRequest.kind === "obra-status"
                                    ? changeRequest.nextActive
                                        ? reactivateObraReasonPresets
                                        : archiveObraReasonPresets
                                    : reasonRequired
                                        ? changeReasonPresets
                                        : [OPTIONAL_REASON, ...changeReasonPresets]
                            ).map((reason) => (
                                <button key={reason} type="button" onClick={() => {
                                    setChangeReason(reason);
                                    setChangeReasonError(false);
                                }}>
                                    {reason}
                                </button>
                            ))}
                        </div>

                        <label className={changeReasonError ? "has-error" : ""}>
                            <span>{reasonRequired ? "Justificación obligatoria" : "Justificación (opcional)"}</span>
                            {/* 500 = tamaño del motivo en el historial (Obras_Auditoria.Motivo y
                                MotivoBaja): con más, archivar o cambiar un catálogo fallaba. */}
                            <textarea
                                autoFocus
                                rows={3}
                                maxLength={500}
                                value={changeReason}
                                onChange={(event) => {
                                    setChangeReason(event.target.value);
                                    setChangeReasonError(false);
                                }}
                                placeholder={reasonRequired
                                    ? "Explica por qué cambia este dato"
                                    : `Opcional: si la dejas vacía, el historial guarda «${OPTIONAL_REASON}»`}
                            />
                            {changeReasonError ? (
                                <small>
                                    {FIELD_EDIT_CHANGE_KINDS.has(changeRequest.kind)
                                        ? "Cambiar el inicio de obra o la apertura exige justificación."
                                        : "Escribe una justificación antes de guardar."}
                                </small>
                            ) : null}
                        </label>

                        {saveError ? <p className="obras-save-error" role="alert"><FiAlertTriangle /> {saveError}</p> : null}

                        <footer>
                            <span><FiClock /> Quedara registrado con usuario y fecha</span>
                            <div>
                                <button type="button" className="obras-secondary-button" onClick={cancelChangeSave} disabled={saving}>Seguir editando</button>
                                <button
                                    type="submit"
                                    className={changeRequest.kind === "bulk-delete" ? "obras-primary-button obras-primary-button--danger" : "obras-primary-button"}
                                    disabled={saving}
                                >
                                    {saving
                                        ? <>Guardando…</>
                                        : changeRequest.kind === "batch" || changeRequest.kind === "alta"
                                            ? <><FiSave /> Guardar {dockDrafts().length} {dockDrafts().length === 1 ? "cambio" : "cambios"}</>
                                            : changeRequest.kind === "bulk-delete"
                                                ? <><FiTrash2 /> Eliminar {selectionCount} {selectionCount === 1 ? "obra" : "obras"}</>
                                                : changeRequest.kind === "obra-status"
                                                    ? changeRequest.nextActive
                                                        ? <><FiRefreshCw /> Reactivar obra</>
                                                        : <><FiArchive /> Archivar obra</>
                                                    : <><FiSave /> Guardar cambio</>}
                                </button>
                            </div>
                        </footer>
                    </form>
                </aside>
            ) : null}
        </div>
    );
}
